// People (HR directory) — roster + tabbed employee record, attendance & approvals.
// Compensation / bank are gated behind people=full (canSeeComp); Postgres RLS is
// the real boundary, this just keeps the UI honest.
import {
  Fragment,
  useState,
  useEffect,
  useId,
  useMemo,
  useCallback,
  useRef,
  type ComponentProps,
  type CSSProperties,
} from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../lib/auth';
import { can, canSeeComp, roleLabel, roleTone, type PermLevel, type PermMap } from '../lib/roles';
import { FixDayDialog, type DayToFix } from '../components/fixDay';
import { DayEditDecisions, EditDayDialog } from '../components/editDay';
import {
  Badge,
  Avatar,
  AttentionCount,
  Progress,
  Mono,
  Button,
  Checkbox,
  Icon,
  Select,
  Tabs,
  TONE,
} from '../components/primitives';
import { CompOffRow } from '../components/overtimeYear';
import { KpiTile, KpiGrid, AttendanceStrip, type StripDay } from '../components/domain';
import {
  PCard,
  SensitiveMark,
  EditableField,
  Stepper,
  LeaveCalendar,
  type LeaveCalendarHoliday,
  fmtINR,
  fmtINRFull,
  maskAcct,
  parseINR,
} from '../components/people';
import { SectionHeader, usePageCrumbs, type PageCrumb } from '../components/shell';
import { Markdown } from '../components/Markdown';
import { PayrollPeriods } from '../components/payrollPeriods';
import { AmendWorkspace } from '../components/amendWorkspace';
import { RecordLeaveModal } from '../components/recordLeave';
import { PayrollRegister } from '../components/payroll';
import { loadCostTrend, PRE_STATUTORY_NOTE, type CostMonth } from '../lib/payroll';
import {
  DueDatesCard,
  KeyDatesCard,
  ServiceHistoryCard,
  ExceptionsCard,
} from '../components/keyDates';
import { DocumentsTab, RoleHandbooksCard, GatePanel } from '../components/documents';
import {
  listDocuments,
  listHandbooks,
  checkGate,
  type EmployeeDocument,
  type RoleHandbook,
  type Gate,
} from '../lib/documents';
import {
  listDueDates,
  listKeyDates,
  listEvents,
  listExceptions,
  type DueDate,
  type KeyDate,
  type EngagementEvent,
  type HrException,
} from '../lib/keyDates';
import {
  listQueue as listPendingComp,
  listCategories as listCompCategories,
  type PendingComp,
  type Category as CompCategory,
} from '../lib/compHours';
import { listCorrections, type PendingCorrection } from '../lib/timeCorrections';
import { listPendingDayEdits, type PendingDayEdit } from '../lib/editDay';
import {
  isoDay,
  addDays,
  orgToday,
  orgDateKey,
  monthRange,
  weekStart,
  weekdayLabels,
  fmtDate,
  fmtDayKey,
  fmtRelative,
} from '../lib/dates';
import { useOrgDates } from '../lib/orgSettings';
import { personDay, type OrgHours } from '../lib/orgHours';
import { completionPct } from '../lib/contract';
import {
  attendanceRate,
  attendanceRateOrNull,
  presentFraction,
  rateChange,
} from '../lib/attendance';
import { MonthlySummary } from '../components/MonthlySummary';
import { DayClockLog } from '../components/dayClockLog';
import { CompHoursModal } from '../components/compHours';
import { CompHoursTrail } from '../components/compHoursTrail';
import { listCategories, type Category } from '../lib/compHours';
import { OnTheClockCard } from '../components/onTheClock';
import { LeaveTrail } from '../components/leaveTrail';
import { useOnTheClock } from '../lib/usePresence';
import { canSeeOrgSummary, loadSummaries, type MonthSummary } from '../lib/monthlySummary';
import { isWorkday, workingDayList, type Workdays } from '../lib/workdays';
import { CONTACT_SELECT } from '../lib/contact';
import { captureError } from '../lib/posthog';
import { write } from '../lib/write';
import { useBelow, useBreakpoint } from '../lib/useMediaQuery';
import { EmptyState, InlineStatus, LoadError, useToast } from '../components/feedback';
import {
  CompHoursDecisions,
  CorrectionDecisions,
  DecisionActions,
  DecisionRow,
  KindTag,
  RequestStatus,
  YOURS,
} from '../components/requests';
import { reqRef } from '../lib/reqRef';
import { ConfirmDialog, Dialog } from '../components/dialog';
import { Field, TextArea, TextInput, type SaveResult } from '../components/field';
import { fmtDuration } from '../lib/duration';
import { DisclosedForm, Eyebrow, SegmentedControl } from '../components/card';
import { DayMark, dayMarkStyle } from '../components/monthGrid';
import { monthLabel } from '../lib/payrollPeriods';
import { useDepartments, departmentName, type Department } from '../lib/departments';
import { useEscalations } from '../lib/escalations';
import { QUEUES, useAttention } from '../lib/attention';
import { EscalationsCard } from '../components/escalations';
import { PoliciesCard } from '../components/policies';
import { CertificatesCard } from '../components/certificates';

// ── Row shapes (mirror the Supabase tables) ──────────────────────────────────
type Profile = {
  id: string;
  employee_no: number | null;
  manager_id: string | null;
  full_name: string | null;
  role_id: string | null;
  // The name comes from the departments list; profiles.department is a legacy copy.
  department_id: string | null;
  title: string | null;
  status: string | null;
  location: string | null;
  joined: string | null;
  avail_hrs: number | null;
  contract_hrs_per_day: number | null;
  assigned_hrs: number | null;
  job_description: string | null;
};
// Private contact PII — lives on employee_contact (self + people:full only).
// dob and the next-of-kin fields are collected at first-login intake; they are on
// this table rather than profiles because profiles_read grants talent:view, which
// would make them readable by everyone on the roster.
type Contact = {
  mobile: string | null;
  personal_email: string | null;
  address: string | null;
  dob: string | null;
  emergency_name: string | null;
  emergency_relation: string | null;
  emergency_phone: string | null;
};
type Comp = { profile_id: string; annual_ctc: number | null; currency: string | null };
type Bank = {
  profile_id: string;
  holder: string | null;
  account_no: string | null;
  ifsc: string | null;
  bank_name: string | null;
  branch: string | null;
};
type LeaveType = {
  id: string;
  label: string;
  short: string;
  color: string;
  days_per_year: number | null;
  /** Whether a day of this type is paid. The entitlement ceiling only applies to
   *  paid types, and only paid leave is netted out of required hours — so an
   *  unpaid day keeps its hours, lands in shortfall, and reduces pay. This screen
   *  did not fetch the column at all, which is why nothing on it could say so. */
  paid: boolean;
  /** Retired (ORG-01): kept so existing rows keep their label, never offered for
   *  anything new. The database refuses new leave on it. */
  archived?: boolean;
};
type LeaveBalance = { profile_id: string; leave_type_id: string; allotted: number; used: number };
type LeaveRequest = {
  id: string;
  profile_id: string;
  leave_type_id: string;
  start_date: string;
  end_date: string;
  day_count: number;
  reason: string | null;
  status: 'pending' | 'approved' | 'rejected';
  approver: string | null;
  decided_at: string | null;
};
type Attendance = {
  profile_id: string;
  date: string;
  status: 'present' | 'wfh' | 'leave' | 'absent' | 'holiday';
  hours: number | null;
};
// LeaveRequest enriched with the names we join in the approvals query.
type PendingRequest = LeaveRequest & {
  full_name: string | null;
  leave_label: string | null;
  /** false ⇒ approving this reduces the person's pay. */
  leave_paid: boolean;
  created_at: string | null;
  /** Why it was declined, when it was (leave_requests.decline_reason). */
  decline_reason?: string | null;
};

// ── Date helpers ─────────────────────────────────────────────────────────────
// `iso` used to be `d.toISOString().slice(0, 10)`, which is wrong for every Date
// here: weekStart() returns LOCAL midnight, and in IST that instant is 18:30Z the
// previous day — so every key came back a day early. That shifted the week query
// to Sun–Sat, rendered Sunday's status in the attendance strip's Monday column,
// and made each heatmap cell show the day before. attendance.date is stamped
// server-side from org_today(), so client and server disagreed by exactly a day.
const iso = isoDay;
// The private Monday-anchored weekStart() that lived here is gone — one of three
// identical copies across three screens. Use weekStart(weekStartsOn, …) from
// lib/dates, which takes the org setting.
const tenureYears = (joined: string | null): number => {
  if (!joined) return 0;
  // A date key is parsed at LOCAL midnight; `new Date('2022-03-01')` is UTC.
  const start = new Date(`${joined.slice(0, 10)}T00:00:00`).getTime();
  if (Number.isNaN(start)) return 0;
  return (Date.now() - start) / (1000 * 60 * 60 * 24 * 365.25);
};

// ─────────────────────────────────────────────────────────────────────────────
// Invite a new employee
// ─────────────────────────────────────────────────────────────────────────────
// Creating an auth user needs the service role, so the work happens in the
// invite-user Edge Function; this only collects three fields and reports back.
// Everything else about the person (department, title, joined date) is editable
// on the roster the moment they appear, so asking for it twice is waste.

// The one status pip in an avatar's corner. On-leave and on-the-clock share the
// slot and cannot collide: on_the_clock() returns only profiles with
// status='active' (20260902110000_on_the_clock.sql:50), and the leave pip needs
// status='leave'. The ternary below keeps that structural rather than a comment
// you have to trust.
const PIP: CSSProperties = {
  position: 'absolute',
  bottom: -1,
  right: -1,
  width: 10,
  height: 10,
  borderRadius: 'var(--hf-radius-pill)',
  border: '2px solid var(--hf-paper)',
};

// R3 reader: turns "Ada Lovelace" into the company address the org has settled
// on, so the format lives in policy.employment instead of in whoever is typing.
// Everything after the first word is the surname, joined — "van Dijk" is one
// person's name, not a middle name to drop.
export const suggestEmail = (fullName: string, format: string): string => {
  const parts = fullName.trim().toLowerCase().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '';
  const clean = (v: string) => v.replace(/[^a-z0-9]/g, '');
  const first = clean(parts[0]);
  const last = clean(parts.slice(1).join(''));
  if (!first) return '';
  return format
    .replace(/\{first\}/g, first)
    .replace(/\{last\}/g, last)
    .replace(/\.@/, '@'); // one-word name: don't leave a trailing dot
};

// On the Dialog and Field primitives (HRA-V1, HRP-11). It was a hand-built
// overlay that ignored Escape and let Tab walk out into the page behind it, with
// a private field recipe, unlinked labels, a native select, and a Send button
// that did nothing, silently, until both fields were filled.
export function InviteModal({
  roles,
  actorIsOwner,
  emailFormat,
  onClose,
  onInvited,
}: {
  roles: { id: string; label: string }[];
  actorIsOwner: boolean;
  emailFormat: string;
  onClose: () => void;
  onInvited: () => void;
}) {
  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [roleId, setRoleId] = useState('member');
  const [busy, setBusy] = useState(false);
  // Which field a problem belongs to, so it is said beside that field.
  const [errors, setErrors] = useState<{ email?: string; name?: string }>({});
  // The invite service's refusal: about the invite as a whole, not one field.
  const [refusal, setRefusal] = useState<string | null>(null);
  const [sent, setSent] = useState<string | null>(null);
  // Only suggest while the field is untouched — a typed address always wins.
  const [emailEdited, setEmailEdited] = useState(false);
  // R8. Re-checked whenever the email or role changes, so the modal says which
  // piece of paper is missing before anyone presses send.
  const [gate, setGate] = useState<Gate | null>(null);
  useEffect(() => {
    let live = true;
    const t = setTimeout(() => {
      void checkGate(email, roleId).then((g) => live && setGate(g));
    }, 350);
    return () => {
      live = false;
      clearTimeout(t);
    };
  }, [email, roleId]);

  // Mirrors the role dropdown on the record: only an owner may hand out owner.
  // The database is the real backstop — protect_profile_columns() refuses the
  // transition — but there is no reason to offer a choice that will be rejected.
  const options = roles.filter((r) => r.id !== 'owner' || actorIsOwner);

  const send = async () => {
    if (busy) return;
    const found: typeof errors = {};
    if (!email.trim()) found.email = 'Enter the address the invite goes to.';
    if (!fullName.trim()) found.name = 'Enter their name.';
    setErrors(found);
    if (found.email || found.name) return;
    if (!supabase) {
      setRefusal('Not connected.');
      return;
    }
    setBusy(true);
    setRefusal(null);
    const { data, error: fnErr } = await supabase.functions.invoke('invite-user', {
      body: { email: email.trim(), full_name: fullName.trim(), role_id: roleId },
    });
    setBusy(false);
    // Business failures come back as 200 with ok:false on purpose: functions-js
    // discards the body of any non-2xx, so an error status would reduce every
    // message below to a generic "non-2xx status code".
    if (fnErr) {
      setRefusal('Could not reach the invite service.');
      return;
    }
    if (!data?.ok) {
      setRefusal(data?.error ?? 'Invite failed.');
      return;
    }
    setSent(data.warning ?? `Invite sent to ${email.trim()}.`);
    onInvited();
  };

  return (
    <Dialog
      open
      title="Invite an employee"
      size="sm"
      onClose={onClose}
      dirty={!sent && !!(email.trim() || fullName.trim())}
      footer={(requestClose) =>
        sent ? (
          <Button variant="primary" onClick={onClose}>
            Done
          </Button>
        ) : (
          <>
            <Button variant="ghost" onClick={requestClose}>
              Cancel
            </Button>
            <Button variant="primary" icon="arrowRight" loading={busy} onClick={() => void send()}>
              Send invite
            </Button>
          </>
        )
      }
    >
      {sent ? (
        // Inside the dialog, not a toast: the page behind a modal is inert.
        <InlineStatus tone="success">
          {sent} They’ll get an email with a link, and set their own password when they open it.
        </InlineStatus>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--hf-space-4)' }}>
          {refusal && <InlineStatus tone="error">{refusal}</InlineStatus>}
          <Field label="Work email" required error={errors.email}>
            <TextInput
              type="email"
              autoComplete="off"
              value={email}
              onChange={(v) => {
                setEmail(v);
                setEmailEdited(true);
              }}
              placeholder={suggestEmail('Ada Lovelace', emailFormat)}
            />
          </Field>
          <Field
            label="Full name"
            required
            error={errors.name}
            hint={emailEdited ? undefined : 'Fills in the work email from the org’s format.'}
          >
            <TextInput
              autoComplete="off"
              value={fullName}
              onChange={(v) => {
                setFullName(v);
                if (!emailEdited) setEmail(suggestEmail(v, emailFormat));
              }}
            />
          </Field>
          <Field label="Role">
            {(control) => (
              <Select
                {...control}
                value={roleId}
                onChange={setRoleId}
                options={options.map((r) => ({ value: r.id, label: r.label }))}
              />
            )}
          </Field>
          <GatePanel
            gate={gate}
            email={email}
            onBypassed={() => void checkGate(email, roleId).then(setGate)}
          />
        </div>
      )}
    </Dialog>
  );
}

// Contracted salary cost, by department — CTC ÷ 12.
//
// Deliberately NOT called "Monthly payroll" any more. It sits on the Payroll tab
// a few lines below the register, and the register prints `gross`, which is what
// payroll actually COMPUTED for the month. These two figures legitimately differ
// — one is what you have committed to pay, the other is what was priced after
// shortfall deductions — and two cards called payroll showing different totals is
// exactly the disagreeing-numbers failure docs/UI-CONVENTIONS.md exists to
// prevent. The name and the caption make the difference read as intentional.
export function SalaryByDepartment({
  people,
  compByPid,
  departments,
}: {
  people: Profile[];
  compByPid: Record<string, number>;
  departments: readonly Department[];
}) {
  const payDept: Record<string, number> = {};
  people.forEach((p) => {
    const amt = Math.round((compByPid[p.id] || 0) / 12);
    if (amt) {
      const d = departmentName(departments, p.department_id) ?? '—';
      payDept[d] = (payDept[d] || 0) + amt;
    }
  });
  const payRows = Object.entries(payDept).sort((a, b) => b[1] - a[1]);
  const maxPay = Math.max(1, ...payRows.map((r) => r[1]));
  const totalPay = payRows.reduce((s, r) => s + r[1], 0);

  return (
    <PCard
      label="Contracted salary cost · by department"
      right={<Mono>{fmtINR(totalPay)}/mo</Mono>}
    >
      {payRows.length ? (
        payRows.map(([dept, amt]) => (
          <HBar key={dept} label={dept} value={amt} max={maxPay} valueLabel={fmtINR(amt)} />
        ))
      ) : (
        <div style={NOTE}>No compensation on record yet.</div>
      )}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          gap: 'var(--hf-space-3)',
          marginTop: 'var(--hf-space-3)',
          paddingTop: 'var(--hf-space-3)',
          borderTop: '1px solid var(--hf-rule)',
          fontSize: 'var(--hf-text-xs)',
          color: 'var(--hf-ink-3)',
        }}
      >
        <span>Annual run-rate</span>
        <Mono>{fmtINR(totalPay * 12)}/yr</Mono>
      </div>
      <div
        style={{
          fontSize: 'var(--hf-text-xs)',
          color: 'var(--hf-ink-3)',
          marginTop: 'var(--hf-space-2)',
        }}
      >
        Contracted cost — the register above is what payroll computed.
      </div>
    </PCard>
  );
}

// The same empty state, four times over — it was copy-pasted at each attendance
// card, which is how three of them said one thing and drifted.
const NoAttendance = () => <div style={NOTE}>No attendance recorded yet.</div>;

// The People screen's four sub-views.
type View = 'dash' | 'details' | 'requests' | 'payroll';

// The reads this screen can report as failed, by the card each one feeds.
type ReadName =
  | 'dueDates'
  | 'exceptions'
  | 'handbooks'
  | 'costTrend'
  | 'keyDates'
  | 'events'
  | 'selExceptions'
  | 'docs'
  | 'requests'
  // The screen's own queries. Each used to be `data ?? []`, so a failed roster
  // read said "No teammates yet" and a failed attendance read drew an empty week.
  | 'roster'
  | 'setup' // leave types, roles, holidays
  | 'teamBalances'
  | 'teamAtt'
  | 'teamComp'
  | 'record'; // the open person's comp, bank, contact, balances, leave, week

/** The card a failed read would have filled: its name, the failure, and a Retry. */
const FailedCard = ({
  label,
  what,
  onRetry,
}: {
  label: string;
  what: string;
  onRetry: () => void;
}) => (
  <PCard label={label}>
    <LoadError what={what} onRetry={onRetry} />
  </PCard>
);

// A Record rather than a ternary chain: the chain this replaced had no default,
// so a fourth view silently inherited the Requests copy. Now a view without a
// subtitle is a compile error.
const VIEW_SUBTITLE: Record<View, string> = {
  dash: 'Team analytics across every department',
  details: 'Roster, records, leave & compensation',
  requests: 'Leave, time corrections and compensated hours — approve or deny',
  payroll: 'Close the month, price it, and export the register',
};

/**
 * The sub-views this viewer is offered. Payroll is people:full only. Requests &
 * approvals stays open to a people:view lead: it is the only place their role's
 * current handbook link lives (RoleHandbooksCard; rh_read allows their own
 * role), and it lists their line's compensated-hours claims. What made it read
 * as an approval queue for them was a header pill and a 44px "Awaiting you"
 * counting their own requests; both are gone, so the tab's AttentionCount —
 * gated at people:full in lib/attention.ts — is the only total, and a lead
 * gets no badge.
 */
export const peopleViews = (peopleFull: boolean): readonly View[] =>
  peopleFull ? ['dash', 'details', 'requests', 'payroll'] : ['dash', 'details', 'requests'];

// ─────────────────────────────────────────────────────────────────────────────
// Roster (left list)
// ─────────────────────────────────────────────────────────────────────────────
// Each row is a <button aria-current>, not a <div onClick>: a keyboard user could
// not open anyone's record from here (HRA-05, HRP-01; WCAG 2.1.1). Hover paint
// is the ghost button's, in tokens.css; the selected row's wash and rule are
// inline because they are state the URL owns, not a pointer state.
export function Roster({
  people,
  departments,
  selId,
  onSelect,
  loading,
  failed,
  onRetry,
  canInvite,
  onInvite,
  hidden = false,
}: {
  people: Profile[];
  departments: readonly Department[];
  selId: string | null;
  onSelect: (id: string) => void;
  loading: boolean;
  /** The roster read failed: say so, never "No teammates yet". */
  failed?: boolean;
  onRetry?: () => void;
  canInvite: boolean;
  onInvite: () => void;
  /** Out of sight while a phone shows a record, but still mounted: its search
   *  is there when the list comes back. Inline, as the `hidden` attribute would
   *  lose to the inline display. */
  hidden?: boolean;
}) {
  // Fetched here rather than passed down: Roster mounts only under the details
  // view, so the 60s poll starts and stops with the view. on_the_clock() gates
  // in its own body, so there is no permission check to make here — an
  // unauthorised viewer simply gets a roster with no pips.
  const { now, byProfile } = useOnTheClock();
  const headingId = useId();
  // Finding someone in a growing studio meant scrolling (HRP-21).
  const [query, setQuery] = useState('');
  const needle = query.trim().toLowerCase();
  const shown = needle
    ? people.filter((p) =>
        [p.full_name, p.title, departmentName(departments, p.department_id), roleLabel(p.role_id)]
          .filter(Boolean)
          .some((v) => v!.toLowerCase().includes(needle)),
      )
    : people;
  return (
    <section
      className="hf-glass"
      aria-labelledby={headingId}
      style={{
        borderRadius: 'var(--hf-radius-card)',
        overflow: 'hidden',
        display: hidden ? 'none' : 'flex',
        flexDirection: 'column',
        minHeight: 0,
        minWidth: 0,
      }}
    >
      <div
        style={{
          padding: 'var(--hf-space-3) var(--hf-space-3) var(--hf-space-2)',
          borderBottom: '1px solid var(--hf-rule)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--hf-space-2)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--hf-space-2)' }}>
          <Eyebrow as="h2" id={headingId}>
            Roster
          </Eyebrow>
          <Mono dim>{needle ? `${shown.length} of ${people.length}` : people.length}</Mono>
          {canInvite && (
            <Button
              variant="secondary"
              size="sm"
              icon="plus"
              onClick={onInvite}
              style={{ marginLeft: 'auto' }}
            >
              Invite
            </Button>
          )}
        </div>
        <TextInput
          dense
          type="search"
          aria-label="Search the roster"
          placeholder="Name, title or department"
          value={query}
          onChange={setQuery}
          leading={
            <Icon name="search" size={14} style={{ color: 'var(--hf-ink-3)', flexShrink: 0 }} />
          }
        />
      </div>
      <nav aria-label="People" className="hf-scroll" style={{ overflow: 'auto' }}>
        {loading && <div style={ROSTER_NOTE}>Loading…</div>}
        {failed && (
          <div style={{ padding: 'var(--hf-space-3)' }}>
            <LoadError what="the roster" onRetry={() => onRetry?.()} />
          </div>
        )}
        {!loading && !failed && people.length === 0 && (
          <div style={ROSTER_NOTE}>
            No teammates yet — people appear here as they’re added or sign in.
          </div>
        )}
        {!loading && people.length > 0 && shown.length === 0 && (
          <div style={ROSTER_NOTE}>Nobody matches “{query.trim()}”.</div>
        )}
        {shown.map((p) => {
          const active = p.id === selId;
          const clock = byProfile.get(p.id);
          const onFor = clock
            ? fmtDuration((now - new Date(clock.since).getTime()) / 1000, 'hm')
            : '';
          return (
            <button
              key={p.id}
              type="button"
              className="hf-btn"
              data-variant="ghost"
              aria-current={active ? 'true' : undefined}
              // Where focus returns when a phone comes back from this record.
              data-person={p.id}
              onClick={() => onSelect(p.id)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--hf-space-3)',
                width: '100%',
                padding: 'var(--hf-space-3)',
                borderRadius: 0,
                borderBottom: '1px solid var(--hf-rule)',
                textAlign: 'left',
                fontFamily: 'inherit',
                ...(active
                  ? {
                      background: 'var(--hf-accent-wash)',
                      boxShadow: 'inset 2px 0 0 var(--hf-accent)',
                    }
                  : null),
              }}
            >
              <span style={{ position: 'relative', flexShrink: 0 }}>
                <Avatar name={p.full_name || '—'} size={32} />
                {p.status === 'leave' ? (
                  <span
                    role="img"
                    aria-label="On leave"
                    title="On leave"
                    style={{ ...PIP, background: 'var(--hf-lilac)' }}
                  />
                ) : (
                  clock && (
                    <span
                      role="img"
                      aria-label={
                        clock.state === 'in' ? 'On the clock' : 'On the clock, on a break'
                      }
                      title={`${clock.state === 'in' ? 'On the clock' : 'On a break'} · ${onFor}`}
                      style={{
                        ...PIP,
                        background: clock.state === 'in' ? 'var(--hf-sage)' : 'var(--hf-sky)',
                      }}
                    />
                  )
                )}
              </span>
              <span style={{ flex: 1, minWidth: 0 }}>
                <span
                  style={{
                    display: 'block',
                    fontSize: 'var(--hf-text-sm)',
                    color: 'var(--hf-ink)',
                    fontWeight: 'var(--hf-weight-medium)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {p.full_name || 'Unnamed'}
                </span>
                <span
                  style={{
                    display: 'block',
                    fontSize: 'var(--hf-text-xs)',
                    color: 'var(--hf-ink-3)',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {p.title || departmentName(departments, p.department_id) || '—'}
                  {/* Only a contract that differs from the org's day. "org standard
                      day" sat on every row, a line that said nothing (HRP-21). */}
                  {p.contract_hrs_per_day ? ` · ${perDayText(p.contract_hrs_per_day)}` : ''}
                </span>
              </span>
              {isLeaver(p) && (
                <Badge tone="neutral" dot>
                  Left
                </Badge>
              )}
              <Badge tone={roleTone(p.role_id)}>{roleLabel(p.role_id)}</Badge>
            </button>
          );
        })}
      </nav>
    </section>
  );
}

const ROSTER_NOTE: CSSProperties = {
  padding: 'var(--hf-space-4)',
  fontSize: 'var(--hf-text-sm)',
  color: 'var(--hf-ink-3)',
};

// ─────────────────────────────────────────────────────────────────────────────
// Bank account number with a reveal toggle
// ─────────────────────────────────────────────────────────────────────────────
function BankAcct({
  accountNo,
  onSave,
}: {
  accountNo: string;
  onSave?: (v: string) => Promise<SaveResult>;
}) {
  const [show, setShow] = useState(false);
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: 'var(--hf-space-2)',
        minWidth: 0,
      }}
    >
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Masked, it is text: there is nothing to edit until it is revealed. */}
        <EditableField
          label="Account number or IBAN"
          value={show ? accountNo : maskAcct(accountNo)}
          mono
          commitOn="explicit"
          onSaveAsync={show ? onSave : undefined}
        />
      </div>
      {/* The name changes with the state, so it says what pressing it does. */}
      <Button
        variant="ghost"
        size="sm"
        icon={show ? 'eyeOff' : 'eye'}
        onClick={() => setShow((s) => !s)}
      >
        {show ? 'Hide' : 'Reveal'}
      </Button>
    </div>
  );
}

// A card's stack of fields, and a grid that folds to one column when there is
// no room for two (HRP-08): every record grid was a fixed `1fr 1fr`.
const FIELDS: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  gap: 'var(--hf-space-4)',
};
const fold = (min: string): CSSProperties => ({
  display: 'grid',
  gridTemplateColumns: `repeat(auto-fit, minmax(min(100%, ${min}), 1fr))`,
  gap: 'var(--hf-space-3)',
  alignItems: 'flex-start',
});

type PermRow = { role_id: string; module: string; level: PermLevel };
const permsByRole = (rows: readonly PermRow[]): Record<string, PermMap> => {
  const out: Record<string, PermMap> = {};
  for (const r of rows) (out[r.role_id] ??= {})[r.module] = r.level;
  return out;
};

/**
 * Does moving from one role to another grant anything, on any module? Read off
 * the matrix, not roles.rank: a custom role is ranked below the seeded ones but
 * can carry Admin's permissions, and the matrix is edited at runtime. With no
 * matrix to read, every change counts as a raise, so it is confirmed.
 * Owner is always a raise: what it adds sits outside the matrix (only an owner
 * may grant or remove 'owner', self_role_guard), so Admin to Owner, whose rows
 * are identical, would otherwise apply unasked.
 */
const raisesAccess = (
  perms: Readonly<Record<string, PermMap>> | null,
  from: string | null,
  to: string,
): boolean => {
  if (!perms || (to === 'owner' && from !== 'owner')) return true;
  const was = perms[from ?? ''] ?? {};
  return Object.entries(perms[to] ?? {}).some(([module, level]) => !can(was, module, level));
};

// ─────────────────────────────────────────────────────────────────────────────
// TAB: Profile — who they are and what they can reach, then contact and bank
// ─────────────────────────────────────────────────────────────────────────────
// Ordered by what HR opens this tab for: the role, department and manager came
// after an eight-field Contact card, below the fold (HRP-12).
export function ProfileTab({
  sel,
  bank,
  contact,
  showComp,
  roles,
  rolePerms,
  people,
  departments,
  canEdit,
  canAssignRoles,
  keyDates,
  events,
  exceptions,
  failed,
  onRecordChanged,
  actorIsOwner,
  isSelf,
  onSetRole,
  onPatch,
  onPatchBank,
  onPatchContact,
  onErased,
}: {
  sel: Profile;
  bank: Bank | null;
  contact: Contact | null;
  showComp: boolean;
  /** Ordered most senior first (roles.rank), for the list only. */
  roles: { id: string; label: string }[];
  /** Each role's row of the permission matrix; a role with no rows is all
   *  `none`. Null when the matrix could not be read. */
  rolePerms: Readonly<Record<string, PermMap>> | null;
  people: Profile[];
  departments: readonly Department[];
  canEdit: boolean;
  canAssignRoles: boolean;
  keyDates: KeyDate[];
  events: EngagementEvent[];
  exceptions: HrException[];
  /** Reads that failed: each renders as an error with Retry, never as "none yet". */
  failed: ReadonlySet<ReadName>;
  onRecordChanged: () => void;
  actorIsOwner: boolean;
  isSelf: boolean;
  onSetRole: (roleId: string) => void;
  onPatch: (delta: Partial<Profile>) => Promise<SaveResult>;
  onPatchBank: (delta: Partial<Bank>) => Promise<SaveResult>;
  onPatchContact: (delta: Partial<Contact>) => Promise<SaveResult>;
  onErased: () => void;
}) {
  const toast = useToast();
  // Owner rows are untouchable unless the actor is an owner; the 'owner'
  // option itself is owner-grantable only (the DB trigger enforces the same).
  // Never your own: an admin could demote themselves in one change, and the
  // database now refuses it too (HRP-02).
  const roleEditable = canAssignRoles && !isSelf && (sel.role_id !== 'owner' || actorIsOwner);
  // Contact and bank are the person's own as well as people:full's
  // (contact_rw, bank_self_read; UI-CONVENTIONS Vocabulary).
  // On your own record that person is you.
  const ownAudience = `${isSelf ? 'you' : sel.full_name?.trim().split(/\s+/)[0] || 'this person'}, HR and admins`;
  const roleOptions = roles.filter((r) => r.id !== 'owner' || actorIsOwner);
  // A raise is staged and confirmed with what it grants (UI-CONVENTIONS §5,
  // rung 2); a step down applies at once, with Undo. Whether a step down also
  // confirms is an open owner decision (plan §8).
  const [raising, setRaising] = useState<string | null>(null);
  const pickRole = (next: string) => {
    if (next === sel.role_id) return;
    if (raisesAccess(rolePerms, sel.role_id, next)) setRaising(next);
    else onSetRole(next);
  };
  const readsComp = (id: string | null) => can(rolePerms?.[id ?? ''], 'people', 'full');
  const name = sel.full_name ?? 'this person';
  const raisingLabel = roles.find((r) => r.id === raising)?.label ?? roleLabel(raising);

  // A field gets a save only when this viewer may write it. Passing one
  // regardless let a people:view lead edit a teammate's title: the header
  // updated, RLS matched zero rows (not an error), and it was gone on reload.
  // Their own record is edited from My Space.
  const save = (fn: (v: string) => Promise<SaveResult>) => (canEdit ? fn : undefined);
  // For the selects, which have no inline "Saved": the toast carries it.
  const report = (what: string, res: Promise<SaveResult>) =>
    void res.then(({ error }) =>
      toast.push(
        error
          ? { tone: 'error', message: `Couldn’t change ${what} — ${error}` }
          : { tone: 'success', message: `Saved ${name}’s ${what}.` },
      ),
    );
  const managerName = people.find((p) => p.id === sel.manager_id)?.full_name ?? '';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--hf-space-3)' }}>
      <PCard label="Name, role & department">
        <div style={{ ...fold('13rem'), gap: 'var(--hf-space-4)' }}>
          <EditableField
            label="Full name"
            value={sel.full_name ?? ''}
            placeholder={canEdit ? 'Add a name' : undefined}
            onSaveAsync={save((v) => onPatch({ full_name: v.trim() || null }))}
          />
          <EditableField
            label="Job title"
            value={sel.title ?? ''}
            onSaveAsync={save((v) => onPatch({ title: v }))}
          />
          <EditableField
            label="Location"
            value={sel.location ?? ''}
            onSaveAsync={save((v) => onPatch({ location: v }))}
          />
          {roleEditable ? (
            <Field label="System role" hint="Decides what they can see and do.">
              {(control) => (
                <Select
                  {...control}
                  value={sel.role_id ?? ''}
                  onChange={pickRole}
                  options={roleOptions.map((r) => ({ value: r.id, label: r.label }))}
                />
              )}
            </Field>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--hf-space-2)' }}>
              <EditableField label="System role" value={roleLabel(sel.role_id)} />
              {canAssignRoles && isSelf && (
                <span style={{ fontSize: 'var(--hf-text-xs)', color: 'var(--hf-ink-3)' }}>
                  You can’t change your own role. Ask another admin.
                </span>
              )}
            </div>
          )}
          {/* A managed list, not free text, so one typo can no longer split a
              department in two. Only people:full may change it
              (protect_profile_columns); this select is the courtesy. */}
          {canEdit ? (
            <Field label="Department">
              {(control) => (
                <Select
                  {...control}
                  value={sel.department_id ?? ''}
                  onChange={(v) => report('department', onPatch({ department_id: v || null }))}
                  options={[
                    { value: '', label: '— none —' },
                    // An archived department is no longer offered, but someone
                    // still in one must see it selected rather than "none".
                    ...departments
                      .filter((d) => !d.archived || d.id === sel.department_id)
                      .map((d) => ({
                        value: d.id,
                        label: d.archived ? `${d.name} (archived)` : d.name,
                      })),
                  ]}
                />
              )}
            </Field>
          ) : (
            <EditableField
              label="Department"
              value={departmentName(departments, sel.department_id) ?? ''}
            />
          )}
          {/* Decides who approves this person's compensated hours, so it is
              HR-only — self-service here would be self-approval one step off. */}
          {canEdit ? (
            <Field label="Reports to" hint="Approves their compensated hours.">
              {(control) => (
                <Select
                  {...control}
                  value={sel.manager_id ?? ''}
                  onChange={(v) => report('manager', onPatch({ manager_id: v || null }))}
                  options={[
                    { value: '', label: '— nobody —' },
                    // Active people only — except the current manager, who may
                    // have left since and must still show as selected.
                    ...people
                      .filter(
                        (p) =>
                          p.id !== sel.id && (p.status === 'active' || p.id === sel.manager_id),
                      )
                      .map((p) => ({
                        value: p.id,
                        label: `${p.full_name ?? 'Unnamed'}${isLeaver(p) ? ' (left)' : ''}`,
                      })),
                  ]}
                />
              )}
            </Field>
          ) : (
            <EditableField label="Reports to" value={managerName} />
          )}
          {/* Server-assigned from a sequence and immutable for every role, so it
              is shown rather than edited. It is what payroll and statutory
              records hang off once a name has been erased. */}
          <EditableField
            label="Employee №"
            value={sel.employee_no == null ? '' : String(sel.employee_no)}
            mono
          />
        </div>
      </PCard>

      {/* Contact and bank are read through people:full (contact_rw, bank_rw). For
          anyone else the rows do not arrive, and eight dashes read as "no data"
          when the truth is "not yours to see" (HRP-V1). */}
      {showComp ? (
        <div style={fold('20rem')}>
          <PCard label="Contact" right={<SensitiveMark label={`Only ${ownAudience}`} />}>
            <div style={FIELDS}>
              <EditableField
                label="Mobile"
                value={contact?.mobile ?? ''}
                mono
                type="tel"
                onSaveAsync={save((v) => onPatchContact({ mobile: v }))}
              />
              <EditableField
                label="Personal email"
                value={contact?.personal_email ?? ''}
                type="email"
                onSaveAsync={save((v) => onPatchContact({ personal_email: v }))}
              />
              <EditableField
                label="Address"
                value={contact?.address ?? ''}
                multiline
                onSaveAsync={save((v) => onPatchContact({ address: v }))}
              />
              <EditableField
                label="Date of birth"
                value={contact?.dob ?? ''}
                mono
                type="date"
                onSaveAsync={save((v) => onPatchContact({ dob: v || null }))}
              />
              <EditableField
                label="Emergency contact"
                value={contact?.emergency_name ?? ''}
                onSaveAsync={save((v) => onPatchContact({ emergency_name: v }))}
              />
              <EditableField
                label="Relationship"
                value={contact?.emergency_relation ?? ''}
                onSaveAsync={save((v) => onPatchContact({ emergency_relation: v }))}
              />
              <EditableField
                label="Emergency phone"
                value={contact?.emergency_phone ?? ''}
                mono
                type="tel"
                onSaveAsync={save((v) => onPatchContact({ emergency_phone: v }))}
              />
            </div>
          </PCard>

          {/* Where the salary is paid: every field is an explicit Save, never a
              blur (UI-CONVENTIONS §6). */}
          <PCard label="Bank details" right={<SensitiveMark label={`Only ${ownAudience}`} />}>
            <div style={FIELDS}>
              <EditableField
                label="Account holder"
                value={bank?.holder ?? ''}
                commitOn="explicit"
                onSaveAsync={save((v) => onPatchBank({ holder: v }))}
              />
              <BankAcct
                accountNo={bank?.account_no ?? ''}
                onSave={save((v) => onPatchBank({ account_no: v }))}
              />
              <div style={{ ...fold('9rem'), gap: 'var(--hf-space-4)' }}>
                <EditableField
                  label="Bank code (IFSC or SWIFT)"
                  value={bank?.ifsc ?? ''}
                  mono
                  commitOn="explicit"
                  onSaveAsync={save((v) => onPatchBank({ ifsc: v }))}
                />
                <EditableField
                  label="Branch"
                  value={bank?.branch ?? ''}
                  commitOn="explicit"
                  onSaveAsync={save((v) => onPatchBank({ branch: v }))}
                />
              </div>
              <EditableField
                label="Bank"
                value={bank?.bank_name ?? ''}
                commitOn="explicit"
                onSaveAsync={save((v) => onPatchBank({ bank_name: v }))}
              />
            </div>
          </PCard>
        </div>
      ) : (
        <LockCard>
          {isSelf
            ? 'Your contact and bank details are on My space.'
            : `Contact and bank details: only ${ownAudience} can see them.`}
        </LockCard>
      )}

      {failed.has('keyDates') ? (
        <FailedCard label="Key dates" what="their key dates" onRetry={onRecordChanged} />
      ) : (
        <KeyDatesCard
          profileId={sel.id}
          rows={keyDates}
          canEdit={canEdit}
          onChanged={onRecordChanged}
        />
      )}
      {failed.has('events') ? (
        <FailedCard
          label="Service history"
          what="their service history"
          onRetry={onRecordChanged}
        />
      ) : (
        <ServiceHistoryCard
          profileId={sel.id}
          rows={events}
          canEdit={canEdit}
          onChanged={onRecordChanged}
        />
      )}
      {/* R7: the deviations recorded against this person, on the record they
          attach to — not only in the central list. */}
      {failed.has('selExceptions') ? (
        <FailedCard
          label="Exceptions on this record"
          what="the exceptions on this record"
          onRetry={onRecordChanged}
        />
      ) : (
        <ExceptionsCard rows={exceptions} compact />
      )}

      {canEdit &&
        !isSelf &&
        (sel.role_id !== 'owner' || actorIsOwner) &&
        (sel.status === 'active' || isLeaver(sel)) && (
          <LeaverCard
            sel={sel}
            onPatch={(delta) =>
              report(
                delta.status === 'active' ? 'status to active' : 'status to left',
                onPatch(delta),
              )
            }
          />
        )}
      {canAssignRoles && (
        <DataPrivacyCard profileId={sel.id} name={sel.full_name} onErased={onErased} />
      )}

      <ConfirmDialog
        open={raising !== null}
        title={`Make ${name} ${/^[aeiou]/i.test(raisingLabel) ? 'an' : 'a'} ${raisingLabel}?`}
        consequence={[
          `From their next page load, ${name} can see and do everything ${raisingLabel} can, in place of ${roleLabel(sel.role_id)}.`,
          ...(readsComp(raising) && !readsComp(sel.role_id)
            ? ['That includes everyone’s compensation, bank and contact details.']
            : []),
          ...(raising === 'owner'
            ? ['An Owner can change any role and every org setting, including yours.']
            : []),
          'You can change it back here at any time.',
        ]}
        confirmLabel={`Change role to ${raisingLabel}`}
        onCancel={() => setRaising(null)}
        onConfirm={() => {
          if (raising) onSetRole(raising);
          setRaising(null);
        }}
      />
    </div>
  );
}

/** A leaver: status 'inactive'. 'archived' means erased (DataPrivacyCard). */
export const isLeaver = (p: Pick<Profile, 'status'>) => p.status === 'inactive';

// A leaver is status 'inactive' — 'archived' means erased (DataPrivacyCard), and
// protect_profile_columns already lets people:full set either. The notification
// fan-outs (calendar audiences, HR and manager queues) pick recipients with
// status = 'active', and so does the Reports to list, so this one value takes a
// leaver out of all of them while their records stay. It does not block sign-in.
export function LeaverCard({
  sel,
  onPatch,
}: {
  sel: Profile;
  onPatch: (delta: Partial<Profile>) => void;
}) {
  const name = sel.full_name ?? 'this person';
  const left = isLeaver(sel);
  const [confirming, setConfirming] = useState(false);
  return (
    <PCard label="Employment status">
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 'var(--hf-space-3)',
          flexWrap: 'wrap',
        }}
      >
        <span
          style={{
            fontSize: 'var(--hf-text-sm)',
            color: 'var(--hf-ink-3)',
            flex: '1 1 14rem',
            minWidth: 0,
          }}
        >
          {left
            ? `${name} has left: no calendar or team notifications, and not offered as a manager. They can still sign in until their role changes.`
            : 'When someone leaves the company, mark them as left. Their records are kept.'}
        </span>
        {left ? (
          <Button size="sm" onClick={() => onPatch({ status: 'active' })}>
            Undo — mark as active
          </Button>
        ) : (
          // Secondary, not danger: it is reversible, and rose is for what is
          // wrong (UI-CONVENTIONS §3). The consequences are read in the confirm.
          <Button size="sm" onClick={() => setConfirming(true)}>
            Mark as left
          </Button>
        )}
      </div>
      <ConfirmDialog
        open={confirming}
        title={`Mark ${name} as left?`}
        consequence={[
          'They drop out of calendar and team notifications, and can’t be picked as a manager.',
          'Their records are kept, and you can undo this here.',
          'It does not stop them signing in or change their role. Do that separately.',
        ]}
        confirmLabel="Mark as left"
        onCancel={() => setConfirming(false)}
        onConfirm={() => {
          setConfirming(false);
          onPatch({ status: 'inactive' });
        }}
      />
    </PCard>
  );
}

// Data-subject rights: export a person's full record as JSON, or erase their PII.
// Erase is the one irreversible act on a record, so it sits apart from Export in
// its own zone and is typed to confirm (UI-CONVENTIONS §5, rung 3). It was a
// window.confirm on a button beside Export, followed by a full page reload that
// said nothing (HRP-09).
export function DataPrivacyCard({
  profileId,
  name,
  onErased,
}: {
  profileId: string;
  name: string | null;
  /** After the erase lands: the caller refetches the roster and the record. */
  onErased: () => void;
}) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);
  const who = name ?? 'this person';
  const exportData = async () => {
    if (!supabase || busy) return;
    setBusy(true);
    setErr(null);
    const { data, error } = await supabase.rpc('export_personal_data', { p_profile: profileId });
    setBusy(false);
    if (error) return setErr(error.message);
    const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `personal-data-${(name ?? profileId).replace(/\s+/g, '-').toLowerCase()}.json`;
    a.click();
    URL.revokeObjectURL(url);
    toast.push({ tone: 'success', message: `Exported ${who}’s data.` });
  };
  const erase = async () => {
    setConfirming(false);
    if (!supabase || busy) return;
    setBusy(true);
    setErr(null);
    const { error } = await supabase.rpc('erase_personal_data', { p_profile: profileId });
    setBusy(false);
    if (error) return setErr(error.message);
    toast.push({ tone: 'success', message: `Erased ${who}’s personal data.` });
    onErased();
  };
  const row: CSSProperties = {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--hf-space-3)',
    flexWrap: 'wrap',
  };
  const note: CSSProperties = {
    fontSize: 'var(--hf-text-sm)',
    color: 'var(--hf-ink-3)',
    flex: '1 1 14rem',
    minWidth: 0,
  };
  return (
    <PCard label="Data & privacy" right={<Badge tone="neutral">GDPR / DPDP</Badge>}>
      {err && (
        <div style={{ marginBottom: 'var(--hf-space-3)' }}>
          <InlineStatus tone="error">{err}</InlineStatus>
        </div>
      )}
      <div style={row}>
        <span style={note}>Export everything the system holds about this person.</span>
        <Button
          variant="secondary"
          size="sm"
          icon="download"
          loading={busy}
          onClick={() => void exportData()}
        >
          Export data
        </Button>
      </div>
      <div
        style={{
          ...row,
          marginTop: 'var(--hf-space-4)',
          paddingTop: 'var(--hf-space-4)',
          borderTop: '1px solid var(--hf-rose-line)',
        }}
      >
        <span style={note}>
          <strong style={{ color: 'var(--hf-ink-2)', fontWeight: 'var(--hf-weight-medium)' }}>
            Erase personal data.
          </strong>{' '}
          On request only. It cannot be undone.
        </span>
        <Button variant="danger" size="sm" disabled={busy} onClick={() => setConfirming(true)}>
          Erase…
        </Button>
      </div>
      <ConfirmDialog
        open={confirming}
        tone="danger"
        title={`Erase ${who}’s personal data?`}
        consequence={[
          'Their contact, bank and compensation data are deleted, and their profile is anonymised.',
          'Attendance and leave history are kept, without their name.',
          'This cannot be undone.',
        ]}
        confirmLabel="Erase personal data"
        typeToConfirm={name?.trim() || 'erase'}
        onCancel={() => setConfirming(false)}
        onConfirm={() => void erase()}
      />
    </PCard>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TAB: Job description — HR writes markdown; everyone reads it rendered.
// ─────────────────────────────────────────────────────────────────────────────
// It was a three-row textarea that saved on blur and never showed the editor
// what employees would see (HRP-20). Editors now read the same rendering, and
// an edit is a draft with a preview and an explicit Save.
export function JDTab({
  sel,
  canEdit,
  onPatch,
}: {
  sel: Profile;
  canEdit: boolean;
  onPatch: (delta: Partial<Profile>) => Promise<SaveResult>;
}) {
  const toast = useToast();
  const saved = sel.job_description ?? '';
  // null: reading. A string: the draft being edited.
  const [draft, setDraft] = useState<string | null>(null);
  const [mode, setMode] = useState<'write' | 'preview'>('write');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const opener = useId();
  const close = () => {
    setDraft(null);
    setMode('write');
    setErr(null);
  };
  const commit = async () => {
    if (draft === null) return;
    if (draft === saved) return close();
    setBusy(true);
    setErr(null);
    const { error } = await onPatch({ job_description: draft });
    setBusy(false);
    if (error) return setErr(error);
    toast.push({
      tone: 'success',
      message: `Saved ${sel.full_name ?? 'their'}${sel.full_name ? '’s' : ''} job description.`,
    });
    close();
  };
  const body = (source: string) =>
    source ? (
      <div style={{ maxWidth: 720 }}>
        <Markdown source={source} />
      </div>
    ) : (
      <div style={{ fontSize: 'var(--hf-text-sm)', color: 'var(--hf-ink-3)' }}>
        No job description set.
      </div>
    );
  return (
    <PCard
      label="Job description"
      right={
        canEdit &&
        draft === null && (
          <span id={opener}>
            <Button variant="secondary" size="sm" onClick={() => setDraft(saved)}>
              {saved ? 'Edit' : 'Write one'}
            </Button>
          </span>
        )
      }
    >
      {draft === null ? (
        <div className="hf-scroll" style={{ maxHeight: 460, overflowY: 'auto' }}>
          {body(saved)}
        </div>
      ) : (
        <DisclosedForm
          opener={opener}
          // Edit means "let me type": focus lands in the text, not on the switch.
          first="textarea"
          style={{ display: 'flex', flexDirection: 'column', gap: 'var(--hf-space-3)' }}
        >
          <SegmentedControl
            label="Job description editor"
            options={[
              { value: 'write', label: 'Write' },
              { value: 'preview', label: 'Preview' },
            ]}
            value={mode}
            onChange={setMode}
          />
          {mode === 'write' ? (
            <Field label="Job description" hint="Markdown: # headings, - lists, **bold**.">
              <TextArea autoResize rows={12} value={draft} onChange={setDraft} />
            </Field>
          ) : (
            body(draft)
          )}
          {err && <InlineStatus tone="error">Couldn’t save — {err}</InlineStatus>}
          <div style={{ display: 'flex', gap: 'var(--hf-space-2)' }}>
            <Button variant="primary" size="sm" loading={busy} onClick={() => void commit()}>
              Save
            </Button>
            <Button variant="ghost" size="sm" disabled={busy} onClick={close}>
              Cancel
            </Button>
          </div>
        </DisclosedForm>
      )}
    </PCard>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TAB: Compensation — gated; lock card otherwise
// ─────────────────────────────────────────────────────────────────────────────
// What a card would hold, and who can see it, for a viewer who can't. No
// glyph: the lock means a month that can no longer change (UI-CONVENTIONS,
// Icons), and the sentence names the audience in SensitiveMark's words.
function LockCard({ children }: { children: React.ReactNode }) {
  return (
    <PCard>
      <div style={{ fontSize: 'var(--hf-text-sm)', color: 'var(--hf-ink-3)' }}>{children}</div>
    </PCard>
  );
}

// The record's figures, in two sizes on the type scale. Adjacent tabs used five
// (20, 26, 30, 34 and 40px), so no number read as the headline, under a 9.5px
// label recipe copied a dozen times (HRP-17).
const HERO: CSSProperties = {
  fontFamily: 'var(--hf-font-display)',
  fontSize: 'var(--hf-text-3xl)',
  fontWeight: 'var(--hf-weight-regular)',
  color: 'var(--hf-ink)',
  letterSpacing: 'var(--hf-track-display)',
  lineHeight: 'var(--hf-leading-none)',
};
const UNIT: CSSProperties = {
  fontSize: 'var(--hf-text-sm)',
  color: 'var(--hf-ink-3)',
  fontFamily: 'var(--hf-font-mono)',
  letterSpacing: 0,
  marginLeft: 'var(--hf-space-1)',
};
const NOTE: CSSProperties = { fontSize: 'var(--hf-text-sm)', color: 'var(--hf-ink-3)' };
/** The line under a figure's bar: small, quiet, digits that line up. */
const WEEK_NOTE: CSSProperties = {
  fontSize: 'var(--hf-text-xs)',
  color: 'var(--hf-ink-3)',
  fontVariantNumeric: 'tabular-nums',
};

/** A labelled figure inside a card: an eyebrow over a mono value. */
function Figure({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div style={{ minWidth: 0 }}>
      <Eyebrow as="div" style={{ marginBottom: 'var(--hf-space-1)' }}>
        {label}
      </Eyebrow>
      <div
        style={{
          fontFamily: 'var(--hf-font-mono)',
          fontSize: 'var(--hf-text-xl)',
          color: 'var(--hf-ink)',
          fontVariantNumeric: 'tabular-nums',
        }}
      >
        {children}
      </div>
    </div>
  );
}

/** Hours as the figure the rest of the app sums: one decimal, no unit. */
const hrs = (hours: number) => fmtDuration(hours * 3600, 'decimal');
/** A contracted day in its one shape, "8h / day" or "7h 30m / day": the Hours
 *  tab, Pay basis and the roster drew it "8 h/day", "8h / day" and "8h/day"
 *  (critique 2026-10-06 #6; UI-CONVENTIONS Vocabulary). */
const perDayText = (hours: number) => fmtDuration(hours * 3600, 'perDay');
/** The same day's length without its rate, for a sentence: "7h 30m a day". */
const dayLength = (hours: number) => perDayText(hours).replace(/ \/ day$/, '');
const ordinal = (n: number) =>
  `${n}${n % 100 >= 11 && n % 100 <= 13 ? 'th' : (['th', 'st', 'nd', 'rd'][n % 10] ?? 'th')}`;

// Changing someone's salary: stage the figure, read what it becomes, then
// commit (UI-CONVENTIONS §5, rung 2). It was a click-to-edit box under the
// figure that saved on blur — the least guarded value on the record (HRP-02).
function CtcDialog({
  name,
  current,
  onSave,
  onClose,
}: {
  name: string;
  current: number;
  onSave: (v: number) => Promise<SaveResult>;
  /** `saved` is the new figure when one was written. */
  onClose: (saved?: number) => void;
}) {
  const start = current ? String(current) : '';
  const [text, setText] = useState(start);
  const [busy, setBusy] = useState(false);
  const [fieldErr, setFieldErr] = useState<string>();
  const [refusal, setRefusal] = useState<string | null>(null);
  const parsed = parseINR(text);

  const submit = async () => {
    if (busy) return;
    if (parsed === null) {
      // Refused input is said so, on the field, and nothing is written — the old
      // digit-strip turned "14.5 L" into ₹145 without a word.
      setFieldErr(
        `Couldn’t read “${text.trim()}”. Enter whole rupees (14,50,000) or lakh / crore (14.5 L, 1.2 Cr).`,
      );
      return;
    }
    // "14,50,000" over a stored 1450000 is a different string but the same
    // salary — no write, so no compensation audit row.
    if (parsed === current) return onClose();
    setFieldErr(undefined);
    setRefusal(null);
    setBusy(true);
    const { error } = await onSave(parsed);
    setBusy(false);
    if (error) return setRefusal(error);
    onClose(parsed);
  };

  return (
    <Dialog
      open
      title={`${current ? 'Change' : 'Set'} ${name}’s annual CTC`}
      size="sm"
      onClose={() => onClose()}
      dirty={text.trim() !== start}
      footer={(requestClose) => (
        <>
          <Button variant="ghost" onClick={requestClose}>
            Cancel
          </Button>
          <Button variant="primary" loading={busy} onClick={() => void submit()}>
            {parsed !== null && parsed !== current ? `Set CTC to ${fmtINRFull(parsed)}` : 'Set CTC'}
          </Button>
        </>
      )}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--hf-space-4)' }}>
        {refusal && <InlineStatus tone="error">Couldn’t save — {refusal}</InlineStatus>}
        <Field
          label="Annual CTC (₹)"
          required
          error={fieldErr}
          hint="Whole rupees (14,50,000), or lakh / crore (14.5 L, 1.2 Cr)."
        >
          <TextInput
            mono
            inputMode="decimal"
            autoComplete="off"
            value={text}
            onChange={(v) => {
              setText(v);
              if (fieldErr) setFieldErr(undefined);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') void submit();
            }}
          />
        </Field>
        {parsed !== null && parsed !== current && (
          <div
            style={{
              fontSize: 'var(--hf-text-sm)',
              lineHeight: 'var(--hf-leading-body)',
              color: 'var(--hf-ink-2)',
            }}
          >
            <Mono>
              {current ? fmtINRFull(current) : 'no CTC'} → {fmtINRFull(parsed)}
            </Mono>{' '}
            a year, which is <Mono>{fmtINRFull(Math.round(parsed / 12))}</Mono> a month.
            <ul style={{ margin: 'var(--hf-space-2) 0 0', paddingLeft: 'var(--hf-space-5)' }}>
              <li>Payroll runs from now on are priced from the new figure.</li>
              <li>A locked month keeps the figure it was priced with.</li>
              <li>The change is kept in the audit trail, with your name.</li>
            </ul>
          </div>
        )}
      </div>
    </Dialog>
  );
}

// A "Monthly split" (Basic 50% · HRA 20% · …) and a "Next pay run" date used to
// render here. Neither had a source: payroll computes no such split, and the date
// was the last day of the browser's month with no org setting behind it. A figure
// needs a source before it gets a tile — HR quoting "Basic ₹60K/mo" or "paid on
// the 30th" to an employee would be quoting numbers payroll disagrees with.
export function CompTab({
  sel,
  comp,
  showComp,
  onSaveCtc,
  orgHours,
  yearWorkingDays,
}: {
  sel: Profile;
  comp: Comp | null;
  showComp: boolean;
  onSaveCtc: (v: number) => Promise<SaveResult>;
  orgHours: OrgHours;
  yearWorkingDays: number;
}) {
  const toast = useToast();
  // Org-wide comp context (share, median, position) — RLS already limits
  // employee_compensation reads to people=full holders.
  const [teamComp, setTeamComp] = useState<number[]>([]);
  const [teamFailed, setTeamFailed] = useState(false);
  const [teamNonce, setTeamNonce] = useState(0);
  const [changing, setChanging] = useState(false);
  useEffect(() => {
    if (!supabase || !showComp) return;
    let active = true;
    supabase
      .from('employee_compensation')
      .select('annual_ctc')
      .then(({ data, error }) => {
        if (!active) return;
        setTeamFailed(!!error);
        const vals = ((data as { annual_ctc: number | null }[]) ?? [])
          .map((r) => r.annual_ctc ?? 0)
          .filter((v) => v > 0)
          .sort((a, b) => a - b);
        setTeamComp(vals);
      });
    return () => {
      active = false;
    };
  }, [showComp, teamNonce]);

  if (!showComp) {
    return <LockCard>Compensation: only HR and admins can see it.</LockCard>;
  }
  const name = sel.full_name ?? 'this person';
  const first = sel.full_name?.split(' ')[0] ?? 'This person';
  const ctc = comp?.annual_ctc ?? 0;
  const perDay = personDay(sel.contract_hrs_per_day, orgHours);
  const monthly = ctc ? Math.round(ctc / 12) : 0;
  // Was `ctc / (52 * avail_hrs)` — a flat 52 weeks that counted public holidays
  // and the org's non-working days as paid working time. Divide by the hours the
  // person is actually contracted for across the year instead.
  const annualHrs = perDay * yearWorkingDays;
  const perHr = ctc && annualHrs ? Math.round(ctc / annualHrs) : 0;

  const contracted = teamComp.reduce((s, v) => s + v, 0);
  const share = contracted && ctc ? (ctc / contracted) * 100 : 0;
  const median = teamComp.length ? teamComp[Math.floor(teamComp.length / 2)] : 0;
  const vsMedian = median && ctc ? ((ctc - median) / median) * 100 : 0;
  const selIdx = teamComp.findIndex((v) => v === ctc);
  const maxComp = Math.max(1, ...teamComp);
  // teamComp is sorted low to high, so the last bar is the highest paid.
  const position =
    selIdx >= 0
      ? `${first}’s CTC is the ${ordinal(teamComp.length - selIdx)} highest of ${teamComp.length}.`
      : `${teamComp.length} people have a CTC on record.`;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--hf-space-3)' }}>
      <div style={fold('20rem')}>
        {/* One marker for the tab: its three cards share one audience. */}
        <PCard label="Annual CTC" right={<SensitiveMark />}>
          {ctc > 0 ? (
            <div>
              <div style={HERO}>{fmtINR(ctc)}</div>
              <Mono dim style={{ display: 'block', marginTop: 'var(--hf-space-1)' }}>
                {fmtINRFull(ctc)} / year
              </Mono>
            </div>
          ) : (
            <div style={NOTE}>No compensation on record.</div>
          )}
          <div style={{ marginTop: 'var(--hf-space-4)' }}>
            <Button variant="secondary" size="sm" onClick={() => setChanging(true)}>
              {ctc ? 'Change CTC' : 'Set CTC'}
            </Button>
          </div>
        </PCard>

        {/* One card of derived figures, where there were four glass tiles each
            wearing a padlock "calculated" chip (HRP-19: the lock means "no
            permission", not "derived"). */}
        <PCard label="Derived from the CTC">
          <div style={{ ...fold('9rem'), gap: 'var(--hf-space-4)' }}>
            <Figure label="Monthly">{monthly ? fmtINRFull(monthly) : '—'}</Figure>
            <Figure label="Cost / hour">{perHr ? fmtINRFull(perHr) : '—'}</Figure>
            <Figure label="Share of contracted cost">{share ? `${share.toFixed(1)}%` : '—'}</Figure>
            <Figure label="vs team median">
              {median && ctc
                ? `${vsMedian >= 0 ? '+' : '−'}${Math.abs(vsMedian).toFixed(0)}%`
                : '—'}
            </Figure>
          </div>
        </PCard>
      </div>

      <PCard label="Team comp position">
        {teamFailed ? (
          <LoadError what="the team’s compensation" onRetry={() => setTeamNonce((n) => n + 1)} />
        ) : (
          <>
            <div
              role="img"
              aria-label={`Everyone with a CTC, lowest to highest. ${position}`}
              style={{
                display: 'flex',
                alignItems: 'flex-end',
                gap: 'var(--hf-space-1)',
                height: 64,
              }}
            >
              {teamComp.map((v, i) => (
                <div
                  key={i}
                  style={{
                    flex: 1,
                    height: `${Math.max(8, (v / maxComp) * 100)}%`,
                    borderRadius: 'var(--hf-radius-sm) var(--hf-radius-sm) 0 0',
                    background: i === selIdx ? 'var(--hf-accent)' : 'var(--hf-rule-3)',
                  }}
                />
              ))}
            </div>
            <div style={{ ...NOTE, marginTop: 'var(--hf-space-2)' }}>
              Lowest to highest. {position}
            </div>
          </>
        )}
      </PCard>

      {/* Only what has a source: the currency on the comp row and the contracted
          day. "Cycle: Monthly" was a literal and "Next pay run" a guess (see
          the note above CompTab), so the card is no longer a pay schedule. */}
      <PCard label="Pay basis">
        <div style={{ ...fold('9rem'), gap: 'var(--hf-space-4)' }}>
          <Figure label="Currency">{comp?.currency || 'INR'}</Figure>
          <Figure label="Contracted">{perDayText(perDay)}</Figure>
        </div>
      </PCard>

      {changing && (
        <CtcDialog
          name={name}
          current={ctc}
          onSave={onSaveCtc}
          onClose={(saved) => {
            setChanging(false);
            if (saved != null)
              toast.push({
                tone: 'success',
                message: `${first}’s annual CTC is now ${fmtINRFull(saved)}.`,
              });
          }}
        />
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TAB: Leave — balances (steppered, gated) + calendar
// ─────────────────────────────────────────────────────────────────────────────
export function LeaveTab({
  leaveTypes,
  balances,
  leaveDates,
  canEdit,
  profileId,
  onSetAllotted,
  weekStartsOn,
  holidays,
  workdays,
  today,
  uid,
  personName,
  onRecorded,
}: {
  leaveTypes: LeaveType[];
  balances: LeaveBalance[];
  leaveDates: { date: string; type: string; reason?: string | null }[];
  canEdit: boolean;
  /** Whose balances and trail. lb_read and ll_read show HR anyone and a person
   *  only themselves, so anyone else gets a lock rather than empty reads. */
  profileId: string;
  /** Resolves with `error` when the database refused (blank reason, below
   *  `used`, no permission): it is said under the row, which stays staged. */
  onSetAllotted: (leaveTypeId: string, allotted: number, reason: string) => Promise<SaveResult>;
  holidays?: Record<string, LeaveCalendarHoliday>;
  /** The org's working week: the calendar hatches the days it doesn't work. */
  workdays?: Workdays | null;
  weekStartsOn: number;
  /** The org's today, so the calendar opens on the org's month. */
  today?: string;
  /** The viewer: the recorder, stamped as `approver` on anything filed here,
   *  and who may read this record's balances below people:full. */
  uid: string;
  /** Whose record this is. Labels only. */
  personName: string | null;
  /** After leave is recorded, with what was charged: the balance and the calendar
   *  both move, and the page says so. */
  onRecorded: (charged: number, typeLabel: string) => void;
}) {
  const toast = useToast();
  // Staged allotment edits, keyed by leave type. A pay-affecting change should be
  // deliberate rather than a side effect of clicking an arrow, so the stepper
  // moves this and the Save below commits it with its reason.
  const [draft, setDraft] = useState<Record<string, number | undefined>>({});
  const [reason, setReason] = useState<Record<string, string>>({});
  const [refused, setRefused] = useState<Record<string, string | undefined>>({});
  const [saving, setSaving] = useState<string | null>(null);
  const [recording, setRecording] = useState(false);
  const byType: Record<string, LeaveBalance> = {};
  balances.forEach((b) => (byType[b.leave_type_id] = b));
  // PAID types only. An unpaid type has allotted = 0 and still records `used`
  // (the trigger writes it for the record, 16_leave_integrity:117), so pooling
  // them took five days off "days left" for five days of LOP — a number nobody
  // could reconcile, on both this screen and My Space. Not archived ones either:
  // the server refuses new leave on them, so what is left there cannot be taken.
  const paidTypes = leaveTypes.filter((t) => t.paid && !t.archived);
  const totalAllot = paidTypes.reduce((s, t) => s + (byType[t.id]?.allotted ?? 0), 0);
  const totalUsed = paidTypes.reduce((s, t) => s + (byType[t.id]?.used ?? 0), 0);
  const typeMeta: Record<string, { label: string; color: string }> = {};
  leaveTypes.forEach((t) => (typeMeta[t.id] = { label: t.label, color: t.color }));
  const unstage = (id: string) => {
    setDraft((d) => ({ ...d, [id]: undefined }));
    setRefused((r) => ({ ...r, [id]: undefined }));
  };
  // Balances (lb_read), the ledger (ll_read) and the requests the calendar is
  // drawn from (lr_read) are self or people:full. A lead on a teammate's record
  // got RLS-empty reads, drawn as "0 left", "nothing recorded yet" and "No
  // leave or holiday" on a day they were off: facts that were not true.
  const locked = !canEdit && profileId !== uid;
  // An archived type stays while this person has a balance on it, read-only,
  // so its history keeps its row; it is never offered for anything new.
  const shownTypes = leaveTypes.filter((t) => !t.archived || byType[t.id]);

  return (
    <div style={fold('22rem')}>
      {locked && (
        <LockCard>
          Leave balances, calendar and history: only{' '}
          {personName?.trim().split(/\s+/)[0] || 'this person'}, HR and admins can see them.
        </LockCard>
      )}
      {recording && (
        <RecordLeaveModal
          profileId={profileId}
          personName={personName}
          types={leaveTypes}
          uid={uid}
          holidays={holidays}
          onClose={() => setRecording(false)}
          onFiled={onRecorded}
        />
      )}
      {!locked && (
        <PCard
          label="Leave balances"
          right={
            /* Leave that was taken and never filed. The server has always allowed
             HR to record one (backdating is refused only for non-HR); there was
             simply no surface for it, because the apply form is mounted once,
             on My Space, for yourself. Not on your own record: it files the
             leave approved, and nobody decides their own (20261103100000). */
            canEdit &&
            profileId !== uid &&
            leaveTypes.some((t) => !t.archived) && (
              <Button variant="secondary" size="sm" icon="leave" onClick={() => setRecording(true)}>
                Record leave taken
              </Button>
            )
          }
        >
          {shownTypes.length === 0 ? (
            <div style={NOTE}>No leave types configured.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column' }}>
              {shownTypes.map((t) => {
                const rec = byType[t.id] ?? { allotted: 0, used: 0 };
                const bal = rec.allotted - rec.used;
                const pct = rec.allotted ? (rec.used / rec.allotted) * 100 : 0;
                const staged = draft[t.id];
                const changed = canEdit && staged !== undefined && staged !== rec.allotted;
                return (
                  <div
                    key={t.id}
                    style={{
                      padding: 'var(--hf-space-3) 0',
                      borderBottom: '1px solid var(--hf-rule)',
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 'var(--hf-space-3)',
                    }}
                  >
                    {/* The type and its allotment on one line, its bar under them: three
                      fixed tracks (120px, 1fr, auto) squeezed the bar to nothing in
                      a narrow card. */}
                    <div
                      style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        gap: 'var(--hf-space-2) var(--hf-space-3)',
                      }}
                    >
                      <div
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 'var(--hf-space-2)',
                          minWidth: 0,
                        }}
                      >
                        {/* The type's mark as the calendar's key below draws
                            it: the raw stored colour was a second shade of it. */}
                        <span aria-hidden="true" style={dayMarkStyle('leave', t.color)} />
                        <span style={{ fontSize: 'var(--hf-text-md)', color: 'var(--hf-ink)' }}>
                          {t.label}
                        </span>
                        {/* The word appears nowhere else in this module. An unpaid
                          day keeps its required hours, so it reaches payroll as a
                          shortfall and costs salary — which the person's own
                          leave record never said. */}
                        {/* Not beside a label that already says it: "Unpaid
                            [unpaid]" (critique 2026-10-06, minor). */}
                        {!t.paid && !/\bunpaid\b/i.test(t.label) && (
                          <Badge tone="neutral">unpaid</Badge>
                        )}
                        {t.archived && <Badge tone="neutral">archived</Badge>}
                      </div>
                      {canEdit && !t.paid ? (
                        // enforce_leave_request consults `allotted` only for a PAID
                        // type (`coalesce(v_paid, true) and ...`), so a stepper here
                        // moved a number with no consequence anywhere.
                        <Mono dim>not allotted</Mono>
                      ) : canEdit && !t.archived ? (
                        <Stepper
                          label={`${t.label} allotment`}
                          value={staged ?? rec.allotted}
                          onChange={(v) => setDraft((d) => ({ ...d, [t.id]: v }))}
                          min={rec.used}
                          // Never above the org standard (20261010120000).
                          max={t.days_per_year ?? undefined}
                          /* Half days are spendable, so they have to be grantable.
                         `used` already moves by 0.5, and min={rec.used} means a
                         balance sitting at 0.5 used could not otherwise be
                         matched by a whole-day allotment control. */
                          step={0.5}
                          unit="days"
                        />
                      ) : (
                        <Mono>{rec.allotted} allotted</Mono>
                      )}
                    </div>
                    <div>
                      {t.paid && (
                        <Progress value={pct} tone={t.color} size={4} label={`${t.label} used`} />
                      )}
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          marginTop: t.paid ? 'var(--hf-space-1)' : 0,
                        }}
                      >
                        <Mono dim>{rec.used} used</Mono>
                        {/* An unpaid type is uncapped by design — the server
                          skips the entitlement ceiling for it — so it has no
                          balance to be over. `allotted` is always 0, which
                          made this print "-5 bal" in rose: an overdraft
                          warning for something that cannot be overdrawn. */}
                        <Mono
                          style={{
                            color: t.paid && bal < 0 ? 'var(--hf-rose-text)' : 'var(--hf-ink-2)',
                          }}
                        >
                          {t.archived ? 'closed' : t.paid ? `${bal} left` : 'no limit'}
                        </Mono>
                      </div>
                    </div>
                    {/* The stepper stages; this commits. An allotment change is the one
                    leave movement with no cause of its own — no request, no accrual
                    — so the reason IS its audit, and the database refuses a blank
                    one. Asking on every arrow-click would be unusable, so the ask
                    lands once, when the change is actually made. */}
                    {changed && (
                      <div
                        style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          alignItems: 'flex-end',
                          gap: 'var(--hf-space-2) var(--hf-space-3)',
                        }}
                      >
                        {/* The refusal is said here, on the field: Save used to do
                          nothing at all when the database said no (HRP-03). */}
                        <Field
                          label={`Why is ${t.label} changing from ${rec.allotted} to ${staged}?`}
                          required
                          error={refused[t.id]}
                          style={{ flex: '1 1 14rem' }}
                        >
                          <TextInput
                            dense
                            value={reason[t.id] ?? ''}
                            onChange={(v) => setReason((r) => ({ ...r, [t.id]: v }))}
                            placeholder="Annual top-up"
                          />
                        </Field>
                        <span style={{ display: 'inline-flex', gap: 'var(--hf-space-2)' }}>
                          <Button
                            variant="primary"
                            size="sm"
                            loading={saving === t.id}
                            disabled={!(reason[t.id] ?? '').trim()}
                            onClick={() => {
                              setSaving(t.id);
                              void onSetAllotted(t.id, staged, reason[t.id]!.trim()).then(
                                ({ error }) => {
                                  setSaving(null);
                                  if (error) return setRefused((r) => ({ ...r, [t.id]: error }));
                                  toast.push({
                                    tone: 'success',
                                    message: `${t.label} allotment is now ${staged} days.`,
                                  });
                                  unstage(t.id);
                                  setReason((r) => ({ ...r, [t.id]: '' }));
                                },
                              );
                            }}
                          >
                            Save
                          </Button>
                          <Button variant="ghost" size="sm" onClick={() => unstage(t.id)}>
                            Cancel
                          </Button>
                        </span>
                      </div>
                    )}
                  </div>
                );
              })}
              {/* Comp off, one more balance (owner, 2026-10-09). */}
              <CompOffRow profileId={profileId} year={new Date().getFullYear()} />
            </div>
          )}
          <div style={{ ...fold('6rem'), marginTop: 'var(--hf-space-4)' }}>
            {[
              { l: 'Allotted', v: totalAllot },
              { l: 'Used', v: totalUsed },
              { l: 'Remaining', v: totalAllot - totalUsed },
            ].map((x) => (
              <Figure key={x.l} label={`${x.l} · paid`}>
                {x.v}
                <span style={UNIT}>days</span>
              </Figure>
            ))}
          </div>
        </PCard>
      )}

      {!locked && (
        <PCard label="Leaves taken · calendar">
          <LeaveCalendar
            leaveDates={leaveDates}
            leaveTypes={typeMeta}
            holidays={holidays}
            workdays={workdays}
            weekStartsOn={weekStartsOn}
            today={today}
          />
        </PCard>
      )}

      {/* Spans both columns: the balances above say WHAT the number is, this says
          how it got there. Every movement — the annual entitlement, overtime
          leave accrued from overtime, days taken and returned, and HR's own
          adjustments — with the reason and the policy that was in force. */}
      {!locked && (
        <div
          style={{
            gridColumn: '1 / -1',
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--hf-space-3)',
          }}
        >
          <LeaveTrail
            key={profileId}
            profileId={profileId}
            types={Object.fromEntries(leaveTypes.map((t) => [t.id, t.label]))}
          />
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// TAB: Hours — contracted day (gated), this week against its target, the month
// ─────────────────────────────────────────────────────────────────────────────

/** Who may CREDIT hours in one step, mirroring what the trigger enforces
 *  (20260902120000): the reporting manager, or people:full — never yourself.
 *  null when granting is allowed, otherwise the reason shown in its place. */
export function grantRefusal(
  sel: Pick<Profile, 'id' | 'manager_id'>,
  uid: string,
  perms: PermMap | undefined,
): string | null {
  if (sel.id === uid) return 'Your own hours — someone else credits them';
  if (can(perms, 'people', 'full') || sel.manager_id === uid) return null;
  return 'Only their reporting manager or HR can credit hours';
}

/**
 * Where this week stands against what was owed by now.
 *
 * It was `over ? 'over cap' : 'on track'`, so ten hours of forty on a Friday
 * read a green "on track" (HRP-16). "Behind" is measured against the hours owed
 * up to YESTERDAY: requiredToDate counts today in full, and nobody is behind at
 * 10am for not having finished the day. Amber means behind now, never behind a
 * target that has not arrived (UI-CONVENTIONS §7).
 */
export const weekPace = (
  worked: number,
  requiredToDate: number,
  perDay: number,
  weekTarget: number,
): 'behind' | 'on pace' | 'target met' | null => {
  if (weekTarget <= 0) return null;
  if (worked >= weekTarget) return 'target met';
  return worked < requiredToDate - perDay ? 'behind' : 'on pace';
};

export function HoursTab({
  sel,
  canEdit,
  onSetContract,
  orgHours,
  weekHrs,
  weekTarget,
  weekRequiredToDate,
  summary,
  tz,
  workdays,
}: {
  sel: Profile;
  canEdit: boolean;
  /** null puts them back on the org's standard day. */
  onSetContract: (v: number | null) => Promise<SaveResult>;
  orgHours: OrgHours; // the org's standard day — the fallback for an unset contract
  weekHrs: number; // hours actually logged this week (attendance, server-stamped)
  weekTarget: number; // this week's REQUIRED hours: perDay × expected days
  weekRequiredToDate: number; // …of which, owed up to and including today
  summary: MonthSummary | null; // this person's month, from lib/monthlySummary
  tz: string;
  workdays: Workdays | null;
}) {
  const toast = useToast();
  const { perms, session } = useAuth();
  // Rendered where the button would be, so a missing button is never silent.
  const refusal = grantRefusal(sel, session?.user.id ?? '', perms);
  const canGrant = refusal === null;
  const [granting, setGranting] = useState(false);
  const [grantCats, setGrantCats] = useState<Category[]>([]);
  const [grantNonce, setGrantNonce] = useState(0);
  // An auto-closed day: HR or their direct manager may set the real times,
  // never on their own record (owner, 2026-10-13). The server holds the rule.
  const me = session?.user.id ?? '';
  const canFix = !!me && sel.id !== me && (can(perms, 'people', 'full') || sel.manager_id === me);
  const [fixing, setFixing] = useState<DayToFix | null>(null);
  // Edit day (policy.editDay.rollout, HR and managers first): any day of
  // theirs opens the whole-day dialog instead of Set times.
  const editDayOn = useOrgDates().editDay !== 'off';
  const [editingDay, setEditingDay] = useState<string | null>(null);
  // The trail needs the category labels whether or not this viewer can grant —
  // only its Edit and Revoke buttons are gated on canGrant (canRevoke below).
  useEffect(() => {
    void listCategories().then(setGrantCats);
  }, []);

  const name = sel.full_name ?? 'This person';
  const perDay = personDay(sel.contract_hrs_per_day, orgHours);
  const usesOrgDay = !(sel.contract_hrs_per_day != null && sel.contract_hrs_per_day > 0);
  // The contracted day moves required hours, and required hours move pay. Every
  // ± used to be a database write (HRP-02): the stepper stages, Save confirms.
  // `null` stages a return to the org's standard day, which the stepper's
  // half-hour floor could never reach.
  const [staged, setStaged] = useState<{ to: number | null } | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [saving, setSaving] = useState(false);
  const [contractErr, setContractErr] = useState<string | null>(null);
  const stagedDay = staged ? (staged.to ?? orgHours.perDay) : perDay;
  const commitContract = async () => {
    if (!staged) return;
    setConfirming(false);
    setSaving(true);
    setContractErr(null);
    const { error } = await onSetContract(staged.to);
    setSaving(false);
    if (error) return setContractErr(error);
    toast.push({
      tone: 'success',
      message:
        staged.to === null
          ? `${name} is back on the org’s standard day.`
          : `${name} is contracted for ${dayLength(staged.to)} a day.`,
    });
    setStaged(null);
  };

  // NOT capped at 100. The cap is exactly why a 109% month — the reading their
  // spreadsheet has shown for years — could never appear here. The BAR is clamped
  // by Progress instead.
  const pct = weekTarget ? Math.round((weekHrs / weekTarget) * 100) : 0;
  const pace = weekPace(weekHrs, weekRequiredToDate, perDay, weekTarget);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--hf-space-3)' }}>
      <div style={fold('20rem')}>
        <PCard label="Contracted hours">
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 'var(--hf-space-3)',
            }}
          >
            <div>
              <div style={HERO}>
                {dayLength(perDay)}
                <span style={UNIT}>/ day</span>
              </div>
              {/* Was `avail x 52`, which knew nothing about holidays, leave or the
                  org's working days. This is the month's real requirement. */}
              <div style={{ ...NOTE, marginTop: 'var(--hf-space-2)' }}>
                {summary
                  ? `${hrs(summary.requiredHours)}h required this month · ${summary.expectedDays} days`
                  : '—'}
                {usesOrgDay && (
                  <>
                    {' '}
                    <Badge tone="neutral">org standard day</Badge>
                  </>
                )}
              </div>
            </div>
            {/* Half-hour steps because the column is numeric: a 6.5h contracted
                day is ordinary and was unrepresentable while this was an int. The
                80 ceiling was a WEEKLY one. */}
            {canEdit ? (
              <Stepper
                label="Contracted hours per day"
                value={stagedDay}
                onChange={(v) => setStaged(v === perDay ? null : { to: v })}
                min={0.5}
                max={24}
                step={0.5}
                unit="h"
                disabled={saving}
              />
            ) : (
              <span style={NOTE}>Set by HR</span>
            )}
          </div>
          {canEdit && !usesOrgDay && !staged && (
            <div style={{ marginTop: 'var(--hf-space-2)' }}>
              <Button variant="ghost" size="sm" onClick={() => setStaged({ to: null })}>
                Use the org’s standard day ({perDayText(orgHours.perDay)})
              </Button>
            </div>
          )}
          {staged && (
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                alignItems: 'center',
                gap: 'var(--hf-space-2) var(--hf-space-3)',
                marginTop: 'var(--hf-space-3)',
              }}
            >
              <Mono>
                {perDayText(perDay)} →{' '}
                {staged.to === null
                  ? `${perDayText(orgHours.perDay)} (org standard)`
                  : perDayText(staged.to)}
              </Mono>
              <Button
                variant="primary"
                size="sm"
                loading={saving}
                onClick={() => setConfirming(true)}
              >
                Save
              </Button>
              <Button
                variant="ghost"
                size="sm"
                disabled={saving}
                onClick={() => {
                  setStaged(null);
                  setContractErr(null);
                }}
              >
                Cancel
              </Button>
            </div>
          )}
          {contractErr && (
            <div style={{ marginTop: 'var(--hf-space-2)' }}>
              <InlineStatus tone="error">Couldn’t save — {contractErr}</InlineStatus>
            </div>
          )}
          {/* The card is the contract. Its strip of This week, vs target and This
              month printed the week's hours a second time beside the card that
              measures them, and the month's a second time over the summary that
              owns them (HRP-16). */}
        </PCard>

        <PCard
          label="This week · hours against target"
          right={
            pace === 'behind' ? (
              <Badge tone="warning" dot>
                behind pace
              </Badge>
            ) : (
              pace && <Badge tone="neutral">{pace}</Badge>
            )
          }
        >
          <div
            style={{
              display: 'flex',
              alignItems: 'baseline',
              gap: 'var(--hf-space-2)',
              marginBottom: 'var(--hf-space-2)',
            }}
          >
            <span style={HERO}>{hrs(weekHrs)}</span>
            <span style={UNIT}>/ {hrs(weekTarget)}h</span>
          </div>
          <Progress value={pct} size={8} label="This week’s hours against target" meter />
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              gap: 'var(--hf-space-2)',
              marginTop: 'var(--hf-space-2)',
            }}
          >
            {/* Sentences with figures in them, so the UI face, not mono
                (critique 2026-10-06, mono carrying prose). */}
            <span style={WEEK_NOTE}>{pct}% of the week’s target</span>
            {/* "Over" carries what the deleted "vs target" figure said past 100%. */}
            <span style={WEEK_NOTE}>
              {weekHrs > weekTarget
                ? `${hrs(weekHrs - weekTarget)}h over target`
                : `${hrs(weekTarget - weekHrs)}h to go`}{' '}
              · {hrs(weekRequiredToDate)}h owed by today
            </span>
          </div>
        </PCard>
      </div>
      {/* This person's month, with its own ‹ › stepper. Same component and same
          arithmetic as the org-wide table and the employee's own Timesheets view;
          only the rows differ. */}
      <MonthlySummary
        key={grantNonce}
        tz={tz}
        org={orgHours}
        workdays={workdays}
        onlyProfileId={sel.id}
      />
      {/* The action lives on the card it changes. */}
      <CompHoursTrail
        profileId={sel.id}
        canRevoke={canGrant}
        categories={grantCats}
        today={isoDay(orgToday(tz))}
        personName={sel.full_name}
        reloadKey={grantNonce}
        onChanged={() => setGrantNonce((n) => n + 1)}
        action={
          canGrant ? (
            <Button variant="secondary" size="sm" icon="award" onClick={() => setGranting(true)}>
              Credit hours
            </Button>
          ) : (
            <span style={{ fontSize: 'var(--hf-text-xs)', color: 'var(--hf-ink-3)' }}>
              {refusal}
            </span>
          )
        }
      />
      {granting && (
        <CompHoursModal
          mode="grant"
          uid={sel.id}
          personName={sel.full_name}
          categories={grantCats}
          today={isoDay(orgToday(tz))}
          onClose={() => setGranting(false)}
          onFiled={() => setGrantNonce((n) => n + 1)}
        />
      )}
      {/* grantNonce: a credit just given (or a day just set) shows up at once. */}
      <DayClockLog
        profileId={sel.id}
        reloadKey={grantNonce}
        onFix={
          canFix && !editDayOn
            ? (d) => setFixing({ ...d, name: sel.full_name || 'Someone' })
            : undefined
        }
        onEditDay={
          canFix && editDayOn ? (k) => setEditingDay(k ?? isoDay(orgToday(tz))) : undefined
        }
      />
      {editingDay && (
        <EditDayDialog
          profileId={sel.id}
          name={sel.full_name || 'Someone'}
          date={editingDay}
          pickDate
          canAmend={can(perms, 'people', 'full')}
          onClose={() => setEditingDay(null)}
          onDone={() => setGrantNonce((n) => n + 1)}
        />
      )}
      {fixing && (
        <FixDayDialog
          day={fixing}
          tz={tz}
          onClose={() => setFixing(null)}
          onFixed={() => setGrantNonce((n) => n + 1)}
        />
      )}

      <ConfirmDialog
        open={confirming && staged !== null}
        title={`Change ${name}’s contracted day?`}
        consequence={[
          `Their day goes from ${dayLength(perDay)} to ${dayLength(stagedDay)}${staged?.to === null ? ', the org’s standard' : ''}.`,
          'Required hours are recalculated from it, this month included.',
          'Required hours decide the shortfall, and a shortfall reduces pay.',
        ]}
        confirmLabel={`Set ${dayLength(stagedDay)} a day`}
        onCancel={() => setConfirming(false)}
        onConfirm={() => void commitContract()}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// DASHBOARD — team-wide analytics (composition · payroll · capacity · leave)
// ─────────────────────────────────────────────────────────────────────────────
// The charts. Each takes a `label` and draws itself as role="img" with a
// sentence that carries the figures: they were bare svg and div stacks, so the
// Attendance section said nothing to a screen reader (HRA-15). They stay in this
// file because domain.tsx belongs to another slice.
//
// One mark colour. Departments were painted from an eight-hex palette that
// borrowed the semantic hues (amber, sage, rose), so a department could look
// "waiting" or "wrong" (UI-CONVENTIONS §3); each row is already named, so the
// colour said nothing the label did not.

// Team capacity: a name, then three right-aligned figures. Shared by the
// column heads and the rows.
const CAP_GRID: CSSProperties = {
  display: 'grid',
  gridTemplateColumns: 'minmax(0, 1fr) auto auto auto',
  alignItems: 'center',
  gap: 'var(--hf-space-1) var(--hf-space-3)',
};
const CAP_NUM: CSSProperties = { minWidth: '5.5rem', textAlign: 'right' };

/** A labelled row with a bar: name, share of `max`, and the figure. */
function HBar({
  label,
  sub,
  value,
  max,
  color = 'accent',
  valueLabel,
}: {
  label: string;
  sub?: string;
  value: number;
  max: number;
  /** A tone name, or a stored colour (a leave type's). */
  color?: string;
  valueLabel: React.ReactNode;
}) {
  const pct = max ? Math.min(100, (value / max) * 100) : 0;
  // Below sm the name, bar and figure stop fitting one line, and the wrap left
  // the figure on a line of its own, away from its name and its bar ("38%"
  // under "6/16 days" at 320). There the name and the figure share the first
  // line and the bar takes the card's width under them.
  const narrow = useBelow('sm');
  const at = (gridColumn: string, gridRow: number): CSSProperties =>
    narrow ? { gridColumn, gridRow } : {};
  return (
    <div
      style={{
        ...(narrow
          ? { display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) auto', alignItems: 'baseline' }
          : { display: 'flex', flexWrap: 'wrap', alignItems: 'center' }),
        gap: 'var(--hf-space-1) var(--hf-space-3)',
        padding: 'var(--hf-space-2) 0',
      }}
    >
      <div style={{ flex: '1 1 7rem', minWidth: 0, ...at('1', 1) }}>
        <div
          style={{
            fontSize: 'var(--hf-text-sm)',
            color: 'var(--hf-ink)',
            overflowWrap: 'anywhere',
          }}
        >
          {label}
        </div>
        {sub && <Mono dim>{sub}</Mono>}
      </div>
      {/* Decorative: the figure beside it is the value. Progress clips its fill
          rather than animating a width (the detector's layout-transition hit). */}
      <div style={{ flex: '2 1 6rem', minWidth: 0, ...at('1 / -1', 2) }}>
        <Progress value={pct} tone={color} size={6} />
      </div>
      <Mono style={{ minWidth: '3rem', textAlign: 'right', ...at('2', 1) }}>{valueLabel}</Mono>
    </div>
  );
}

// Org-wide rows the dashboard analytics consume.
type AttRow = { profile_id: string; date: string; status: string; hours: number };

type Point = { label: string; value: number | null };

// ── Trend (line + soft area) — for week-over-week series ──────────────────────
// A null is a week that was not measured, drawn as a GAP: the line breaks and
// picks up again. It used to take numbers only, so a week with no rows plotted
// as 0 and six weeks before the data began read as a collapse (HRA-14).
export function Trend({
  label,
  points,
  color = 'var(--hf-accent-2)',
  height = 54,
  suffix = '',
}: {
  /** What the series is, e.g. "Attendance rate, 8 weeks". Read with its figures. */
  label: string;
  points: Point[];
  color?: string;
  height?: number;
  suffix?: string;
}) {
  const known = points.filter((p): p is { label: string; value: number } => p.value != null);
  if (!known.length) return <div style={NOTE}>No data yet.</div>;
  // The viewBox is 100 units wide and the svg renders at 100% — around 560px in
  // this grid — and preserveAspectRatio="none" then scales x by ~5.6 while y
  // stays 1. Every slope was flattened by that factor: literally "stretched and
  // flattened", which is what this card was reported as. Drawing in a viewBox
  // whose width is close to the rendered width leaves the residual distortion at
  // a few percent instead.
  // ponytail: fixed 560 rather than measured. If a card ever renders far from
  // that width, measure it with a ResizeObserver instead of widening this number.
  const W = 560;
  const n = points.length;
  const vals = known.map((p) => p.value);
  const max = Math.max(1, ...vals);
  const min = Math.min(0, ...vals);
  const range = max - min || 1;
  // Inset, so a point on the first or last week is drawn whole rather than
  // clipped by the edge of the svg.
  const PAD = 6;
  const x = (i: number) => (n <= 1 ? W / 2 : PAD + (i / (n - 1)) * (W - 2 * PAD));
  const y = (v: number) => height - ((v - min) / range) * (height - 4) - 2;
  // Runs of consecutive measured weeks. Each is its own line and its own area.
  const runs: { i: number; v: number }[][] = [];
  points.forEach((p, i) => {
    if (p.value == null) return;
    const last = runs.at(-1);
    if (last && last.at(-1)!.i === i - 1) last.push({ i, v: p.value });
    else runs.push([{ i, v: p.value }]);
  });
  const path = (run: { i: number; v: number }[]) =>
    run.map((q, k) => `${k ? 'L' : 'M'}${x(q.i).toFixed(1)},${y(q.v).toFixed(1)}`).join(' ');
  const first = known[0];
  const last = known.at(-1)!;
  const gaps = n - known.length;
  const fig = (p: { label: string; value: number }) =>
    `${Math.round(p.value)}${suffix} in the week of ${p.label}`;
  return (
    <div>
      <svg
        role="img"
        aria-label={`${label}: ${fig(first)}${known.length > 1 ? `, ${fig(last)}` : ''}${
          gaps ? `. ${gaps} ${gaps === 1 ? 'week has' : 'weeks have'} no data` : ''
        }.`}
        width="100%"
        height={height}
        viewBox={`0 0 ${W} ${height}`}
        preserveAspectRatio="none"
      >
        {runs.map((run, k) =>
          run.length === 1 ? (
            // One measured week between two gaps has no line to draw.
            <circle key={k} cx={x(run[0].i)} cy={y(run[0].v)} r={3} fill={color} />
          ) : (
            <g key={k}>
              <path
                d={`${path(run)} L${x(run.at(-1)!.i).toFixed(1)},${height} L${x(run[0].i).toFixed(1)},${height} Z`}
                fill={color}
                opacity={0.12}
              />
              <path
                d={path(run)}
                fill="none"
                stroke={color}
                strokeWidth={1.6}
                vectorEffect="non-scaling-stroke"
              />
            </g>
          ),
        )}
      </svg>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          gap: 'var(--hf-space-2)',
          marginTop: 'var(--hf-space-1)',
        }}
      >
        <Mono dim>
          {points[0]?.label}
          {gaps > 0 && ` · ${gaps} ${gaps === 1 ? 'week' : 'weeks'} with no data`}
        </Mono>
        <Mono dim>
          {last.label} · {Math.round(last.value)}
          {suffix}
        </Mono>
      </div>
    </div>
  );
}

// ── Histogram (distribution) ──────────────────────────────────────────────────
function Histogram({
  label,
  bins,
  color = 'var(--hf-accent-2)',
}: {
  label: string;
  bins: { label: string; count: number }[];
  color?: string;
}) {
  const max = Math.max(1, ...bins.map((b) => b.count));
  return (
    <div
      role="img"
      aria-label={`${label}: ${bins.map((b) => `${b.label} ${b.count}`).join(', ')}.`}
      style={{ display: 'flex', alignItems: 'flex-end', gap: 'var(--hf-space-2)', height: 104 }}
    >
      {bins.map((b, i) => (
        <div
          key={i}
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 'var(--hf-space-1)',
            height: '100%',
          }}
        >
          <Mono>{b.count}</Mono>
          <div style={{ flex: 1, width: '100%', display: 'flex', alignItems: 'flex-end' }}>
            <div
              style={{
                width: '100%',
                height: `${(b.count / max) * 100}%`,
                minHeight: b.count ? 4 : 0,
                background: color,
                borderRadius: 'var(--hf-radius-sm) var(--hf-radius-sm) 0 0',
              }}
            />
          </div>
          <span style={{ fontSize: 'var(--hf-text-2xs)', color: 'var(--hf-ink-3)' }}>
            {b.label}
          </span>
        </div>
      ))}
    </div>
  );
}

// ── Calendar heatmap (weekday rows × week columns) ────────────────────────────
// The level was green opacity and nothing else, readable only from a hover
// title (HRA-15). It keeps the squares, and adds a scale that says which end is
// which and a table of the same figures for anyone not reading colour.
const HEAT_STEPS = [0, 0.25, 0.5, 0.75, 1];
const heatFill = (rate: number) =>
  `color-mix(in srgb, ${TONE.sage.fg} ${Math.round(14 + rate * 86)}%, transparent)`;

export function Heatmap({
  label,
  cells,
  weekStartsOn,
}: {
  label: string;
  /** `off`: a day nobody was expected, the org's weekend or a holiday. */
  cells: { date: string; rate: number | null; off?: boolean }[];
  weekStartsOn: number;
}) {
  const SZ = 15;
  const GAP = 3;
  // Rotated from the getDay()-indexed labels, so the row order always matches
  // the cell order above it. This column used to be a hardcoded M-T-W-T-F-S-S
  // while the org was configured Sun-first.
  const DOW = weekdayLabels(weekStartsOn);
  const weeks = Array.from({ length: Math.ceil(cells.length / 7) }, (_, w) =>
    cells.slice(w * 7, w * 7 + 7),
  );
  const pct = (r: number | null) => (r == null ? 'no data' : `${Math.round(r * 100)}%`);
  // A day nobody was expected is the day marks' hatch (UI-CONVENTIONS, Day
  // marks), not the "no data" outline: a Saturday is not a day whose records
  // went missing. A rate on one (somebody worked it) still shows as a rate.
  const isOff = (c: (typeof cells)[number]) => c.rate == null && !!c.off;
  const said = (c: (typeof cells)[number]) => (isOff(c) ? 'non-working day' : pct(c.rate));
  const anyOff = cells.some(isOff);
  return (
    <div>
      <div
        aria-hidden="true"
        style={{
          display: 'flex',
          gap: 'var(--hf-space-2)',
          alignItems: 'flex-start',
          flexWrap: 'wrap',
        }}
      >
        {/* weekday labels — rows follow org_settings.week_start */}
        <div style={{ display: 'grid', gridTemplateRows: `repeat(7, ${SZ}px)`, gap: GAP }}>
          {DOW.map((d, i) => (
            <span
              key={i}
              style={{
                fontSize: 'var(--hf-text-2xs)',
                lineHeight: `${SZ}px`,
                color: 'var(--hf-ink-3)',
                fontFamily: 'var(--hf-font-mono)',
              }}
            >
              {d}
            </span>
          ))}
        </div>
        {/* day cells — fixed squares, one column per week */}
        <div
          style={{
            display: 'grid',
            gridTemplateRows: `repeat(7, ${SZ}px)`,
            gridAutoFlow: 'column',
            gridAutoColumns: `${SZ}px`,
            gap: GAP,
          }}
        >
          {cells.map((c, i) => (
            <div
              key={i}
              data-state={isOff(c) ? 'off' : c.rate == null ? 'none' : 'rate'}
              title={`${fmtDayKey(c.date)} · ${isOff(c) ? said(c) : `${pct(c.rate)} present`}`}
              style={{
                borderRadius: 'var(--hf-radius-xs)',
                background: isOff(c)
                  ? dayMarkStyle('off').background
                  : c.rate == null
                    ? 'transparent'
                    : heatFill(c.rate),
                // A day with no data is a faint outline, not a pale fill that
                // reads as "almost nobody came". Faint on purpose: it is the
                // absence of a mark, and eight weeks of them must not shout.
                border: c.rate == null && !isOff(c) ? '1px solid var(--hf-rule-2)' : 0,
              }}
            />
          ))}
        </div>
      </div>
      <div
        aria-hidden="true"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 'var(--hf-space-1) var(--hf-space-4)',
          marginTop: 'var(--hf-space-3)',
          fontSize: 'var(--hf-text-2xs)',
          color: 'var(--hf-ink-3)',
        }}
      >
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--hf-space-1)' }}>
          0%
          {HEAT_STEPS.map((s) => (
            <span
              key={s}
              style={{
                width: SZ,
                height: SZ,
                borderRadius: 'var(--hf-radius-xs)',
                background: heatFill(s),
              }}
            />
          ))}
          100% present · rows {DOW[0]}→{DOW[6]}
        </span>
        {anyOff && <DayMark kind="off" />}
      </div>
      <table className="hf-sr-only">
        <caption>{label}</caption>
        <thead>
          <tr>
            <th scope="col">Week of</th>
            {DOW.map((d) => (
              <th key={d} scope="col">
                {d}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {weeks.map((wk) => (
            <tr key={wk[0].date}>
              <th scope="row">{fmtDayKey(wk[0].date)}</th>
              {wk.map((c) => (
                <td key={c.date}>{said(c)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Days the org still owes: allotted and not yet taken, across the whole team.
 *
 * Clamped per (person, type) rather than summed once at the end. Somebody who
 * has overdrawn is not a credit against a colleague's untaken days — the org
 * owes them nothing, not a negative number — and clamping late would let one
 * overdrawn person quietly cancel out another's balance.
 *
 * Deliberately NOT priced in rupees. Leave here is settled in time, not cash:
 * `policy.overtime` counts overtime as comp off, which never changes a leave
 * balance (20261022100000), and
 * `payroll_line_component.kind` is only base / shortfall_deduction /
 * prior_period_adjustment — there is no leave or overtime component anywhere in
 * payroll. A currency figure would be money this system never pays.
 */
export const leaveLiability = (
  balances: readonly Pick<LeaveBalance, 'allotted' | 'used'>[],
): number => balances.reduce((s, b) => s + Math.max(0, (b.allotted || 0) - (b.used || 0)), 0);

/**
 * Days taken against a PAID entitlement.
 *
 * An unpaid type has `allotted = 0` and still records `used` — the trigger writes
 * it for the record (16_leave_integrity:117) — so summing every row counted LOP
 * days as leave the org had granted. The leave tab and My Space were fixed for
 * this; the headline KPI was the caller that was missed, and it is the one on
 * screen the most, since the strip is pinned across all three HR sub-sections.
 */
export const paidLeaveUsed = (
  balances: readonly Pick<LeaveBalance, 'leave_type_id' | 'used'>[],
  types: readonly { id: string; paid: boolean }[],
): number => {
  const paid = new Set(types.filter((t) => t.paid).map((t) => t.id));
  return balances.reduce((s, b) => s + (paid.has(b.leave_type_id) ? b.used || 0 : 0), 0);
};

/**
 * The "Team capacity" rows, read off the SAME MonthSummary as the org-wide table
 * and each person's HR record — one denominator, the person's own contracted day
 * times the working days elapsed, with approved paid leave netted out.
 *
 * It used to divide a raw month-to-date time_entries sum by `orgPerDay × elapsed
 * workdays` while PRINTING `orgPerDay × the whole month` beside it: two
 * denominators, neither of them the person's own, neither net of leave, over
 * hours unfiltered by status and bucketed by browser-local `started_at` rather
 * than org-local `created_at`. It was filed as "attendance percentage is not
 * working properly", which it also was not — hence `att`, alongside.
 *
 * null is not 0. No summary row (this viewer cannot read that person's hours) or
 * nothing owed yet renders "—"; the confident zero is what got this reported.
 */
export const capacityRows = (
  people: readonly Pick<Profile, 'id' | 'full_name' | 'department_id' | 'status'>[],
  summaries: readonly Pick<MonthSummary, 'profileId' | 'effectiveHours' | 'requiredToDate'>[],
  attFor: (pid: string) => number | null,
  departments: readonly Department[],
) => {
  const byPid = new Map(summaries.map((s) => [s.profileId, s]));
  const round1 = (n: number) => Math.round(n * 10) / 10;
  return (
    people
      .map((p) => {
        const s = byPid.get(p.id);
        const base = {
          id: p.id,
          name: p.full_name || '—',
          dept: departmentName(departments, p.department_id) ?? '—',
          status: p.status,
          att: attFor(p.id),
        };
        // One predicate covers both "no row" and "nothing owed yet" (a month
        // entirely of leave, or the 1st before a workday has elapsed).
        return s && s.requiredToDate > 0
          ? {
              ...base,
              logged: round1(s.effectiveHours),
              required: round1(s.requiredToDate),
              // Uncapped on purpose — the BAR clamps at the render site (lib/contract:113).
              pct: completionPct(s.effectiveHours, s.requiredToDate),
            }
          : { ...base, logged: null, required: null, pct: null };
      })
      // Unknowns sink. `b.pct - a.pct` on a null is NaN, and an unstable sort.
      .sort((a, b) => (b.pct ?? -1) - (a.pct ?? -1))
  );
};

/**
 * Payroll's "Select all": every row on screen except the viewer's own.
 *
 * Not a permission check — the trigger (20260902120000) refuses crediting
 * yourself regardless of role, so offering that checkbox would just be a
 * click that always fails. Kept pure and exported so bulk-select can be
 * tested without mounting the Payroll view.
 */
export const selectableIds = (
  rows: readonly { id: string }[],
  uid: string | null | undefined,
): string[] => rows.filter((r) => r.id !== uid).map((r) => r.id);

/** Bulk credit's header bar and row tick boxes, as the org monthly table takes
 *  them: the screen builds one, and Payroll and the Dashboard both mount it. */
type BulkCredit = Pick<ComponentProps<typeof MonthlySummary>, 'selection' | 'rowLead'>;

// The attendance tile's change with no earlier figure: the stub, not "0 pts"
// (lib/attendance rateChange). A delta is plain text, and a bare dash read
// aloud is nothing at all, so the stub is the tile's note, with its words.
const NO_EARLIER = (
  <>
    <span aria-hidden="true">— vs previous 28 days</span>
    <span className="hf-sr-only">No figure for the previous 28 days to compare</span>
  </>
);

export function PeopleDashboard({
  people,
  compByPid,
  balances,
  leaveTypes,
  summaries,
  monthShape,
  attendance,
  costTrend,
  failed,
  onRetry,
  reqs,
  showComp,
  onOpenPerson,
  weekStartsOn,
  tz,
  orgHours,
  workdays,
  holidays,
  canSeeSummary,
  departments,
  bulkCredit,
  summaryNonce,
}: {
  people: Profile[];
  compByPid: Record<string, number>;
  balances: LeaveBalance[];
  leaveTypes: LeaveType[];
  /** The org's holidays, as date keys: with `workdays`, which heatmap days
   *  nobody was expected on. */
  holidays?: ReadonlySet<string>;
  /** The month's per-person hours — the same rows the org-wide table reads. */
  summaries: MonthSummary[];
  attendance: AttRow[];
  costTrend: CostMonth[];
  /** Reads that failed. Each card they feed says so, with Retry: never "no
   *  attendance recorded yet" or "no month has been priced" after a failure. */
  failed: ReadonlySet<ReadName>;
  onRetry: () => void;
  reqs: PendingRequest[];
  /** people:full. Gates every figure whose read is team-wide only at that level
   *  (leave, compensation, payroll): below it those reads return the
   *  viewer's own rows, and a self-read under a team label is a confident lie. */
  showComp: boolean;
  monthShape: { total: number; elapsed: number }; // workdays in the month, and elapsed
  onOpenPerson: (id: string) => void;
  weekStartsOn: number; // org_settings.week_start — anchors the series + heatmap
  tz: string;
  orgHours: OrgHours;
  workdays: Workdays | null;
  /** Whether this viewer can be shown the org-wide monthly table. */
  canSeeSummary: boolean;
  departments: readonly Department[];
  /** The screen's bulk-credit tick boxes for that table; read-only without. */
  bulkCredit?: BulkCredit;
  /** Bumped after a credit: remounts that table, which refetches. */
  summaryNonce?: number;
}) {
  const headcount = people.length || 1;
  // A phone gets the tiles compact, so the cards are a thumb away rather than
  // four 112px tiles down, and each capacity row its name on a line of its own.
  const phone = useBelow('md');
  const tile = phone ? 'sm' : 'md';
  // The monthly table's month, held above the summaryNonce remount below: a
  // credit refetches the table, and must not step the reader off the month
  // they credited (Payroll's table holds its month the same way, payOffset).
  const [summaryOffset, setSummaryOffset] = useState(0);

  // Headcount by department. Keyed by the resolved name: names are unique in the
  // list, and an id the list can't name joins "—" instead of a second "—" slice.
  const deptMap: Record<string, number> = {};
  people.forEach((p) => {
    const d = departmentName(departments, p.department_id) ?? '—';
    deptMap[d] = (deptMap[d] || 0) + 1;
  });
  const deptRows = Object.entries(deptMap).sort((a, b) => b[1] - a[1]);
  const maxDept = Math.max(1, ...deptRows.map((r) => r[1]));
  // Access & roles
  const roleMap: Record<string, number> = {};
  people.forEach((p) => {
    const r = p.role_id || 'member';
    roleMap[r] = (roleMap[r] || 0) + 1;
  });
  const roleRows = Object.entries(roleMap)
    .map(([id, count]) => ({ id, count, label: roleLabel(id) }))
    .sort((a, b) => b.count - a.count);

  // Leave utilisation by type (team-wide)
  // No rows at all after a failed read: a bar at 0 of 0 would claim nobody has
  // any leave.
  const leaveRows = (failed.has('teamBalances') ? [] : leaveTypes).map((t) => {
    const rows = balances.filter((b) => b.leave_type_id === t.id);
    const allot = rows.reduce((s, b) => s + (b.allotted || 0), 0);
    const used = rows.reduce((s, b) => s + (b.used || 0), 0);
    return { ...t, allot, used, pct: allot ? Math.round((used / allot) * 100) : 0 };
  });

  const liability = leaveLiability(balances);

  // Headline stats
  const avgTenure = people.length
    ? (people.reduce((s, p) => s + tenureYears(p.joined), 0) / people.length).toFixed(1)
    : '0.0';

  // ── Extra analytics (attendance / leave decisions; HR-only data) ──
  // attendance.date is stamped org-local by org_today(), so every window that
  // filters it has to be the org's day, not the browser's. This said exactly
  // that and then used `new Date()` three lines up for the 28-day window, the
  // 8-week series origin, the heatmap start and the future-cell test — one
  // correct value computed and then not used (docs/UI-CONVENTIONS.md §2).
  const today = orgToday(tz);
  const todayKey = iso(today);
  const monthFrom = `${todayKey.slice(0, 7)}-01`;

  // Tenure distribution (histogram)
  const tenureBuckets = [
    { label: '<1y', count: 0 },
    { label: '1–2y', count: 0 },
    { label: '2–3y', count: 0 },
    { label: '3–5y', count: 0 },
    { label: '5y+', count: 0 },
  ];
  people.forEach((p) => {
    const t = tenureYears(p.joined);
    tenureBuckets[t < 1 ? 0 : t < 2 ? 1 : t < 3 ? 2 : t < 5 ? 3 : 4].count += 1;
  });

  // Attendance rate: the last 28 days, today included, and the 28 before them
  // for the tile's change. `>=` here made the window 29 days under a "28 days"
  // label. The team read goes back 84 days, so the earlier window is whole.
  const since28 = iso(addDays(today, -28));
  const since56 = iso(addDays(today, -56));
  const recentAtt = attendance.filter((a) => a.date > since28);
  const earlierAtt = attendance.filter((a) => a.date > since56 && a.date <= since28);
  // A day counts fractionally by hours worked vs the standard day (a 3h day is
  // not a 100% present day) — Testing_230726 N3.
  //
  // Against the PERSON's contracted day, not the org's. This used to divide
  // every row by the org standard, so anyone on a non-default contract was
  // scored against someone else's day: a 6h-contracted employee who worked a
  // full 6h showed 75%, and a 10h contract showed 100% after 8h. Every other
  // hours-vs-target figure in this screen already resolves per person through
  // personDay() (the HR card, the week target) — this was the one that did not.
  const dayByPid = new Map(people.map((p) => [p.id, personDay(p.contract_hrs_per_day, orgHours)]));
  const dayFor = (pid: string) => dayByPid.get(pid) ?? orgHours.perDay;
  const presFrac = (a: AttRow) => presentFraction(a, dayFor(a.profile_id));

  // Capacity — effective hours against hours owed to date, per person. Both the
  // fraction and the percentage now come off one MonthSummary, so the two figures
  // printed side by side finally share a denominator.
  //
  // Gated: time_entries opens at timesheets:full and leave_requests at people:full,
  // and PostgREST answers a forbidden read with an empty SET rather than a 403 — so
  // below the gate every row would have been a confident zero. Hand it nothing and
  // the rows render "—" instead (lib/monthlySummary's canSeeOrgSummary).
  //
  // Attendance rides along because the card was reported as an attendance figure:
  // days present ÷ days scheduled, over the month the card is titled for, against
  // the person's own contracted day. With no rows for a person this is null,
  // not a zero — a confident 0% attendance is a claim, and an absent one is not.

  const monthAtt = new Map<string, AttRow[]>();
  for (const a of attendance) {
    if (a.date < monthFrom || a.date > todayKey) continue;
    const rows = monthAtt.get(a.profile_id);
    if (rows) rows.push(a);
    else monthAtt.set(a.profile_id, [a]);
  }
  const attFor = (pid: string) => {
    const rows = monthAtt.get(pid);
    return rows?.length ? attendanceRate(rows, dayFor) : null;
  };
  const capRows = capacityRows(people, canSeeSummary ? summaries : [], attFor, departments);
  // Without the hours the row is a name and one figure, which fits a phone's
  // width; with them the name takes a line of its own there.
  const capGrid: CSSProperties = canSeeSummary
    ? CAP_GRID
    : { ...CAP_GRID, gridTemplateColumns: 'minmax(0, 1fr) auto' };
  const stackName = phone && canSeeSummary;
  const scored = capRows.map((r) => r.pct).filter((v): v is number => v != null);
  const avgUtil = scored.length
    ? Math.round(scored.reduce((a, b) => a + b, 0) / scored.length)
    : null;
  // Both rules — fractional against the person's own day, and leave/holiday out
  // of the denominator rather than scoring 0 in it — now live in lib/attendance.
  // (`absent` and `leave` are structurally 0 today: nothing writes those
  // statuses, so the leave rule is about the shape being right, not the current
  // number. The per-person divisor is live for anyone with a contract set.)
  // null when nobody was scheduled in the window: not measured is not 0%.
  const attendanceRatePct = attendanceRateOrNull(recentAtt, dayFor);
  // The same function over the same rows, so the change can't disagree with
  // the figure. No earlier figure is the stub, not "0 pts" (lib/attendance).
  const attChange = rateChange(
    attendanceRatePct,
    attendanceRateOrNull(earlierAtt, dayFor),
    'previous 28 days',
  );

  // Weekly attendance rate, last 8 weeks. A week with no rows is null — a gap
  // in the line, not a zero.
  const WEEKS = 8;
  const weekRate: Point[] = [];
  for (let w = WEEKS - 1; w >= 0; w--) {
    const ws = weekStart(weekStartsOn, addDays(today, -7 * w));
    const wk = attendance.filter((a) => a.date >= iso(ws) && a.date < iso(addDays(ws, 7)));
    const label = fmtDayKey(iso(ws));
    // Same function as the headline tile — that is the point. These used to be
    // two hand-rolled expressions with different denominators.
    weekRate.push({ label, value: attendanceRateOrNull(wk, dayFor) });
  }

  // Attendance heatmap (8 weeks, aligned to org_settings.week_start, present-rate/day)
  const heatStart = weekStart(weekStartsOn, addDays(today, -7 * (WEEKS - 1)));
  const byDate: Record<string, AttRow[]> = {};
  attendance.forEach((a) => (byDate[a.date] = byDate[a.date] || []).push(a));
  const heatCells: { date: string; rate: number | null; off: boolean }[] = [];
  for (let i = 0; i < WEEKS * 7; i++) {
    const d = addDays(heatStart, i);
    const rows = byDate[iso(d)] || [];
    const pres = rows.reduce((s, a) => s + presFrac(a), 0);
    heatCells.push({
      date: iso(d),
      rate: d > today || !rows.length ? null : pres / headcount,
      // The org's week and holidays decide it, as everywhere (UI-CONVENTIONS §2).
      off: !isWorkday(iso(d), workdays, holidays),
    });
  }

  // Approval turnaround (decided requests) + pending count
  // Only count requests decided on/after they were created (guards against demo
  // rows whose decided_at predates created_at → no spurious negative SLA).
  const decided = reqs.filter(
    (r) =>
      r.status !== 'pending' &&
      r.decided_at &&
      r.created_at &&
      new Date(r.decided_at).getTime() >= new Date(r.created_at).getTime(),
  );
  const turnaround = decided.length
    ? Math.round(
        (decided.reduce(
          (s, r) =>
            s +
            (new Date(r.decided_at as string).getTime() -
              new Date(r.created_at as string).getTime()),
          0,
        ) /
          decided.length /
          86400000) *
          10,
      ) / 10
    : null;

  // Payroll efficiency (cost / worked hour this month)
  // Worked, not effective: this is payroll ÷ hours actually clocked. Same rows as
  // the capacity card, so the two KPIs cannot disagree about the month.
  const monthHrsTotal = Math.round(summaries.reduce((s, r) => s + r.workedHours, 0));
  const payrollMonthly = people.reduce((s, p) => s + Math.round((compByPid[p.id] || 0) / 12), 0);
  // payrollMonthly is a WHOLE month; monthHrsTotal is month-to-date. Dividing one
  // by the other read ~₹2,50,000/hr on the 1st and decayed all month, correct only
  // by coincidence on the last day. Prorate the payroll to the elapsed workdays so
  // both sides cover the same window.
  const payrollToDate = monthShape.total
    ? (payrollMonthly * monthShape.elapsed) / monthShape.total
    : payrollMonthly;
  const costPerHour = monthHrsTotal ? Math.round(payrollToDate / monthHrsTotal) : 0;

  // `reqs` is org-wide only at people:full, which is the gate on the tile.
  // profiles.status is an EMPLOYMENT status that nothing sets to 'leave', so
  // counting it read 0 every day; the approved requests are the source.
  // Who, not just how many: "0" said nothing a reader could act on (critique
  // 2026-10-06 #1). One name per person, however many requests cover today.
  const onLeave = new Map(
    reqs
      .filter((r) => r.status === 'approved' && r.start_date <= todayKey && todayKey <= r.end_date)
      .map((r) => [r.profile_id, r.full_name?.trim() || 'Unnamed']),
  );
  const onLeaveToday = onLeave.size;
  const reqsFailed = failed.has('requests');
  const names = [...onLeave.values()];
  const onLeaveNames = !names.length
    ? 'Nobody'
    : names.length > 3
      ? `${names.slice(0, 2).join(', ')} and ${names.length - 2} more`
      : names.length > 1
        ? `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`
        : names[0];
  // The same rows, for the "On leave today" table (owner, 2026-10-09).
  const awayToday = reqs
    .filter((r) => r.status === 'approved' && r.start_date <= todayKey && todayKey <= r.end_date)
    .sort((a, b) => (a.full_name ?? '').localeCompare(b.full_name ?? ''));
  const paidUsed = paidLeaveUsed(balances, leaveTypes);

  const caption: CSSProperties = { fontSize: 'var(--hf-text-xs)', color: 'var(--hf-ink-3)' };
  const attFailed = failed.has('teamAtt');
  /** An attendance card's body: the failure, the chart, or the honest empty state. */
  const att = (chart: React.ReactNode, has = attendance.length > 0) =>
    attFailed ? (
      <LoadError what="the team’s attendance" onRetry={onRetry} />
    ) : has ? (
      chart
    ) : (
      <NoAttendance />
    );
  const footnote: CSSProperties = {
    ...caption,
    marginTop: 'var(--hf-space-3)',
    paddingTop: 'var(--hf-space-3)',
    borderTop: '1px solid var(--hf-rule)',
  };

  return (
    <div
      className="hf-scroll"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--hf-space-4)',
        height: '100%',
        overflowY: 'auto',
        paddingRight: 2,
        paddingBottom: 'var(--hf-space-1)',
      }}
    >
      {/* ── One row of at most four tiles, here and nowhere else (UI-CONVENTIONS
          §8). The strip used to be pinned over Requests, Payroll and every
          person's record, above seven more tiles on this view: eleven figures of
          equal weight before any content (HRA-13, HRP-12). The seven are now
          captions on the cards they describe.

          No tones: a tile is a measurement, and 2% utilisation in green or a
          zero in rose was colour editorialising (HRA-20).

          The leave and cost tiles are team figures only at people:full — lr_read,
          lb_read and the compensation read are self-or-people:full — so below it
          there are fewer tiles, never a substitute. ── */}
      <KpiGrid>
        {/* No ring: headcount has no denominator, so value/total is always 100%. */}
        <KpiTile label="Headcount" value={String(people.length)} size={tile} />
        {/* After a failed read "Nobody" would be a claim about the team. */}
        {showComp && (
          <KpiTile
            label="On leave today"
            value={reqsFailed ? null : String(onLeaveToday)}
            note={reqsFailed ? 'Couldn’t load' : onLeaveNames}
            size={tile}
          />
        )}
        <KpiTile
          label="Attendance · 28 days"
          value={attFailed || attendanceRatePct == null ? null : String(attendanceRatePct)}
          unit="%"
          delta={attFailed ? null : attChange?.delta}
          trend={attChange?.trend}
          note={
            attFailed ? 'Couldn’t load' : attChange && !attChange.delta ? NO_EARLIER : undefined
          }
          size={tile}
        />
        {/* CTC ÷ 12 — what is committed, not what payroll computed. "Monthly
            payroll" put the same figure under a second name beside the register,
            which prices it differently; see SalaryByDepartment. "Contracted
            cost / mo" wrapped onto two lines at 390 (critique 2026-10-06 #1). */}
        {showComp && (
          <KpiTile
            label="Cost / month"
            value={failed.has('teamComp') ? null : fmtINR(payrollMonthly)}
            note={failed.has('teamComp') ? 'Couldn’t load' : undefined}
            size={tile}
          />
        )}
      </KpiGrid>

      {/* The heading names both halves of the row: Access & roles and Tenure
          are the team's composition, not its capacity (critique 2026-10-05 #3). */}
      <SectionHeader title="Capacity & composition" />

      {/* ── 1 · Capacity & composition ── */}
      <div style={{ ...fold('22rem'), gap: 'var(--hf-space-4)' }}>
        {/* Below the summary gate the hours never arrive, so a lead's card was
            a column of dashes beside a column of empty bars: not measured,
            drawn as 0 (critique 2026-10-05, minor). There it is the attendance
            it can show, and says so in its name. */}
        <PCard
          level={3}
          label={
            canSeeSummary ? 'Team capacity · hours this month' : 'Team attendance · this month'
          }
          right={avgUtil != null && <span style={caption}>avg {avgUtil}% complete</span>}
        >
          {/* One grid for the column heads and every row, so the three figures
              line up under their names at any card width; the bar runs under
              them. As a wrapping flex row the attendance figure dropped to a line
              of its own in a narrow card. */}
          <div style={{ ...caption, ...capGrid, padding: '0 0 var(--hf-space-1)' }}>
            {!stackName && <span />}
            {canSeeSummary && (
              <>
                <span style={{ ...CAP_NUM, gridColumn: stackName ? 2 : undefined }}>
                  hours / owed
                </span>
                <span style={{ ...CAP_NUM, minWidth: '3rem' }}>done</span>
              </>
            )}
            <span style={{ ...CAP_NUM, minWidth: '3.5rem' }}>present</span>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {capRows.map((r) => (
              // A real button, with the ghost button's hover from tokens.css —
              // it repainted itself from onMouseEnter.
              <button
                key={r.id}
                type="button"
                className="hf-btn"
                data-variant="ghost"
                onClick={() => onOpenPerson(r.id)}
                style={{
                  ...capGrid,
                  padding: 'var(--hf-space-2)',
                  margin: '0 calc(-1 * var(--hf-space-2))',
                  borderRadius: 'var(--hf-radius-md)',
                  textAlign: 'left',
                  fontFamily: 'inherit',
                }}
              >
                {/* On a phone the three figures leave the name no width at all
                    (at 320 it was gone and the department ran under the
                    hours), so the name takes the row and the figures sit under
                    their heads on the next. */}
                <span
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 'var(--hf-space-2)',
                    minWidth: 0,
                    gridColumn: stackName ? '1 / -1' : undefined,
                  }}
                >
                  <Avatar name={r.name} size={26} />
                  <span style={{ minWidth: 0 }}>
                    <span
                      style={{
                        display: 'block',
                        fontSize: 'var(--hf-text-sm)',
                        color: 'var(--hf-ink)',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {r.name}
                    </span>
                    {/* Clipped like the name: unclipped, it ran under the hours. */}
                    <span
                      style={{
                        ...caption,
                        display: 'block',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                      }}
                    >
                      {r.dept}
                    </span>
                  </span>
                </span>
                {/* null is not 0: no summary row, or nothing owed yet, is "—". */}
                {canSeeSummary && (
                  <>
                    <Mono dim style={{ ...CAP_NUM, gridColumn: stackName ? 2 : undefined }}>
                      {r.logged == null ? '—' : `${r.logged} / ${r.required}h`}
                    </Mono>
                    <Mono style={{ ...CAP_NUM, minWidth: '3rem' }}>
                      {r.pct == null ? '—' : `${r.pct}%`}
                    </Mono>
                  </>
                )}
                {/* Attendance, the figure this card was reported for. Secondary:
                    utilisation is what the row is sorted by. */}
                <Mono dim style={{ ...CAP_NUM, minWidth: '3.5rem' }}>
                  {r.att == null ? '—' : `${r.att}%`}
                </Mono>
                {/* A bar for a measured figure only: an empty one read as 0%. */}
                {r.pct != null && (
                  <span style={{ gridColumn: '1 / -1' }}>
                    <Progress value={r.pct} size={4} />
                  </span>
                )}
              </button>
            ))}
            {!capRows.length && <div style={NOTE}>No one in the roster yet.</div>}
          </div>
        </PCard>

        {/* The row's right column is a STACK, not one card. Team capacity is one
            row per person, so at any real headcount it towers over its
            neighbours. Three short ungated cards fill the column. */}
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--hf-space-4)',
            minWidth: 0,
          }}
        >
          {/* Rows with bars, not a donut: seven people in near-equal slices said
              less than the list beside it already did (HRA-13). No "7 people"
              in its header: the Headcount tile above already says it. */}
          <PCard level={3} label="Headcount by department">
            {deptRows.map(([dept, count]) => (
              <HBar
                key={dept}
                label={dept}
                value={count}
                max={maxDept}
                valueLabel={`${count} · ${Math.round((count / headcount) * 100)}%`}
              />
            ))}
          </PCard>

          {/* A list. As bars scaled to the largest role, one person per role
              drew seven full bars that each said "1". */}
          <PCard
            level={3}
            label="Access & roles"
            right={<span style={caption}>{roleRows.length} roles in use</span>}
          >
            {roleRows.map((r) => (
              <div
                key={r.id}
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 'var(--hf-space-3)',
                  padding: 'var(--hf-space-1) 0',
                  fontSize: 'var(--hf-text-sm)',
                  color: 'var(--hf-ink-2)',
                }}
              >
                <span>{r.label}</span>
                <Mono>{r.count}</Mono>
              </div>
            ))}
          </PCard>

          <PCard
            level={3}
            label="Tenure distribution"
            right={<span style={caption}>avg {avgTenure} yrs</span>}
          >
            <Histogram label="People by tenure" bins={tenureBuckets} />
          </PCard>
        </div>
      </div>

      <SectionHeader title="Attendance" />

      {/* ── 2 · Attendance ──
          No longer behind showComp. attendance opens at people:view (att_read,
          20260604010000_modules.sql) — the same level that opens this screen — so
          gating it on people:full was a client-side invention that showed a lead
          two tiles and three cards where everyone else saw a dashboard. The
          layout is now identical for every viewer; only values differ.

          Two charts, each answering a question someone acts on: is attendance
          moving, and on which days is the team thin. Six cards on attendance
          for a seven-person team (critique 2026-10-05 #3) also drew the 28-day
          status mix, which nothing writes past present and WFH; worked hours,
          which move with the rate; the spread, which Team capacity's present
          column says per person; and overtime, which the monthly summary and
          each person's Hours tab carry. */}
      <div style={{ ...fold('20rem'), gap: 'var(--hf-space-4)' }}>
        <PCard level={3} label="Attendance rate · 8 weeks">
          {att(
            <Trend
              label="Attendance rate, 8 weeks"
              points={weekRate}
              color={TONE.sage.fg}
              suffix="%"
              // As tall as the heatmap beside it, which also gives the line
              // the room to show a change. Stacked on a phone, it stays short.
              height={phone ? undefined : 124}
            />,
          )}
        </PCard>

        <PCard level={3} label="Attendance heatmap · 8 weeks">
          {att(
            <Heatmap
              label="Share of the team present, by day, over 8 weeks"
              cells={heatCells}
              weekStartsOn={weekStartsOn}
            />,
          )}
        </PCard>
      </div>

      {/* Who is working right now. No permission gate of its own: on_the_clock()
          filters to talent:view / people:view in the function body, so a viewer
          who should not see presence gets an empty card rather than a hidden
          one. Placed here on the People module as requested — note that with
          the default matrix `member` holds people:none and cannot reach this
          screen at all, so colleagues will not see it until either this is also
          mounted on Talent (which member CAN reach) or people:view is granted. */}
      {/* Who is working and who is away, side by side (owner, 2026-10-09:
          "Who is on leave today displayed besides On the clock"). The leave
          half is people:full, as the requests behind it are org-wide only there. */}
      {showComp ? (
        <div style={{ ...fold('22rem'), gap: 'var(--hf-space-4)', alignItems: 'start' }}>
          <OnTheClockCard />
          <PCard level={3} label="Who is on leave today">
            {reqsFailed ? (
              <LoadError what="today’s leave" onRetry={onRetry} />
            ) : awayToday.length === 0 ? (
              <div style={caption}>Nobody is on leave today.</div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {awayToday.map((r) => (
                  <button
                    key={r.id}
                    type="button"
                    className="hf-hit"
                    onClick={() => onOpenPerson(r.profile_id)}
                    style={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      alignItems: 'center',
                      gap: 'var(--hf-space-2) var(--hf-space-3)',
                      padding: 'var(--hf-space-2) 0',
                      background: 'transparent',
                      border: 0,
                      borderBlockStart: '1px solid var(--hf-rule)',
                      textAlign: 'left',
                      font: 'inherit',
                      color: 'inherit',
                      cursor: 'pointer',
                    }}
                  >
                    <Avatar name={r.full_name ?? undefined} size={24} />
                    <span style={{ flex: '1 1 10rem', fontSize: 'var(--hf-text-sm)' }}>
                      {r.full_name ?? 'Someone'}
                    </span>
                    <Badge tone="neutral">{r.leave_label ?? r.leave_type_id}</Badge>
                    <Mono dim>
                      {r.end_date === todayKey
                        ? 'last day today'
                        : `until ${fmtDayKey(r.end_date)}`}
                    </Mono>
                  </button>
                ))}
              </div>
            )}
          </PCard>
        </div>
      ) : (
        <OnTheClockCard />
      )}

      {/* -- Leave & payroll --
          Both cards read people:full tables. lb_read is
          `profile_id = auth.uid() or has_perm('people','full')`, a SELF-read below
          full, and pl_read is people:full or your own month once it closes. So the
          whole SECTION is gated rather than each card: below the gate it does not
          render at all. */}
      {showComp && (
        <>
          <SectionHeader title="Leave & payroll" />
          <div style={{ ...fold('22rem'), gap: 'var(--hf-space-4)' }}>
            <PCard
              level={3}
              label="Leave utilisation · team"
              right={
                turnaround != null && (
                  <span style={caption}>decided in {turnaround} d on average</span>
                )
              }
            >
              {failed.has('teamBalances') && (
                <LoadError what="the team’s leave balances" onRetry={onRetry} />
              )}
              {leaveRows.map((t) => (
                <HBar
                  key={t.id}
                  label={t.label}
                  // An unpaid type has no entitlement, so used/allotted is not
                  // a ratio. `allot` is always 0 for one, and `max={t.allot || 1}`
                  // then clamped HBar to a FULL bar reading "3/0 days" and "0%".
                  // Its days are real and stay on screen; the percentage does not.
                  sub={t.paid ? `${t.used}/${t.allot} days` : `${t.used} days · no entitlement`}
                  value={t.paid ? t.used : 0}
                  max={t.paid ? t.allot || 1 : 1}
                  color={t.color}
                  valueLabel={t.paid ? `${t.pct}%` : 'unpaid'}
                />
              ))}
              {/* Days, not rupees: leave here is settled in time (leaveLiability). */}
              <div style={footnote} hidden={failed.has('teamBalances')}>
                {paidUsed} paid days taken · {Math.round(liability * 10) / 10} days allotted and not
                yet taken
              </div>
            </PCard>

            {/* Rows, not bars. A bar from zero is the wrong encoding here: monthly
                payroll barely moves, so five bars scaled to the largest month all
                render within a few percent of full width and say nothing at all.
                The month-on-month delta is the signal, and money wants exact
                figures anyway - the register's own note is that a rounded figure
                "is not a number anybody can reconcile against a bank transfer".

                A month with no payroll_line rows is ABSENT from the array rather
                than zero: payroll_run writes rows only when somebody executes it,
                so the series is sparse by construction and a zero row would assert
                something the table never said. */}
            <PCard level={3} label="Payroll · last 6 months">
              <div style={{ ...caption, marginBottom: 'var(--hf-space-2)' }}>
                {PRE_STATUTORY_NOTE}
              </div>
              {failed.has('costTrend') ? (
                <LoadError what="the payroll trend" onRetry={onRetry} />
              ) : costTrend.length ? (
                <div style={{ display: 'flex', flexDirection: 'column' }}>
                  {costTrend.map((m, i) => {
                    const prev = costTrend[i - 1];
                    const delta = prev ? m.payable - prev.payable : null;
                    const notes = [
                      `${m.people} priced`,
                      m.deduction ? `${fmtINRFull(m.deduction)} deducted` : null,
                      m.shortfallHours ? `${hrs(m.shortfallHours)}h short` : null,
                      // The payroll migration says a figure built on auto-closed
                      // days is a guess. The count is how a reader knows whether
                      // to trust the money beside it.
                      m.autoClosedDays ? `${m.autoClosedDays} auto-closed` : null,
                    ]
                      .filter(Boolean)
                      .join(' · ');
                    return (
                      <div
                        key={`${m.year}-${m.month}`}
                        style={{
                          display: 'flex',
                          flexWrap: 'wrap',
                          alignItems: 'baseline',
                          gap: 'var(--hf-space-1) var(--hf-space-3)',
                          padding: 'var(--hf-space-2) 0',
                          borderBottom:
                            i < costTrend.length - 1 ? '1px solid var(--hf-rule)' : undefined,
                        }}
                      >
                        <div style={{ flex: '1 1 10rem', minWidth: 0 }}>
                          <div style={{ fontSize: 'var(--hf-text-sm)', color: 'var(--hf-ink)' }}>
                            {monthLabel(m.year, m.month)}
                          </div>
                          <Mono dim>{notes}</Mono>
                        </div>
                        <Mono style={{ color: 'var(--hf-ink)' }}>{fmtINRFull(m.payable)}</Mono>
                        {/* Neutral on purpose. Payroll going up is not a problem to
                            flag - it is a hire or a raise - so the sign carries the
                            meaning and the colour does not editorialise. */}
                        <Mono dim style={{ minWidth: '5.5rem', textAlign: 'right' }}>
                          {delta == null ? '' : `${delta > 0 ? '+' : ''}${fmtINRFull(delta)}`}
                        </Mono>
                      </div>
                    );
                  })}
                </div>
              ) : (
                <div style={NOTE}>
                  No month has been priced yet. A month appears here once payroll is run for it.
                </div>
              )}
              {monthHrsTotal > 0 && (
                <div style={footnote}>
                  {fmtINRFull(costPerHour)} of contracted cost per hour worked so far this month
                </div>
              )}
            </PCard>
          </div>
        </>
      )}

      {/* ── The org-wide monthly summary, and the one-click export ──
          Gated on people:full AND timesheets:full. Not cosmetic: leave_requests
          opens at people:full while time_entries opens at timesheets:full, so a
          viewer holding only one of them would get a table that is silently and
          confidently wrong rather than an error. Nothing is better than that. */}
      {canSeeSummary && (
        // ONE key for what refreshes after a credit, on a Fragment, the way
        // Payroll's group does it (see the comment there). Keyed here, not on
        // the dashboard's root: that is the scroller, and remounting it would
        // throw the reader back to the top, away from the table they credited.
        // Its month comes back in through initialOffset, from above the key.
        <Fragment key={summaryNonce}>
          <MonthlySummary
            initialOffset={summaryOffset}
            onOffset={setSummaryOffset}
            tz={tz}
            org={orgHours}
            workdays={workdays}
            exportable
            onOpenPerson={onOpenPerson}
            {...bulkCredit}
          />
        </Fragment>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// REQUESTS & APPROVALS — the decision queue first, then what else needs a look
// ─────────────────────────────────────────────────────────────────────────────
// Approve / Decline, approved / declined (UI-CONVENTIONS Vocabulary). The chip
// said "Denied", the button "Deny" and the employee's own badge "declined".
const REQ_FILTERS = [
  { value: 'pending', label: 'Pending' },
  { value: 'approved', label: 'Approved' },
  { value: 'rejected', label: 'Denied' },
  { value: 'all', label: 'All' },
] as const;
type ReqFilter = (typeof REQ_FILTERS)[number]['value'];

/** A day or a range of days, "27 Sep" or "27 Sep – 29 Sep". fmtDayKey parses the
 *  key at local midnight: `new Date('2026-06-18')` is UTC, and showed every
 *  request a day early to an approver west of GMT (HRA-V2). */
const dayRange = (from: string, to: string): string =>
  from === to ? fmtDayKey(from) : `${fmtDayKey(from)} – ${fmtDayKey(to)}`;
const days = (n: number) => `${n} ${n === 1 ? 'day' : 'days'}`;

/** What a leave request is, for a sentence: "Asha Rao’s casual leave". */
const leaveOf = (r: Pick<PendingRequest, 'full_name' | 'leave_label'>) =>
  `${r.full_name || 'Someone'}’s ${(r.leave_label || 'leave').toLowerCase()}${r.leave_label && !/leave/i.test(r.leave_label) ? ' leave' : ''}`;

type DecideOpts = { reason?: string; quiet?: boolean };

export function RequestsApprovals({
  reqs,
  reqsFailed,
  onRetryReqs,
  leaveTypes,
  canDecide,
  onDecide,
  onApproveAll,
  corrections,
  compQueue,
  compCats,
  dueDates,
  exceptions,
  exceptionsPeriod,
  onDatesChanged,
  handbooks,
  rolesList,
  handbookBehind,
  onHandbooksChanged,
  failed,
  onRetryExceptions,
  uid,
  onCorrectionsChanged,
  onCompChanged,
  dayEdits = [],
  onDayEditsChanged = () => {},
}: {
  reqs: PendingRequest[];
  /** The leave read failed: say so, never an empty queue. */
  reqsFailed?: boolean;
  onRetryReqs?: () => void;
  leaveTypes: LeaveType[];
  canDecide: boolean;
  onDecide: (id: string, status: 'approved' | 'rejected' | 'pending', opts?: DecideOpts) => void;
  /** Approves these leave requests, and reports once for all of them. */
  onApproveAll?: (ids: string[]) => void;
  /** null when the read failed — rendered as an error, never as an empty queue. */
  corrections: PendingCorrection[] | null;
  compQueue: PendingComp[];
  compCats: CompCategory[];
  dueDates: DueDate[];
  exceptions: HrException[];
  exceptionsPeriod?: React.ComponentProps<typeof ExceptionsCard>['period'];
  onDatesChanged: () => void;
  handbooks: RoleHandbook[];
  rolesList: { id: string; label: string }[];
  handbookBehind: Record<string, number>;
  onHandbooksChanged: () => void;
  /** Reads that failed: each renders as an error with Retry, never as an empty card. */
  failed: ReadonlySet<ReadName>;
  onRetryExceptions: () => void;
  uid: string;
  onCorrectionsChanged: () => void;
  onCompChanged: () => void;
  /** Waiting whole-day changes (Edit day, 20261103120000). Only waiting ones
   *  are read: a decided one lives on the person's own day. */
  dayEdits?: PendingDayEdit[];
  onDayEditsChanged?: () => void;
}) {
  const [filter, setFilter] = useState<ReqFilter>('pending');
  const [approvingAll, setApprovingAll] = useState(false);
  const [reverting, setReverting] = useState<PendingRequest | null>(null);
  const headingId = useId();
  const typeColor: Record<string, string> = {};
  leaveTypes.forEach((t) => (typeColor[t.id] = t.color));

  // Leave AND time corrections. The chips used to count leave only, so a
  // correction that had been approved was not merely hard to find — it was
  // nowhere in the product at all, because the listPending() this replaced drops
  // a row the moment it is decided. Both kinds share the same three statuses, so one filter
  // governs both lists.
  // A failed corrections read adds 0 to the chips, so they then count leave only.
  // That is honest only because the banner below the chips says the corrections
  // could not be loaded.
  const corrRows = corrections ?? [];
  const corrFailed = corrections === null;
  const escalations = useEscalations(canDecide, reqs, corrections);
  // One queue (owner, 2026-10-09: "no need of separate approval queues"):
  // leave, time corrections and compensated hours under the same filters.
  const nOf = (st: ReqFilter) =>
    (st === 'all' ? reqs.length : reqs.filter((r) => r.status === st).length) +
    (st === 'all' ? corrRows.length : corrRows.filter((c) => c.status === st).length) +
    (st === 'all' ? compQueue.length : compQueue.filter((c) => c.status === st).length) +
    (st === 'all' || st === 'pending' ? dayEdits.length : 0);
  const shownDayEdits = filter === 'all' || filter === 'pending' ? dayEdits : [];
  const shownCorrections =
    filter === 'all' ? corrRows : corrRows.filter((c) => c.status === filter);
  const shownComp = filter === 'all' ? compQueue : compQueue.filter((c) => c.status === filter);
  const list = (filter === 'all' ? reqs : reqs.filter((r) => r.status === filter))
    .slice()
    .sort((a, b) => (b.created_at || b.start_date).localeCompare(a.created_at || a.start_date));

  // What is waiting in the OTHER cards on this tab. The leave list's empty state
  // needs it: this tab owns four queues and deliberately shows one badge for all
  // of them (docs/UI-CONVENTIONS.md §1), so an empty leave list is not evidence
  // that the tab is clear.
  // Time corrections used to be counted here, back when they had their own card
  // above. They are in THIS list now, under the same chips, so naming them as
  // waiting "elsewhere" would send somebody looking for a card that is gone.

  // Leave only. "Approve all" used to appear whenever ANYTHING was pending, time
  // corrections included, and then walked the leave list alone: with only
  // corrections waiting it was a button that did nothing (HRA-02).
  // Not your own: nobody decides their own leave (20261103100000), so the
  // server refuses it and one refusal would stop the whole batch.
  const pendingLeave = reqs.filter((r) => r.status === 'pending' && r.profile_id !== uid);
  const unpaidPending = pendingLeave.filter((r) => !r.leave_paid).length;
  // Waiting leave, by type: a facet of the list below, dimmed — never a second
  // total beside the tab's badge (docs/UI-CONVENTIONS.md §1).
  const byType = leaveTypes
    .map((t) => ({ ...t, count: pendingLeave.filter((r) => r.leave_type_id === t.id).length }))
    .filter((x) => x.count > 0);

  return (
    // The scroller for this view. The screen root is height:100%, so the app
    // shell's scroller never engages on /hr and a descendant has to own it — as
    // PeopleDashboard's root already does. One column: a 296px side
    // panel took the whole width on a phone and left the queue none (HRA-07).
    <div
      className="hf-scroll"
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--hf-space-3)',
        flex: 1,
        minHeight: 0,
        overflowY: 'auto',
        paddingRight: 2,
      }}
    >
      {/* The queue leads: the view exists to decide requests, and it sat below a
          handbook-issuing form and three other cards (HRA-17). */}
      <section
        className="hf-glass"
        aria-labelledby={headingId}
        style={{ borderRadius: 'var(--hf-radius-card)', flexShrink: 0, minWidth: 0 }}
      >
        <div
          style={{
            padding: 'var(--hf-space-3) var(--hf-space-4)',
            borderBottom: '1px solid var(--hf-rule)',
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: 'var(--hf-space-2) var(--hf-space-3)',
          }}
        >
          <Eyebrow as="h2" id={headingId} style={{ marginRight: 'auto' }}>
            Approval queue
          </Eyebrow>
          <SegmentedControl
            label="Request status"
            options={REQ_FILTERS.map((f) => ({ ...f, count: nOf(f.value) }))}
            value={filter}
            onChange={setFilter}
          />
          {canDecide && pendingLeave.length > 1 && (
            <Button
              variant="secondary"
              size="sm"
              icon="check"
              className="hf-hit"
              onClick={() => setApprovingAll(true)}
            >
              Approve all leave ({pendingLeave.length})
            </Button>
          )}
        </div>
        {/* Kept from the claims card this queue absorbed: why HR may be
            deciding someone else's manager's call. */}
        {canDecide && compQueue.some((c) => c.status === 'pending') && (
          <div
            style={{
              padding: 'var(--hf-space-2) var(--hf-space-4)',
              borderBottom: '1px solid var(--hf-rule)',
              fontSize: 'var(--hf-text-xs)',
              color: 'var(--hf-ink-3)',
            }}
          >
            A comp-hours claim whose owner has a reporting manager is theirs to decide; deciding it
            here is recorded as an exception.
          </div>
        )}
        {byType.length > 0 && (
          <div
            style={{
              padding: 'var(--hf-space-2) var(--hf-space-4)',
              borderBottom: '1px solid var(--hf-rule)',
              fontSize: 'var(--hf-text-xs)',
              color: 'var(--hf-ink-3)',
            }}
          >
            Leave waiting: {byType.map((x) => `${x.label} ${x.count}`).join(' · ')}
          </div>
        )}
        <div>
          {reqsFailed && (
            <div style={{ padding: 'var(--hf-space-3) var(--hf-space-4) 0' }}>
              <LoadError what="the leave requests" onRetry={() => onRetryReqs?.()} />
            </div>
          )}
          {/* The whole point of the report this screen answers was corrections
              that could not be seen. If they cannot be READ, the queue has to
              say so — "Nothing on file" below would be the same silence again,
              and this time it would be a lie. */}
          {corrFailed && (
            <div style={{ padding: 'var(--hf-space-3) var(--hf-space-4) 0' }}>
              <InlineStatus
                tone="warning"
                action={{ label: 'Retry', onClick: onCorrectionsChanged }}
              >
                Time corrections could not be loaded, so any waiting here are not shown. Leave
                requests below are unaffected.
              </InlineStatus>
            </div>
          )}
          <div style={{ padding: '0 var(--hf-space-4)' }}>
            <DayEditDecisions
              rows={shownDayEdits}
              uid={uid}
              canAmend={canDecide}
              onChanged={onDayEditsChanged}
            />
          </div>
          {/* Mounted with no rows too, so deciding the last one keeps its receipt. */}
          <div style={{ padding: '0 var(--hf-space-4)' }}>
            <CorrectionDecisions
              rows={shownCorrections}
              uid={uid}
              onChanged={onCorrectionsChanged}
            />
          </div>
          {/* Not "all clear" while half the queue failed to load. */}
          <div style={{ padding: '0 var(--hf-space-4)' }}>
            <CompHoursDecisions
              bare
              rows={shownComp}
              uid={uid}
              categories={compCats}
              title="Compensated hours"
              onChanged={onCompChanged}
            />
          </div>
          {list.length === 0 &&
          shownCorrections.length === 0 &&
          shownComp.length === 0 &&
          shownDayEdits.length === 0 &&
          !corrFailed &&
          !reqsFailed ? (
            <div style={{ padding: '0 var(--hf-space-4)' }}>
              <EmptyState
                title={`Nothing ${filter === 'all' ? 'on file' : filter === 'rejected' ? 'denied' : filter}.`}
              >
                You’re all caught up.
              </EmptyState>
            </div>
          ) : (
            // In the same padded column as the correction and comp-hours rows,
            // so every row's columns line up.
            <div style={{ padding: '0 var(--hf-space-4)' }}>
              {list.map((r) => (
                <DecisionRow
                  key={r.id}
                  flush
                  name={r.full_name || 'Unknown'}
                  reference={reqRef(r.id)}
                  when={dayRange(r.start_date, r.end_date)}
                  what={
                    <>
                      <KindTag kind="leave" />
                      <span style={{ fontSize: 'var(--hf-text-md)', color: 'var(--hf-ink)' }}>
                        {r.leave_label || 'Leave'} leave
                      </span>
                      <Mono>{days(r.day_count)}</Mono>
                      {/* The approver was being asked to approve a pay-reducing action with
                        nothing on screen saying so. Unpaid leave keeps its required hours,
                        so the day reaches payroll as a shortfall priced at
                        shortfall / required * gross. */}
                      {!r.leave_paid && (
                        <span
                          style={{ fontSize: 'var(--hf-text-xs)', color: 'var(--hf-amber-text)' }}
                        >
                          unpaid · can reduce pay
                        </span>
                      )}
                    </>
                  }
                  detail={
                    <>
                      {r.reason || 'No note provided'}
                      {r.status === 'rejected' && r.decline_reason && (
                        <span style={{ display: 'block', color: 'var(--hf-ink-2)' }}>
                          Declined: {r.decline_reason}
                        </span>
                      )}
                    </>
                  }
                  filed={r.created_at ? fmtRelative(r.created_at) : undefined}
                  aside={
                    r.status === 'pending' && canDecide && r.profile_id === uid ? (
                      // The database refuses this outright (20261103100000), as
                      // it does a correction or a claim of your own.
                      <span style={YOURS}>yours — another approver decides</span>
                    ) : r.status === 'pending' && canDecide ? (
                      <DecisionActions
                        subject={leaveOf(r)}
                        onApprove={() => onDecide(r.id, 'approved')}
                        onDecline={(reason) => onDecide(r.id, 'rejected', { reason })}
                        consequence={[
                          'They see it declined, with your reason.',
                          'Nothing is charged to their balance.',
                        ]}
                      />
                    ) : (
                      <>
                        <RequestStatus status={r.status} kind={canDecide ? undefined : 'leave'} />
                        {/* Take back an approved leave (e.g. postponed / not used) — the
                          DB trigger refunds the balance; the row re-enters the queue. */}
                        {canDecide && r.status === 'approved' && r.profile_id !== uid && (
                          <Button
                            variant="ghost"
                            size="sm"
                            className="hf-hit"
                            onClick={() => setReverting(r)}
                          >
                            Revert
                          </Button>
                        )}
                      </>
                    )
                  }
                />
              ))}
            </div>
          )}
        </div>
      </section>

      {failed.has('dueDates') ? (
        <FailedCard
          label="Dates needing attention"
          what="the dates needing attention"
          onRetry={onDatesChanged}
        />
      ) : (
        <DueDatesCard rows={dueDates} onChanged={onDatesChanged} />
      )}

      {/* FR-8.5: what waited past the org's limit, and what became of it. */}
      <EscalationsCard rows={escalations} />

      {/* R7.4: the central list. A deviation should be as easy to find as the
          standard it departed from. */}
      {failed.has('exceptions') ? (
        <FailedCard label="Exceptions" what="the exceptions" onRetry={onRetryExceptions} />
      ) : (
        <ExceptionsCard rows={exceptions} period={exceptionsPeriod} />
      )}

      {/* Reference and setup, so last: each role's current handbook, and (for
          HR, behind its own button) issuing a new version. */}
      {failed.has('handbooks') ? (
        <FailedCard label="Role handbooks" what="the role handbooks" onRetry={onHandbooksChanged} />
      ) : (
        <RoleHandbooksCard
          handbooks={handbooks}
          roles={rolesList}
          behind={handbookBehind}
          canEdit={canDecide}
          onChanged={onHandbooksChanged}
        />
      )}

      {/* FR-8.7: org policies, their versions and who has acknowledged them. */}
      {canDecide && <PoliciesCard />}

      {/* One click approved every request, the pay-cutting ones included, with
          nothing said and only per-row Revert to take it back (HRA-02). */}
      <ConfirmDialog
        open={approvingAll}
        title={`Approve ${pendingLeave.length} leave requests?`}
        consequence={
          <>
            <ul style={{ margin: 0, paddingLeft: 'var(--hf-space-5)' }}>
              {pendingLeave.map((r) => (
                <li key={r.id}>
                  {r.full_name || 'Someone'} · {dayRange(r.start_date, r.end_date)} ·{' '}
                  {r.leave_label || 'Leave'} · {days(r.day_count)}
                  {!r.leave_paid && ' · unpaid'}
                </li>
              ))}
            </ul>
            <p style={{ margin: 'var(--hf-space-3) 0 0' }}>
              Each is charged to that person’s balance and they are told.
              {unpaidPending > 0 && (
                <strong style={{ color: 'var(--hf-amber-text)', fontWeight: 'inherit' }}>
                  {' '}
                  {unpaidPending} of them {unpaidPending === 1 ? 'is' : 'are'} unpaid and will
                  reduce pay.
                </strong>
              )}{' '}
              Time corrections and compensated hours are not included. Any one can be reverted
              afterwards.
            </p>
          </>
        }
        confirmLabel={`Approve ${pendingLeave.length} leave requests`}
        onCancel={() => setApprovingAll(false)}
        onConfirm={() => {
          setApprovingAll(false);
          onApproveAll?.(pendingLeave.map((r) => r.id));
        }}
      />
      <ConfirmDialog
        open={reverting !== null}
        title={`Revert ${reverting ? leaveOf(reverting) : 'this leave'}?`}
        consequence={[
          'It goes back to waiting and re-enters this queue.',
          `${days(reverting?.day_count ?? 0)} ${reverting?.day_count === 1 ? 'is' : 'are'} returned to their balance.`,
        ]}
        confirmLabel="Revert to waiting"
        onCancel={() => setReverting(null)}
        onConfirm={() => {
          if (reverting) onDecide(reverting.id, 'pending');
          setReverting(null);
        }}
      />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// MAIN SCREEN
// ─────────────────────────────────────────────────────────────────────────────
const TABS = [
  { id: 'profile', label: 'Profile & access', icon: 'user' },
  { id: 'comp', label: 'Compensation', icon: 'award' },
  // One meaning per glyph (UI-CONVENTIONS, Icons): leave is the struck
  // calendar, award is compensation and a credit of hours.
  { id: 'leave', label: 'Leave', icon: 'leave' },
  { id: 'hours', label: 'Hours', icon: 'clock' },
  { id: 'jd', label: 'Job description', icon: 'file' },
  { id: 'docs', label: 'Documents', icon: 'file' },
] as const;
type TabId = (typeof TABS)[number]['id'];

// This screen's sub-crumbs, published into the Topbar via usePageCrumbs below.
// Pure and exported so the mapping is tested directly rather than through a
// full render. `sel` only needs the two fields a crumb prints — not the whole
// roster row — so a unit test doesn't have to build one.
export function peopleCrumbs(
  view: View,
  sel: { id: string; full_name: string | null } | null,
  tab: TabId,
): PageCrumb[] {
  switch (view) {
    case 'dash':
      return [{ label: 'Dashboard' }];
    case 'payroll':
      return [{ label: 'Payroll' }];
    case 'requests':
      return [{ label: 'Requests & approvals' }];
    case 'details':
      // Before the roster loads (or a stale ?person= names nobody), there's no
      // record to crumb yet — just the title, same as the other views' plain
      // one-crumb case, only empty.
      if (!sel) return [];
      return [
        { label: sel.full_name ?? '', to: `/hr?view=details&person=${sel.id}` },
        { label: TABS.find((t) => t.id === tab)?.label ?? tab },
      ];
  }
}

export function PeopleHRScreen() {
  const { perms, roleId: actorRoleId, session } = useAuth();
  const showComp = canSeeComp(perms);
  const peopleFull = can(perms, 'people', 'full');
  const views = peopleViews(peopleFull);
  const actorIsOwner = actorRoleId === 'owner';

  const [people, setPeople] = useState<Profile[]>([]);
  const departments = useDepartments();
  // view/selId/tab live in the URL (?view=&person=&tab=) rather than useState,
  // so a link can land straight on a record and Back/Forward moves through
  // them. Names kept as before so most call sites — single-field setters —
  // don't move.
  const [params, setSearchParams] = useSearchParams();
  // A view this viewer is not offered (a lead's stale ?view=payroll) falls back
  // to the dashboard rather than rendering a crumb over an empty body. Derived,
  // not stored, so it resolves itself once perms load.
  const view: View = views.includes(params.get('view') as View)
    ? (params.get('view') as View)
    : 'dash';
  const selId = params.get('person');
  const tab: TabId = TABS.some((t) => t.id === params.get('tab'))
    ? (params.get('tab') as TabId)
    : 'profile';
  // One updater for a handler that changes more than one of the three at once
  // — calling setView/setSelId/setTab separately in the same handler would
  // each start from the params committed before this render and clobber one
  // another. `push` marks a user-initiated change (Back/Forward should visit
  // it); the roster-load default below replaces instead.
  const go = useCallback(
    (patch: { view?: View; person?: string | null; tab?: TabId }, push = false) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (patch.view !== undefined) next.set('view', patch.view);
          if ('person' in patch) {
            if (patch.person) next.set('person', patch.person);
            else next.delete('person');
          }
          if (patch.tab !== undefined) next.set('tab', patch.tab);
          return next;
        },
        { replace: !push },
      );
    },
    [setSearchParams],
  );
  const setView = (v: View) => go({ view: v }, true);
  const setSelId = (id: string | null) => go({ person: id }, true);
  const setTab = (t: TabId) => go({ tab: t }, true);
  // The one place several fields change together outside a plain roster pick:
  // the "Team capacity · hours this month" row opens straight at that
  // person's Hours tab, not wherever `tab` happened to be left.
  const openHours = (id: string) => go({ view: 'details', person: id, tab: 'hours' }, true);
  const [loading, setLoading] = useState(true);

  const [comp, setComp] = useState<Comp | null>(null);
  const [bank, setBank] = useState<Bank | null>(null);
  const [contact, setContact] = useState<Contact | null>(null);
  const [leaveTypes, setLeaveTypes] = useState<LeaveType[]>([]);
  const [balances, setBalances] = useState<LeaveBalance[]>([]);
  // Bumped when something writes to the selected person's record from a modal
  // — recording leave moves both their balance and their leave calendar.
  const [detailNonce, setDetailNonce] = useState(0);
  // Bumped after a period is locked so the register refetches its status.
  const [payrollNonce, setPayrollNonce] = useState(0);
  // Bulk credit: who is checked in the org monthly-hours table (Payroll's and
  // the Dashboard's tick the same set), and whether the grant dialog is open
  // for them. Cleared on a successful credit, left alone on Cancel — a checked
  // box surviving a closed dialog is the whole point of a bulk action.
  // The payroll month, as an offset from the current org month. -1: last month,
  // the one you would actually run. Owned here so the register and the monthly
  // summary under it show the SAME month — each kept its own, so August's
  // register sat above September's hours (HRA-12).
  const [payOffset, setPayOffset] = useState(-1);
  const [paySel, setPaySel] = useState<Set<string>>(new Set());
  const [payGranting, setPayGranting] = useState(false);
  const [amending, setAmending] = useState<{
    year: number;
    month: number;
    closesAt: Date;
  } | null>(null);
  const [requests, setRequests] = useState<LeaveRequest[]>([]);
  const [reqs, setReqs] = useState<PendingRequest[]>([]);
  // Time corrections queue alongside leave — same tab, same gate (people:full).
  // null = the read FAILED. [] = there are none. PostgREST answers an
  // RLS-forbidden read with an empty set, so the two must not share a value.
  const [corrections, setCorrections] = useState<PendingCorrection[] | null>([]);
  // Whole-day changes waiting (Edit day). RLS: HR reads every one. A failed
  // read shows none here; each still reached HR as a notification.
  const [dayEdits, setDayEdits] = useState<PendingDayEdit[]>([]);
  const reloadDayEdits = () => void listPendingDayEdits().then((r) => setDayEdits(r ?? []));
  // R5 — every pending claim RLS returns. Managers decide their own reporting
  // line from /me; this is HR's view, and deciding here over a live manager files
  // an exception.
  const [compQueue, setCompQueue] = useState<PendingComp[]>([]);
  // R6/R7 — dates needing attention, and every deviation on the record.
  const [dueDates, setDueDates] = useState<DueDate[]>([]);
  const [exceptions, setExceptions] = useState<HrException[]>([]);
  // Which month the org-wide exceptions list shows: 0 is this month.
  const [excOffset, setExcOffset] = useState(0);
  const [selKeyDates, setSelKeyDates] = useState<KeyDate[]>([]);
  const [selEvents, setSelEvents] = useState<EngagementEvent[]>([]);
  const [selExceptions, setSelExceptions] = useState<HrException[]>([]);
  // R9 — paperwork on the record, and the handbooks the gates check against.
  const [selDocs, setSelDocs] = useState<EmployeeDocument[]>([]);
  const [handbooks, setHandbooks] = useState<RoleHandbook[]>([]);
  const [compCats, setCompCats] = useState<CompCategory[]>([]);
  const [attendance, setAttendance] = useState<Attendance[]>([]);
  // Team-wide datasets for the Dashboard sub-section (org-wide, loaded once).
  const [compByPid, setCompByPid] = useState<Record<string, number>>({});
  const [teamBalances, setTeamBalances] = useState<LeaveBalance[]>([]);
  const [teamAtt, setTeamAtt] = useState<AttRow[]>([]);
  const [costTrend, setCostTrend] = useState<CostMonth[]>([]);
  const [rolesList, setRolesList] = useState<{ id: string; label: string }[]>([]);
  // The permission matrix, to tell a raise from a step down and to say when one
  // grants compensation visibility. role_perms_read is open to every signed-in user.
  const [rolePerms, setRolePerms] = useState<Readonly<Record<string, PermMap>> | null>(null);
  const [inviting, setInviting] = useState(false);
  const [rosterNonce, setRosterNonce] = useState(0);
  // One source for both. This screen previously made THREE separate org_settings
  // round-trips (tz_iana, week_start, hours) — which is why no single part of it
  // had the whole picture and the heatmap ran on a hardcoded Monday.
  const {
    weekStartsOn,
    tz: orgTz,
    hours: orgHoursOf,
    workdays: orgWorkdays,
    employment,
    dateFormat,
  } = useOrgDates();
  // This month's and this week's figures for every person RLS returns. One
  // loader, so the HR card, the org table, Timesheets and My Space cannot
  // disagree about anyone's hours.
  const [summaries, setSummaries] = useState<MonthSummary[]>([]);
  const [weekSummaries, setWeekSummaries] = useState<MonthSummary[]>([]);
  // ISO date → name and kind: the record's leave calendar draws each kind its own way.
  const [holidayMap, setHolidayMap] = useState<Record<string, LeaveCalendarHoliday>>({});
  const holidaySet = useMemo(() => new Set(Object.keys(holidayMap)), [holidayMap]);
  const toast = useToast();
  // Which reads failed. A failed read is not an empty list (UI-CONVENTIONS §7):
  // each card it feeds renders LoadError with a Retry instead of "nothing yet".
  const [failed, setFailed] = useState<ReadonlySet<ReadName>>(new Set());
  const markFailed = useCallback(
    (name: ReadName, bad: boolean) =>
      setFailed((f) => {
        if (f.has(name) === bad) return f;
        const next = new Set(f);
        if (bad) next.add(name);
        else next.delete(name);
        return next;
      }),
    [],
  );
  // Bumped by a Retry on a dashboard card, so the team-wide reads run again.
  const [dashNonce, setDashNonce] = useState(0);
  const reloadDueDates = useCallback(
    async (live: () => boolean = () => true) => {
      const r = await listDueDates();
      if (!live()) return;
      setDueDates(r.data);
      markFailed('dueDates', !!r.error);
    },
    [markFailed],
  );
  const reloadRequests = useCallback(
    async (live: () => boolean = () => true) => {
      if (!supabase) return;
      const r = await loadRequests(supabase);
      if (!live()) return;
      setReqs(r.data);
      markFailed('requests', r.failed);
    },
    [markFailed],
  );
  const reloadExceptions = useCallback(
    async (live: () => boolean = () => true, offset = 0) => {
      const r = await listExceptions(undefined, {
        ...monthRange(orgToday(orgTz), offset),
        tz: orgTz,
      });
      if (!live()) return;
      setExceptions(r.data);
      markFailed('exceptions', !!r.error);
    },
    [markFailed, orgTz],
  );
  const stepExceptions = (offset: number) => {
    setExcOffset(offset);
    void reloadExceptions(undefined, offset);
  };
  const excMonth = monthRange(orgToday(orgTz), excOffset).from;
  const reloadHandbooks = useCallback(
    async (live: () => boolean = () => true) => {
      const r = await listHandbooks();
      if (!live()) return;
      setHandbooks(r.data);
      markFailed('handbooks', !!r.error);
    },
    [markFailed],
  );

  // Roster + leave types + approvals queue (org-wide, load once).
  useEffect(() => {
    if (!supabase) {
      setLoading(false);
      return;
    }
    const sb = supabase;
    let active = true;

    sb.from('profiles')
      .select(
        'id, employee_no, manager_id, full_name, role_id, department_id, title, status, location, joined, avail_hrs, contract_hrs_per_day, assigned_hrs, job_description',
      )
      // Service accounts are machinery, not headcount (20260910100000): the
      // control account and the `triage` account bug-queue.mjs signs in as. They
      // made headcount read 9 for a team of 7, and every figure derived from this
      // one array inherited it — capacity, tenure, the attendance spread, leave
      // liability, the per-head payroll denominators.
      //
      // Filtered HERE rather than at each render site on purpose: this is the
      // single fetch the whole screen reads from, so one predicate covers every
      // card and no future one can forget.
      //
      // NOT filtered in lib/auth.tsx — the signed-in person loads their OWN
      // profile there, and the control account is somebody's actual login.
      //
      // Nor someone erased (erase_personal_data → 'archived', "Deleted user
      // ab12cd34"): they stayed in the roster and the headcount with a role
      // badge (bug report 2026-10-07). Records naming them still resolve
      // through their own joins; only the directory drops them.
      .not('status', 'in', '(system,archived)')
      .order('full_name')
      .then(({ data, error }) => {
        if (!active) return;
        markFailed('roster', !!error);
        const rows = (data as Profile[]) ?? [];
        setPeople(rows);
        setLoading(false);
      });

    // One group, one verdict: marking each read separately let a later success
    // clear an earlier failure.
    void Promise.all([
      sb.from('leave_types').select('id, label, short, color, days_per_year, paid, archived'),
      sb.from('roles').select('id, label').order('rank'),
      sb.from('holidays').select('date, name, kind'),
      sb.from('role_permissions').select('role_id, module, level'),
    ]).then(([types, roles, hols, perms]) => {
      if (!active) return;
      setLeaveTypes((types.data as LeaveType[]) ?? []);
      setRolesList((roles.data as { id: string; label: string }[]) ?? []);
      setHolidayMap(
        Object.fromEntries(
          ((hols.data as ({ date: string } & Exclude<LeaveCalendarHoliday, string>)[]) ?? []).map(
            (h) => [h.date, { name: h.name, kind: h.kind }],
          ),
        ),
      );
      setRolePerms(perms.error ? null : permsByRole(perms.data as PermRow[]));
      // Not a banner of its own: without the matrix every role change confirms.
      markFailed('setup', !!(types.error || roles.error || hols.error));
    });

    void reloadRequests(() => active);
    // RLS returns nothing below people:full, so this is safe to run for anyone
    // who can open the screen — it simply comes back empty.
    void listCorrections().then((rows) => active && setCorrections(rows));
    void listPendingDayEdits().then((rows) => active && setDayEdits(rows ?? []));
    void listPendingComp().then((rows) => active && setCompQueue(rows));
    void listCompCategories().then((c) => active && setCompCats(c));
    void reloadDueDates(() => active);
    void reloadExceptions(() => active);
    void reloadHandbooks(() => active);

    return () => {
      active = false;
    };
    // rosterNonce is bumped after an invite so the new person appears without a
    // page reload — the profiles row already exists by then, written by
    // handle_new_user() the moment GoTrue created the auth user.
  }, [rosterNonce, markFailed, reloadDueDates, reloadExceptions, reloadHandbooks, reloadRequests]);

  // Team-wide datasets for the Dashboard (composition, payroll, capacity, leave).
  useEffect(() => {
    if (!supabase) return;
    const sb = supabase;
    let active = true;

    sb.from('leave_balances')
      .select('profile_id, leave_type_id, allotted, used')
      .then(({ data, error }) => {
        if (!active) return;
        setTeamBalances((data as LeaveBalance[]) ?? []);
        markFailed('teamBalances', !!error);
      });

    // Attendance is NOT compensation. att_read opens it at people:view
    // (20260604010000_modules.sql) — the same level that opens this screen — so
    // gating the fetch on people:full was a client-side invention that left a
    // lead looking at a dashboard with its middle cut out.
    const since84 = iso(addDays(orgToday(orgTz), -84));
    sb.from('attendance')
      .select('profile_id, date, status, hours')
      .gte('date', since84)
      .then(({ data, error }) => {
        if (!active) return;
        setTeamAtt((data as AttRow[]) ?? []);
        markFailed('teamAtt', !!error);
      });

    if (showComp) {
      sb.from('employee_compensation')
        .select('profile_id, annual_ctc')
        .then(({ data, error }) => {
          if (!active) return;
          markFailed('teamComp', !!error);
          const rows = (data as { profile_id: string; annual_ctc: number | null }[]) ?? [];
          const m: Record<string, number> = {};
          rows.forEach((r) => {
            if (r.annual_ctc) m[r.profile_id] = r.annual_ctc;
          });
          setCompByPid(m);
        });

      // Same gate as the one above it, and the reason is the same one: pl_read
      // is people:full OR your own row once its period is closed, so below the
      // gate this returns a real non-empty set — one person's salary history —
      // which totalled under a heading saying "team" is a confident lie rather
      // than an empty card.
      const payrollToday = orgToday(orgTz);
      void loadCostTrend(payrollToday.getFullYear(), payrollToday.getMonth() + 1).then((r) => {
        if (!active) return;
        setCostTrend(r.data);
        markFailed('costTrend', !!r.error);
      });
    } else {
      setCompByPid({});
      setCostTrend([]);
    }

    return () => {
      active = false;
    };
  }, [showComp, orgTz, markFailed, dashNonce]);

  // Per-employee record: comp (gated), bank (gated), balances, attendance.
  useEffect(() => {
    if (!supabase || !selId) return;
    const sb = supabase;
    let active = true;

    // attendance.date is org-local (stamped by org_today()), so the window has to
    // be the org's week too. Hardcoded Monday here fetched the wrong seven rows
    // outright, not merely rendered them in the wrong order.
    const start = weekStart(weekStartsOn, orgToday(orgTz));
    // The private three are read only at people:full; below it they stay null.
    const one = (table: string, columns: string) =>
      showComp
        ? sb.from(table).select(columns).eq('profile_id', selId).maybeSingle()
        : Promise.resolve({ data: null, error: null });
    // One verdict for the record: any of these failing is "couldn't load this
    // record", never a record with no contact details and a zero balance.
    void Promise.all([
      one('employee_compensation', 'profile_id, annual_ctc, currency'),
      one('employee_bank', 'profile_id, holder, account_no, ifsc, bank_name, branch'),
      one('employee_contact', CONTACT_SELECT),
      sb
        .from('leave_balances')
        .select('profile_id, leave_type_id, allotted, used')
        .eq('profile_id', selId),
      sb
        .from('leave_requests')
        .select(
          'id, profile_id, leave_type_id, start_date, end_date, day_count, reason, status, approver, decided_at',
        )
        .eq('profile_id', selId)
        .eq('status', 'approved'),
      sb
        .from('attendance')
        .select('profile_id, date, status, hours')
        .eq('profile_id', selId)
        .gte('date', iso(start))
        .lte('date', iso(addDays(start, 6))),
    ]).then(([c, b, ct, bal, lr, att]) => {
      if (!active) return;
      setComp((c.data as Comp | null) ?? null);
      setBank((b.data as Bank | null) ?? null);
      setContact((ct.data as Contact | null) ?? null);
      setBalances((bal.data as LeaveBalance[]) ?? []);
      setRequests((lr.data as LeaveRequest[]) ?? []);
      setAttendance((att.data as Attendance[]) ?? []);
      markFailed(
        'record',
        [c, b, ct, bal, lr, att].some((r) => !!r.error),
      );
    });

    return () => {
      active = false;
    };
  }, [selId, showComp, weekStartsOn, orgTz, detailNonce, markFailed]);

  // This month and this week, for everyone RLS returns. Feeds the contracted-hours
  // card and the capacity rows; the org-wide table below loads its own so it can
  // step through months independently.
  useEffect(() => {
    let active = true;
    const month = monthRange(orgToday(orgTz));
    const wk = weekStart(weekStartsOn, orgToday(orgTz));
    const today = orgDateKey(Date.now(), orgTz);
    const common = { tz: orgTz, org: orgHoursOf, workdays: orgWorkdays, today };
    void Promise.all([
      loadSummaries({ from: month.from, to: month.to, ...common }),
      loadSummaries({ from: isoDay(wk), to: isoDay(addDays(wk, 6)), ...common }),
    ]).then(([m, w]) => {
      if (!active) return;
      setSummaries(m);
      setWeekSummaries(w);
    });
    return () => {
      active = false;
    };
  }, [orgTz, weekStartsOn, orgHoursOf, orgWorkdays, rosterNonce]);

  const sel = people.find((p) => p.id === selId) ?? null;

  // Below md the roster and the record take turns rather than share the width
  // (list → detail, UI-CONVENTIONS §8): no ?person= is the roster, a person is
  // their record, with a way back. The URL already held both states.
  const phone = useBelow('md');
  const firstId = people[0]?.id;
  // The first person opens by default where the record sits beside the list.
  // Not on a phone: there the list IS the page, and opening someone unasked hid
  // it. Not a user pick, so it replaces rather than pushes, and it reads `prev`
  // at apply time, so it can't stomp on a ?person= already in the link. Only
  // when ?person= is missing: setSearchParams changes with every URL, and a
  // same-URL replace after each roster or tab press was a history write.
  useEffect(() => {
    if (phone || !firstId || selId) return;
    setSearchParams(
      (prev) => {
        if (prev.get('person')) return prev;
        const next = new URLSearchParams(prev);
        next.set('person', firstId);
        return next;
      },
      { replace: true },
    );
  }, [phone, firstId, selId, setSearchParams]);

  // Focus follows the turn: onto the record's heading when a person opens from
  // a row on another view (Team capacity, the payroll summary), whose button is
  // gone with its view, so focus fell to <body>. On a phone the roster and the
  // record are pages of their own as well: a roster pick goes to the heading,
  // and Back (or the browser's) to the person's row. At desktop width the
  // roster stays beside the record, so a pick there keeps focus on its row. Not
  // on the first load: a link that lands on a record leaves focus where it
  // starts. `turn` is undefined while the roster loads, false on another view,
  // null on the list, and the person's id on their record.
  const rootRef = useRef<HTMLDivElement>(null);
  const recordHeadingRef = useRef<HTMLHeadingElement>(null);
  const turn = loading ? undefined : view !== 'details' ? false : (sel?.id ?? null);
  const lastTurn = useRef(turn);
  useEffect(() => {
    const was = lastTurn.current;
    lastTurn.current = turn;
    if (turn === undefined || turn === false || was === undefined || turn === was) return;
    if (!phone && !(was === false && turn)) return;
    // In from another view by its tab, which is still there and keeps focus.
    if (was === false && document.activeElement !== document.body) return;
    // The list and a record are separate pages here: each starts at its top.
    if (phone) rootRef.current?.scrollTo?.({ top: 0 });
    if (turn) recordHeadingRef.current?.focus();
    else
      [...(rootRef.current?.querySelectorAll<HTMLElement>('[data-person]') ?? [])]
        .find((row) => row.dataset.person === was)
        ?.focus();
  }, [turn, phone]);

  // R6/R7 on the record. Reloaded on selection rather than fetched for the whole
  // roster: only one person's register is ever on screen.
  // Whose record is open now, read after the awaits below. A reload that lands
  // after HR has moved on (a slow read, or an Undo toast, which outlives the tab
  // it came from) put the last person's rows on this one's record, where a
  // signature tick writes to the last person's document.
  const selRef = useRef(selId);
  useEffect(() => {
    selRef.current = selId;
  }, [selId]);
  const reloadRecord = useCallback(async () => {
    if (!selId) {
      setSelKeyDates([]);
      setSelEvents([]);
      setSelExceptions([]);
      setSelDocs([]);
      return;
    }
    const [k, e, x, d] = await Promise.all([
      listKeyDates(selId),
      listEvents(selId),
      listExceptions(selId),
      listDocuments(selId),
    ]);
    if (selRef.current !== selId) return;
    setSelKeyDates(k.data);
    setSelEvents(e.data);
    setSelExceptions(x.data);
    setSelDocs(d.data);
    markFailed('keyDates', !!k.error);
    markFailed('events', !!e.error);
    markFailed('selExceptions', !!x.error);
    markFailed('docs', !!d.error);
  }, [selId, markFailed]);
  useEffect(() => {
    void reloadRecord();
  }, [reloadRecord]);

  // Anchors the attendance strip below. Org week, org calendar.
  const start = weekStart(weekStartsOn, orgToday(orgTz));
  // Who has not acknowledged the current handbook, per role. Derived from the
  // gap dates the R6 sweep already maintains rather than a second query that
  // could disagree with them.
  const handbookBehind = useMemo(() => {
    const out: Record<string, number> = {};
    for (const d of dueDates) {
      if (d.date_type !== 'handbook_gap') continue;
      const role = people.find((p) => p.id === d.profile_id)?.role_id;
      if (role) out[role] = (out[role] ?? 0) + 1;
    }
    return out;
  }, [dueDates, people]);

  // The tab's badge is the registry's number, the same one the sidebar shows
  // (docs/UI-CONVENTIONS.md §1): every queue this tab owns, gated on deciding
  // (people:full), less what is the viewer's own. It summed the lists here
  // instead, which counted your own requests and every comp-hours claim, so the
  // tab and the sidebar disagreed. Recounted whenever a list reloads.
  const reloaded = useMemo(
    () => [reqs, corrections, compQueue, dueDates, dayEdits],
    [reqs, corrections, compQueue, dueDates, dayEdits],
  );
  const { byQueue } = useAttention(perms, session?.user.id, reloaded);
  const pendingQueue = QUEUES.filter((q) => q.tab === 'requests').reduce(
    (n, q) => n + (byQueue[q.id] ?? 0),
    0,
  );
  // The month's actual working days, not a flat 4.33 weeks — that constant ignored
  // holidays entirely and never matched the month being displayed. The ORG's
  // month and the org's today: the browser's put a viewer in another zone a day,
  // and once a month a whole month, out (docs/UI-CONVENTIONS.md §2).
  const monthShape = useMemo(() => {
    const month = monthRange(orgToday(orgTz));
    const all = workingDayList(month.from, month.to, orgWorkdays, holidaySet);
    const today = isoDay(orgToday(orgTz));
    return { total: all.length, elapsed: all.filter((d) => d <= today).length };
  }, [orgWorkdays, holidaySet, orgTz]);
  // Contracted working days in the calendar year — the honest divisor for a
  // salary-per-hour rate, where a flat 52 weeks counted every holiday as paid
  // working time.
  const yearWorkingDays = useMemo(() => {
    const y = orgToday(orgTz).getFullYear();
    return workingDayList(`${y}-01-01`, `${y}-12-31`, orgWorkdays, holidaySet).length;
  }, [orgWorkdays, holidaySet, orgTz]);

  // ── Mutations ───────────────────────────────────────────────────────────────
  // Optimistic write through lib/write, which asks for the written row back. An
  // update RLS filters out is zero rows and NO error, and this used to call that
  // a save: a lead retitled a teammate, saw it in the header and the roster, and
  // lost it on reload (HRP-V1). A refusal now reverts and is reported like any
  // failure. The result goes back to the field that asked, which shows "Saved"
  // or keeps the draft with the reason.
  const persist = async (
    optimistic: () => void,
    revert: () => void,
    run: () => Parameters<typeof write>[0],
    returning = 'id',
  ): Promise<SaveResult> => {
    optimistic();
    const r = await write(run(), returning);
    if (r.ok) return {};
    revert();
    if (r.reason === 'refused') return { error: 'You can’t change this. Nothing was saved.' };
    captureError(r.error, { op: 'people.persist' });
    return { error: `${r.error.message}${r.error.hint ? ` ${r.error.hint}` : ''}` };
  };
  const NOT_CONNECTED: SaveResult = { error: 'Not connected.' };

  const patchProfile = async (delta: Partial<Profile>): Promise<SaveResult> => {
    if (!supabase || !sel) return NOT_CONNECTED;
    const sb = supabase;
    const prev = people;
    return persist(
      () => setPeople((ps) => ps.map((p) => (p.id === sel.id ? { ...p, ...delta } : p))),
      () => setPeople(prev),
      () => sb.from('profiles').update(delta).eq('id', sel.id),
    );
  };

  // The three private tables key on profile_id and have no `id` to ask back for.
  const patchBank = async (delta: Partial<Bank>): Promise<SaveResult> => {
    if (!supabase || !sel) return NOT_CONNECTED;
    const sb = supabase;
    const prev = bank;
    return persist(
      () => setBank((b) => ({ ...(b ?? { profile_id: sel.id }), ...delta }) as Bank),
      () => setBank(prev),
      () =>
        sb
          .from('employee_bank')
          .upsert({ profile_id: sel.id, ...delta }, { onConflict: 'profile_id' }),
      'profile_id',
    );
  };

  const patchContact = async (delta: Partial<Contact>): Promise<SaveResult> => {
    if (!supabase || !sel) return NOT_CONNECTED;
    const sb = supabase;
    const prev = contact;
    return persist(
      () =>
        setContact((c) => ({
          ...(c ?? {
            mobile: null,
            personal_email: null,
            address: null,
            dob: null,
            emergency_name: null,
            emergency_relation: null,
            emergency_phone: null,
          }),
          ...delta,
        })),
      () => setContact(prev),
      () =>
        sb
          .from('employee_contact')
          .upsert({ profile_id: sel.id, ...delta }, { onConflict: 'profile_id' }),
      'profile_id',
    );
  };

  const saveCtc = async (annual_ctc: number): Promise<SaveResult> => {
    if (!supabase || !sel) return NOT_CONNECTED;
    const sb = supabase;
    const prev = comp;
    return persist(
      () =>
        setComp((c) => ({ ...(c ?? { profile_id: sel.id, currency: 'INR' }), annual_ctc }) as Comp),
      () => setComp(prev),
      () =>
        sb
          .from('employee_compensation')
          .upsert({ profile_id: sel.id, annual_ctc }, { onConflict: 'profile_id' }),
      'profile_id',
    );
  };

  // Through set_leave_allotment, not a raw upsert on leave_balances. The upsert
  // sent an ABSOLUTE `allotted`, so setting it to 12 on someone who had accrued 2
  // comp-off days destroyed those 2 days silently, with nothing recording that it
  // happened. The function demands a reason, refuses to allot below what is
  // already used, and records the accrued figure the new value absorbs.
  //
  // Not optimistic: this one can be refused by the database, and a balance that
  // flicks to the new number and back is worse than one that waits.
  const setAllotted = async (
    leaveTypeId: string,
    allotted: number,
    reason: string,
  ): Promise<SaveResult> => {
    if (!supabase || !sel) return { error: 'Not connected.' };
    const { error } = await supabase.rpc('set_leave_allotment', {
      p_profile: sel.id,
      p_type: leaveTypeId,
      p_allotted: allotted,
      p_reason: reason,
    });
    if (error) {
      // The database's own words: it already says what to do instead.
      captureError(error, { where: 'set_leave_allotment' });
      return { error: error.message };
    }
    setBalances((bs) => {
      const exists = bs.some((b) => b.leave_type_id === leaveTypeId);
      return exists
        ? bs.map((b) => (b.leave_type_id === leaveTypeId ? { ...b, allotted } : b))
        : [...bs, { profile_id: sel.id, leave_type_id: leaveTypeId, allotted, used: 0 }];
    });
    return {};
  };

  const setContract = (contract_hrs_per_day: number | null) =>
    patchProfile({ contract_hrs_per_day });

  // Role assignment. It reverted in silence when the database guard refused it
  // (a non-owner attempting an owner transition), which read as a glitch
  // (HRP-03). A step down is undoable from the receipt; a raise was confirmed
  // before it got here.
  const setRole = async (role_id: string, undo = false) => {
    if (!sel) return;
    const who = sel.full_name ?? 'This person';
    const prev = sel.role_id;
    const { error } = await patchProfile({ role_id });
    if (error) {
      toast.push({ tone: 'error', message: `Couldn’t change ${who}’s role — ${error}` });
      return;
    }
    toast.push({
      tone: 'success',
      message: `${who} is now ${roleLabel(role_id)}.`,
      action:
        prev && !undo ? { label: 'Undo', onClick: () => void setRole(prev, true) } : undefined,
    });
  };

  // Deciding leave. It flipped the row and said nothing: under the default
  // Pending filter the row simply vanished, a refusal snapped it back unannounced,
  // and a decline carried no reason (HRA-10). Every decision now leaves a receipt
  // with Undo, which returns it to waiting — the database refunds the balance on
  // approved → pending. Through lib/write, so a row RLS filters out is a refusal.
  const decide = async (
    id: string,
    status: 'approved' | 'rejected' | 'pending',
    opts: DecideOpts = {},
  ): Promise<boolean> => {
    if (!supabase || !session) return false;
    const before = reqs.find((r) => r.id === id) ?? null;
    const patch =
      status === 'pending'
        ? { status, approver: null, decided_at: null }
        : {
            status,
            approver: session.user.id,
            decided_at: new Date().toISOString(),
            // Only a decline carries a reason; the column is null otherwise
            // (keep_decline_reason, 20260930130000).
            ...(status === 'rejected' ? { decline_reason: opts.reason ?? null } : {}),
          };
    // Optimistic — flip just this row in place (stays visible under the
    // Approved/Declined filters).
    setReqs((rs) =>
      rs.map((r) => (r.id === id ? { ...r, ...patch, decline_reason: opts.reason ?? null } : r)),
    );
    const res = await write(supabase.from('leave_requests').update(patch).eq('id', id));
    if (!res.ok) {
      // Refetch rather than restore `before`: an Undo runs from an older render,
      // whose copy of the row predates the decision it is undoing.
      void reloadRequests();
      if (res.reason === 'error') captureError(res.error, { op: 'leave.decide', id, status });
      toast.push({
        tone: 'error',
        message: `Couldn’t update ${before ? leaveOf(before) : 'the leave request'} — ${
          res.reason === 'refused' ? 'you can’t decide this request.' : res.error.message
        }`,
      });
      return false;
    }
    if (!opts.quiet && before) {
      const what = `${leaveOf(before)} · ${dayRange(before.start_date, before.end_date)}`;
      toast.push({
        tone: 'success',
        message:
          status === 'approved'
            ? `Approved ${what}.`
            : status === 'rejected'
              ? `Declined ${what}.`
              : `Reverted ${what} to waiting.`,
        action:
          status === 'pending'
            ? undefined
            : { label: 'Undo', onClick: () => void decide(id, 'pending') },
      });
    }
    return true;
  };

  const approveAll = async (ids: string[]) => {
    const results = await Promise.all(ids.map((id) => decide(id, 'approved', { quiet: true })));
    const ok = results.filter(Boolean).length;
    // Each failure has already said so; this is the receipt for the rest.
    if (ok)
      toast.push({
        tone: 'success',
        message: `Approved ${ok} leave request${ok === 1 ? '' : 's'}${ok < ids.length ? ` of ${ids.length}` : ''}.`,
      });
  };

  // Expand the selected person's approved leave into per-day cells for the calendar.
  const typeLabel: Record<string, string> = {};
  leaveTypes.forEach((t) => (typeLabel[t.id] = t.label));
  // Chargeable days only, via the shared rule — this used to expand every
  // CALENDAR day, so an approved Fri–Mon leave showed HR "4 days off" while
  // day_count deducted 2 and the employee's own calendar showed 2. Same request,
  // two screens, two numbers.
  const leaveDates = requests.flatMap((r) =>
    workingDayList(r.start_date, r.end_date, orgWorkdays, holidaySet).map((date) => ({
      date,
      type: r.leave_type_id,
      reason: r.reason,
    })),
  );

  // Attendance strip — this week's days, as AttendanceStrip draws them.
  const attByDate: Record<string, Attendance> = {};
  attendance.forEach((a) => (attByDate[a.date] = a));
  const leaveOn = new Map(leaveDates.map((l) => [l.date, l.type]));
  // A record wins: a holiday or a leave day someone worked shows what they did.
  // A holiday comes with its kind and a leave day with its type, so the strip
  // draws the calendars' own marks; both are known ahead, so a Friday holiday
  // shows before Friday. A missing row is null, not 'off': on a working day
  // that is a day with no record, where 'off' made a no-show look like a
  // weekend (HRP-15). A working day still ahead is 'upcoming'; a day off ahead
  // is the hatch, as any day off is. The org decides today and the workdays.
  const stripKeys = Array.from({ length: 7 }, (_, i) => iso(addDays(start, i)));
  const orgTodayKey = iso(orgToday(orgTz));
  const strip: StripDay[] = stripKeys.map((k) => {
    const status = attByDate[k]?.status;
    const hol = holidayMap[k];
    if (hol && (!status || status === 'holiday'))
      return typeof hol === 'string' ? { holiday: hol } : { holiday: hol.name, kind: hol.kind };
    const type = leaveOn.get(k);
    const lt = type ? leaveTypes.find((t) => t.id === type) : undefined;
    if (type && (!status || status === 'leave'))
      return { leave: lt?.label ?? 'Leave', color: lt?.color };
    if (status) return status;
    return k > orgTodayKey && isWorkday(k, orgWorkdays, holidaySet) ? 'upcoming' : null;
  });
  const stripWorkdays = stripKeys.map((k) => isWorkday(k, orgWorkdays, holidaySet));
  const selWeek = weekSummaries.find((r) => r.profileId === selId);

  // Below lg the roster and the record stack and the screen scrolls; at desktop
  // width (and in jsdom, which has no media engine) nothing changes.
  const stacked = useBreakpoint() !== 'xl';

  usePageCrumbs(peopleCrumbs(view, sel, tab));

  if (!supabase) {
    return (
      <div style={{ padding: 'var(--hf-space-6)', color: 'var(--hf-ink-3)' }}>
        Supabase isn’t configured — set <Mono>VITE_SUPABASE_URL</Mono> /{' '}
        <Mono>VITE_SUPABASE_ANON_KEY</Mono>.
      </div>
    );
  }

  // Bulk credit: several people, one event, one insert — for when a whole team
  // sat in the same client meeting or travel day. Select all excludes the
  // viewer; the row itself refuses a self-credit regardless. It sits in the
  // table's header, and the tick box leads its row (critique #8). Built once
  // and handed to both monthly tables that offer it, Payroll's and the
  // Dashboard's (bug report 0eb5bbb4), so they tick the same people the same
  // way and open the one CompHoursModal below.
  const bulkCredit: BulkCredit = {
    selection: (
      <span
        role="group"
        aria-label="Credit hours to several people"
        style={{
          display: 'inline-flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 'var(--hf-space-2)',
        }}
      >
        {/* Says what the tick boxes are for, and offers the action only once
            there is someone to act on (HRA-18). */}
        {paySel.size === 0 ? (
          <span style={{ fontSize: 'var(--hf-text-sm)', color: 'var(--hf-ink-3)' }}>
            Tick people to credit them the same hours
          </span>
        ) : (
          <>
            <span style={{ fontSize: 'var(--hf-text-sm)', color: 'var(--hf-ink)' }}>
              {paySel.size} selected
            </span>
            <Button variant="secondary" size="sm" icon="award" onClick={() => setPayGranting(true)}>
              Credit hours
            </Button>
            <Button variant="ghost" size="sm" onClick={() => setPaySel(new Set())}>
              Clear
            </Button>
          </>
        )}
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setPaySel(new Set(selectableIds(people, session?.user.id)))}
        >
          Select all
        </Button>
      </span>
    ),
    rowLead: (r) =>
      // Your own row keeps its place in the column, and says why it can't be
      // ticked: the trigger refuses a self-credit.
      r.profileId === session?.user.id ? (
        <Checkbox
          label="You can't credit hours to yourself"
          hideLabel
          checked={false}
          disabled
          onChange={() => {}}
        />
      ) : (
        <Checkbox
          label={`Select ${r.name}`}
          hideLabel
          checked={paySel.has(r.profileId)}
          onChange={() =>
            setPaySel((s) => {
              const next = new Set(s);
              if (!next.delete(r.profileId)) next.add(r.profileId);
              return next;
            })
          }
        />
      ),
  };

  return (
    <div
      ref={rootRef}
      style={{
        padding: stacked ? 'var(--hf-space-4)' : 'var(--hf-space-5)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--hf-space-4)',
        height: '100%',
        boxSizing: 'border-box',
        // At desktop width each view owns its scroller, so the roster and the
        // record scroll side by side; below it they stack and the screen scrolls.
        // Never hidden: a stack of fixed-height scrollers was a clipped page
        // (HRA-07, HRP-08), and at desktop width anything that cannot shrink into
        // its view scrolls here rather than being cut off.
        overflow: 'auto',
      }}
    >
      {inviting && (
        <InviteModal
          roles={rolesList}
          actorIsOwner={actorIsOwner}
          emailFormat={employment.email_format}
          onClose={() => setInviting(false)}
          onInvited={() => setRosterNonce((n) => n + 1)}
        />
      )}
      {/* Screen level, not inside the Payroll view: the Dashboard's monthly
          table credits through it too, and a modal under `view === 'payroll'`
          never opened from there. */}
      {payGranting && (
        <CompHoursModal
          mode="grant"
          uid={[...paySel][0] ?? ''}
          uids={[...paySel]}
          personName={people
            .filter((p) => paySel.has(p.id))
            .map((p) => p.full_name || 'Someone')
            .join(', ')}
          categories={compCats}
          today={isoDay(orgToday(orgTz))}
          onClose={() => setPayGranting(false)}
          onFiled={() => {
            setPayGranting(false);
            setPaySel(new Set());
            // The Team capacity card reads off `summaries`, not this table.
            setRosterNonce((n) => n + 1);
            // Remounts Payroll's register + summary group and the Dashboard's
            // summary group: both tables refetch.
            setPayrollNonce((n) => n + 1);
          }}
        />
      )}
      {/* Sub-section nav */}
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          gap: 'var(--hf-space-2) var(--hf-space-3)',
          flexShrink: 0,
          minWidth: 0,
        }}
      >
        <Tabs<View>
          label="People sections"
          tabs={(
            [
              { id: 'dash', label: 'Dashboard', icon: 'grid' },
              { id: 'details', label: 'People details', icon: 'users' },
              {
                id: 'requests',
                label: 'Requests & approvals',
                icon: 'inbox',
                // Gated on people:full — the ability to DECIDE. This used to be a
                // raw count of whatever lr_read returned, so a member saw their
                // own pending requests badged as if they were an approval queue
                // waiting on them. See docs/UI-CONVENTIONS.md.
                badge: <AttentionCount n={pendingQueue} label="requests awaiting you" />,
              },
              // No badge, ever: closing a month is not a queue — nothing on it is
              // awaiting a decision — so it carries no attention count
              // (docs/UI-CONVENTIONS.md §1), and lib/attention.ts declares none.
              { id: 'payroll', label: 'Payroll', icon: 'award' },
            ] as const
          ).filter((t) => views.includes(t.id))}
          value={view}
          onChange={setView}
        />
        <span style={{ fontSize: 'var(--hf-text-xs)', color: 'var(--hf-ink-3)' }}>
          {VIEW_SUBTITLE[view]}
        </span>
      </div>

      {failed.has('setup') && (
        <InlineStatus
          tone="error"
          action={{ label: 'Retry', onClick: () => setRosterNonce((n) => n + 1) }}
        >
          Couldn’t load the leave types, roles or holidays, so some lists on this screen are
          incomplete.
        </InlineStatus>
      )}

      {/* ── DASHBOARD ── */}
      {view === 'dash' && (
        <div style={{ flex: 1, minHeight: 0 }}>
          <PeopleDashboard
            people={people}
            compByPid={compByPid}
            balances={teamBalances}
            leaveTypes={leaveTypes}
            summaries={summaries}
            monthShape={monthShape}
            attendance={teamAtt}
            costTrend={costTrend}
            failed={failed}
            onRetry={() => setDashNonce((n) => n + 1)}
            reqs={reqs}
            showComp={showComp}
            weekStartsOn={weekStartsOn}
            tz={orgTz}
            orgHours={orgHoursOf}
            workdays={orgWorkdays}
            holidays={holidaySet}
            canSeeSummary={canSeeOrgSummary(perms)}
            departments={departments}
            onOpenPerson={openHours}
            // Payroll's gate, unchanged: the table only renders at
            // canSeeOrgSummary, which is people:full AND timesheets:full, and
            // Payroll is people:full on top of the same canSeeOrgSummary.
            bulkCredit={bulkCredit}
            summaryNonce={payrollNonce}
          />
        </div>
      )}

      {/* ── PAYROLL ──
          Everything org-wide about paying the month, in one place: the period
          you close, the register that prices it, the hours that register is
          computed from, and what the same people cost by contract. Per-person
          payroll stays on the person's own record — the Compensation and Hours
          tabs — because that is about one employee, not about the payroll run.

          This view owns its scroll, like the Dashboard root and the details
          pane: the screen root is height:100%, so the app shell's scroller
          never engages on /hr. */}
      {view === 'payroll' && peopleFull && (
        <div
          className="hf-scroll"
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 'var(--hf-space-4)',
            height: '100%',
            overflowY: 'auto',
            paddingRight: 2,
            paddingBottom: 'var(--hf-space-1)',
          }}
        >
          {/* The workspace lives here rather than inside PayrollPeriods: that
              component is month-scoped and holds no org hours, no workdays and no
              person, and threading them through it to reach one modal would be
              the wrong shape. Everything it needs is already in scope on this view. */}
          {amending && (
            <AmendWorkspace
              year={amending.year}
              month={amending.month}
              tz={orgTz}
              org={orgHoursOf}
              workdays={orgWorkdays}
              closesAt={amending.closesAt}
              uid={session?.user.id ?? ''}
              onClose={() => {
                setAmending(null);
                // Hours may have moved while it was open; the register prices them,
                // and the monthly summary below reads them.
                setPayrollNonce((n) => n + 1);
                // Corrections decided inside the workspace are gone from the
                // pending queue — without this the Requests tab and its badge kept
                // offering them until a full reload.
                void listCorrections().then(setCorrections);
              }}
            />
          )}

          {/* ponytail: PayrollPeriods and PayrollRegister each call listPeriods().
              One round-trip on a six-row table; hoist the list here if a third
              consumer appears. */}
          {/* ONE key for everything that must refresh together, on a Fragment
              that renders no DOM of its own. Bumping payrollNonce remounts the
              whole group, which refetches.

              It used to be `key={payrollNonce}` written on BOTH the register and
              the summary. Those are siblings, so both carried the same key.
              React keeps one component per key when it reconciles siblings, so on
              every bump (Lock, Amend, closing the workspace) it left an old
              register mounted — and the page stacked duplicate "Payroll register"
              cards, one more per bump, some showing data and one saying "Nothing
              computed". A single key on the group cannot collide with itself. Put
              any new card that has to refresh after an amendment INSIDE this
              Fragment; never give it a key={payrollNonce} of its own. */}
          <Fragment key={payrollNonce}>
            {/* One month for the register and the hours it prices, stepped in
                the register's header. */}
            <PayrollRegister
              canRun={peopleFull}
              tz={orgTz}
              // The month's Lock / Amend live in the register now (bug report
              // 2026-10-05: the periods card repeated it). onChanged bumps the
              // nonce, which remounts this group and refetches.
              monthActions={(year, month) => (
                <PayrollPeriods
                  only={{ year, month }}
                  canLock={peopleFull}
                  onChanged={() => setPayrollNonce((n) => n + 1)}
                  onAmendmentOpen={(y, m, closesAt) => setAmending({ year: y, month: m, closesAt })}
                />
              )}
              offset={payOffset}
              onOffset={setPayOffset}
            />

            {/* The register's own input, column for column: required / worked /
                effective / shortfall are exactly what it prices into gross →
                deduction → payable. Stacked, the month closes without a tab
                switch. Its gate is already the payroll gate — people:full AND
                timesheets:full — so it is narrower than this view, not wider. */}
            {canSeeOrgSummary(perms) && (
              <>
                {/* `pinned` hides the table's own stepper and `key` remounts it on
                    the view's month: it cannot drift from the register above. */}
                <MonthlySummary
                  key={payOffset}
                  pinned
                  initialOffset={payOffset}
                  tz={orgTz}
                  org={orgHoursOf}
                  workdays={orgWorkdays}
                  exportable
                  onOpenPerson={openHours}
                  {...bulkCredit}
                />
              </>
            )}
          </Fragment>

          {showComp && (
            <SalaryByDepartment people={people} compByPid={compByPid} departments={departments} />
          )}
        </div>
      )}

      {/* ── REQUESTS & APPROVALS ── offered per peopleViews; deciding gates on canDecide. */}
      {view === 'requests' && views.includes('requests') && (
        <RequestsApprovals
          reqs={reqs}
          reqsFailed={failed.has('requests')}
          onRetryReqs={() => void reloadRequests()}
          leaveTypes={leaveTypes}
          canDecide={peopleFull}
          onDecide={(id, status, opts) => void decide(id, status, opts)}
          onApproveAll={(ids) => void approveAll(ids)}
          corrections={corrections}
          compQueue={compQueue}
          compCats={compCats}
          dueDates={dueDates}
          exceptions={exceptions}
          exceptionsPeriod={{
            label: monthLabel(Number(excMonth.slice(0, 4)), Number(excMonth.slice(5, 7))),
            onPrev: () => stepExceptions(excOffset - 1),
            onNext: () => stepExceptions(Math.min(0, excOffset + 1)),
            nextDisabled: excOffset >= 0,
          }}
          onDatesChanged={() => void reloadDueDates()}
          handbooks={handbooks}
          rolesList={rolesList}
          handbookBehind={handbookBehind}
          onHandbooksChanged={() => void reloadHandbooks()}
          failed={failed}
          onRetryExceptions={() => void reloadExceptions(undefined, excOffset)}
          uid={session?.user.id ?? ''}
          onCorrectionsChanged={() => void listCorrections().then(setCorrections)}
          onCompChanged={() => void listPendingComp().then(setCompQueue)}
          dayEdits={dayEdits}
          onDayEditsChanged={reloadDayEdits}
        />
      )}

      {/* ── PEOPLE DETAILS — roster + record ── */}
      {view === 'details' && (
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: stacked ? 'minmax(0, 1fr)' : '290px minmax(0, 1fr)',
            // Stacked, the roster is a short scroller over the record, which
            // runs to its natural height while the screen scrolls. On a phone
            // only one of them shows, at its natural height.
            gridTemplateRows: phone ? 'auto' : stacked ? 'minmax(0, 16rem) auto' : 'minmax(0, 1fr)',
            gap: 'var(--hf-space-4)',
            flex: stacked ? 'none' : 1,
            minHeight: 0,
          }}
        >
          {/* A stale ?person= that names nobody is the roster too, not a blank.
              Hidden rather than unmounted under a record, so a search typed
              before opening someone is still there on the way back. */}
          <Roster
            people={people}
            departments={departments}
            selId={selId}
            onSelect={setSelId}
            loading={loading}
            failed={failed.has('roster')}
            onRetry={() => setRosterNonce((n) => n + 1)}
            canInvite={peopleFull}
            onInvite={() => setInviting(true)}
            hidden={phone && !!sel}
          />

          {sel ? (
            <div
              className="hf-scroll"
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--hf-space-3)',
                minWidth: 0,
                height: stacked ? 'auto' : '100%',
                overflowY: stacked ? 'visible' : 'auto',
                paddingRight: 2,
              }}
            >
              {/* Back to the list. A push, like a roster pick, so the browser's
                  Back returns to this record. */}
              {phone && (
                <Button
                  variant="ghost"
                  size="sm"
                  icon="chevronLeft"
                  className="hf-hit"
                  onClick={() => setSelId(null)}
                  style={{ alignSelf: 'flex-start' }}
                >
                  Back to people
                </Button>
              )}
              {/* Header */}
              <div
                className="hf-glass"
                style={{
                  padding: 'var(--hf-space-4) var(--hf-space-5)',
                  borderRadius: 'var(--hf-radius-card)',
                  display: 'flex',
                  flexWrap: 'wrap',
                  alignItems: 'center',
                  gap: 'var(--hf-space-4)',
                }}
              >
                <Avatar name={sel.full_name || '—'} size={54} />
                <div style={{ flex: '1 1 14rem', minWidth: 0 }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--hf-space-2)',
                      marginBottom: 'var(--hf-space-1)',
                      flexWrap: 'wrap',
                    }}
                  >
                    <h2
                      ref={recordHeadingRef}
                      tabIndex={-1}
                      style={{
                        fontFamily: 'var(--hf-font-display)',
                        fontSize: 'var(--hf-text-2xl)',
                        fontWeight: 'var(--hf-weight-regular)',
                        margin: 0,
                        letterSpacing: 'var(--hf-track-display)',
                        overflowWrap: 'anywhere',
                      }}
                    >
                      {sel.full_name || 'Unnamed'}
                    </h2>
                    <Badge tone={roleTone(sel.role_id)} dot>
                      {roleLabel(sel.role_id)}
                    </Badge>
                    {sel.status === 'leave' && (
                      <Badge tone="lilac" dot>
                        On leave
                      </Badge>
                    )}
                    {isLeaver(sel) && (
                      <Badge tone="neutral" dot>
                        Left
                      </Badge>
                    )}
                  </div>
                  <div
                    style={{
                      fontSize: 'var(--hf-text-sm)',
                      color: 'var(--hf-ink-2)',
                      display: 'flex',
                      alignItems: 'center',
                      gap: 'var(--hf-space-2)',
                      flexWrap: 'wrap',
                    }}
                  >
                    <span>{sel.title || '—'}</span>
                    <span style={{ color: 'var(--hf-ink-3)' }}>·</span>
                    <span>{departmentName(departments, sel.department_id) ?? '—'}</span>
                    {sel.joined && (
                      <>
                        <span style={{ color: 'var(--hf-ink-3)' }}>·</span>
                        <span style={{ fontSize: 'var(--hf-text-xs)', color: 'var(--hf-ink-3)' }}>
                          joined {fmtDate(sel.joined, dateFormat)}
                        </span>
                      </>
                    )}
                  </div>
                </div>
                {/* At the header's right edge; on a phone it wraps under the
                    name, so it starts at the left like the lines above it. */}
                <div
                  style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: phone ? 'flex-start' : 'flex-end',
                    gap: 'var(--hf-space-2)',
                  }}
                >
                  <Eyebrow>This week</Eyebrow>
                  <AttendanceStrip
                    days={strip}
                    workdays={stripWorkdays}
                    labels={weekdayLabels(weekStartsOn)}
                  />
                </div>
              </div>

              {/* Tab bar. The Tabs primitive scrolls sideways when it cannot fit, so
                  it needs a box narrower than its content to scroll in. */}
              <div style={{ alignSelf: 'flex-start', maxWidth: '100%', minWidth: 0 }}>
                <Tabs label="Person record sections" tabs={TABS} value={tab} onChange={setTab} />
              </div>

              {/* Tab body. Job description and Documents stand on their own reads;
                  the other four are drawn from the record, and a record that
                  failed to load is not one with no contact and a zero balance. */}
              <div>
                {failed.has('record') && tab !== 'jd' && tab !== 'docs' && (
                  <FailedCard
                    label="Record"
                    what={`${sel.full_name ?? 'this person'}’s record`}
                    onRetry={() => setDetailNonce((n) => n + 1)}
                  />
                )}
                {/* Each tab is keyed by person: an open editor, a staged change or a
                    revealed account number must not follow HR to the next record,
                    where Save would write it to the wrong person. */}
                {tab === 'profile' && !failed.has('record') && (
                  <ProfileTab
                    key={sel.id}
                    sel={sel}
                    bank={bank}
                    contact={contact}
                    showComp={showComp}
                    roles={rolesList}
                    rolePerms={rolePerms}
                    people={people}
                    departments={departments}
                    canEdit={peopleFull}
                    canAssignRoles={peopleFull}
                    keyDates={selKeyDates}
                    events={selEvents}
                    exceptions={selExceptions}
                    failed={failed}
                    onRecordChanged={() => {
                      void reloadRecord();
                      void reloadDueDates();
                    }}
                    actorIsOwner={actorIsOwner}
                    isSelf={sel.id === session?.user.id}
                    onSetRole={(r) => void setRole(r)}
                    onPatch={patchProfile}
                    onPatchBank={patchBank}
                    onPatchContact={patchContact}
                    onErased={() => {
                      // The roster row is anonymised and the private rows are
                      // gone: refetch both rather than reload the page.
                      setRosterNonce((n) => n + 1);
                      setDetailNonce((n) => n + 1);
                    }}
                  />
                )}
                {tab === 'comp' && !failed.has('record') && (
                  <CompTab
                    key={sel.id}
                    sel={sel}
                    comp={comp}
                    showComp={showComp}
                    onSaveCtc={saveCtc}
                    orgHours={orgHoursOf}
                    yearWorkingDays={yearWorkingDays}
                  />
                )}
                {tab === 'leave' && !failed.has('record') && (
                  <LeaveTab
                    key={sel.id}
                    leaveTypes={leaveTypes}
                    balances={balances}
                    leaveDates={leaveDates}
                    profileId={sel.id}
                    canEdit={peopleFull}
                    onSetAllotted={setAllotted}
                    weekStartsOn={weekStartsOn}
                    holidays={holidayMap}
                    workdays={orgWorkdays}
                    today={orgTodayKey}
                    uid={session?.user.id ?? ''}
                    personName={sel.full_name}
                    onRecorded={(charged, typeLabel) => {
                      setDetailNonce((n) => n + 1);
                      toast.push({
                        tone: 'success',
                        message: `Recorded ${charged} day${charged === 1 ? '' : 's'} of ${typeLabel} for ${sel.full_name ?? 'this person'}.`,
                      });
                    }}
                  />
                )}
                {tab === 'hours' && !failed.has('record') && (
                  <HoursTab
                    key={sel.id}
                    sel={sel}
                    canEdit={peopleFull}
                    onSetContract={setContract}
                    orgHours={orgHoursOf}
                    weekHrs={
                      Math.round(attendance.reduce((s, a) => s + (a.hours || 0), 0) * 10) / 10
                    }
                    // This week's REQUIRED hours, not a flat contracted week:
                    // holidays and approved paid leave are already out of it.
                    weekTarget={selWeek?.requiredHours ?? orgHoursOf.perWeek}
                    weekRequiredToDate={selWeek?.requiredToDate ?? 0}
                    summary={summaries.find((r) => r.profileId === sel.id) ?? null}
                    tz={orgTz}
                    workdays={orgWorkdays}
                  />
                )}
                {tab === 'jd' && (
                  <JDTab key={sel.id} sel={sel} canEdit={peopleFull} onPatch={patchProfile} />
                )}
                {tab === 'docs' &&
                  // ed_read and rh_read are people:full or your own: a lead's
                  // read of a teammate's papers came back empty, and the
                  // checklist drew every required paper as missing.
                  (!peopleFull && sel.id !== session?.user.id ? (
                    <LockCard>
                      Documents: only {sel.full_name?.trim().split(/\s+/)[0] || 'this person'}, HR
                      and admins can see them.
                    </LockCard>
                  ) : failed.has('docs') ? (
                    <FailedCard
                      label="Documents"
                      what="their documents"
                      onRetry={() => void reloadRecord()}
                    />
                  ) : (
                    <DocumentsTab
                      key={sel.id}
                      profileId={sel.id}
                      // Until this person's read lands, the last person's papers are
                      // still in state, and a tick here writes by document id.
                      rows={selDocs.filter((d) => d.profile_id === sel.id)}
                      handbooks={handbooks}
                      roleId={sel.role_id}
                      canEdit={peopleFull}
                      onChanged={() => void reloadRecord()}
                    />
                  ))}
                {/* FR-8.6: certificates are read by the person, HR and anyone above
                    them in the line (cert_read), so they show beside the papers or the
                    lock; RLS decides what comes back. */}
                {tab === 'docs' && (
                  <CertificatesCard
                    key={`cert-${sel.id}`}
                    profileId={sel.id}
                    canEdit={peopleFull || sel.id === session?.user.id}
                  />
                )}
              </div>
            </div>
          ) : (
            !phone && (
              <PCard>
                <div style={NOTE}>
                  {loading ? 'Loading…' : 'Select someone from the roster to view their record.'}
                </div>
              </PCard>
            )
          )}
        </div>
      )}
    </div>
  );
}

// Load every leave request (any status) org-wide, joined to the requester name
// and leave-type label, for the Requests & approvals queue. RLS (lr_read) lets
// people:full read all; a self-only user would just see their own.
async function loadRequests(
  sb: NonNullable<typeof supabase>,
): Promise<{ data: PendingRequest[]; failed: boolean }> {
  const { data, error } = await sb
    .from('leave_requests')
    .select(
      'id, profile_id, leave_type_id, start_date, end_date, day_count, reason, status, approver, decided_at, decline_reason, created_at, profiles!leave_requests_profile_id_fkey(full_name), leave_types(label, paid)',
    )
    .order('created_at', { ascending: false });
  type Joined = LeaveRequest & {
    created_at: string | null;
    decline_reason: string | null;
    profiles?: { full_name: string | null } | { full_name: string | null }[] | null;
    leave_types?:
      | { label: string | null; paid: boolean | null }
      | { label: string | null; paid: boolean | null }[]
      | null;
  };
  const rows = (data as Joined[] | null) ?? [];
  return {
    failed: !!error,
    data: rows.map((r) => {
      const prof = Array.isArray(r.profiles) ? r.profiles[0] : r.profiles;
      const lt = Array.isArray(r.leave_types) ? r.leave_types[0] : r.leave_types;
      return {
        id: r.id,
        profile_id: r.profile_id,
        leave_type_id: r.leave_type_id,
        start_date: r.start_date,
        end_date: r.end_date,
        day_count: r.day_count,
        reason: r.reason,
        status: r.status,
        approver: r.approver,
        decided_at: r.decided_at,
        created_at: r.created_at,
        decline_reason: r.decline_reason ?? null,
        full_name: prof?.full_name ?? null,
        leave_label: lt?.label ?? null,
        // Default true: a missing join must not accuse a paid type of costing money.
        leave_paid: lt?.paid ?? true,
      };
    }),
  };
}
